/**
 * 콜봇 월 정산 화면이 같이 쓰는 말·규칙 (순수 함수).
 *
 * 요청: ~/md/front/admin-콜봇-월정산-프론트요청.md
 * 실제 구현과 달라진 점: ~/md/front/admin-콜봇-월정산-call-api-구현회신.md "응답·동작 차이"(아래 "회신 표 n")
 * 판매 요금: 같은 회신 "2026-10-16 개정 — 요금표 v70"(아래 "개정 n") — 요율 칸·유효 요율·청구 줄·정산서 줄·경고가
 *  바뀌었다. 상담사 연결은 판매가 없이 월 사용료에 들고, AI 응대 줄과 상담사 연결 줄은 없다. v70 이전 모양(기본 요율
 *  버전 `legacy: true`, 예전 정산서)은 보이기만 한다.
 * 상담사 번호 추가 요금: ~/md/front/admin-콜봇-상담사번호-추가요금-프론트요청.md(아래 "번호 요청 A n") — 요율 블록
 *  `counselor_number`(기본 포함 수·추가 1개당 월 금액, VAT 별도), 정산서 일곱째 줄 '상담사 번호 추가'(회선 다음, 일회성 앞 —
 *  일회성까지 청구 일곱 줄), 경고 셋. 응답 칸 이름은 call-api 1-2·1-3 구현(계약 묶음 9.4·9.5)을 따른다.
 *  1. 라벨 — 경로·번호 종류·구분·출처·이력 동작("요청 계약 2·4-3")
 *  2. 마감 상태 배지 — "화면 명세 2 공통 헤더", "요청 계약 4-3"
 *  3. 경고 코드 사전과 사유별 해결 방법 — "요청 계약 7", "화면 명세 2-4"
 *  4. 요율 칸 목록, 칸 읽기, 폼 ↔ 본문 변환 — "요청 계약 3-1·3-2·3-3", "화면 명세 2-1·3"
 *  5. 사용량 목록 정렬·필터·묶음·타일 — "화면 명세 1", "요청 계약 1"
 *  6. 정산 요약 줄 — "화면 명세 2 공통 헤더"
 *  7. 마감 폼·이력·일회성 항목 — "화면 명세 2-4", "요청 계약 4-2·4-4"
 *  8. 자동 마감 문구와 확인창 문구 — "화면 명세 2-4·3", "요청 계약 4-1"
 *  9. 대사·연결 진단 — "화면 명세 2-2·2-6"
 * 10. 상세 탭과 목록 쿼리 — "화면 명세 2 탭", "화면 명세 1 헤더"
 *
 * 화면(.vue)과 목(settlementMock.js)이 같은 말을 쓰도록 문구를 여기 모은다. 확인창·안내 문구는
 * 요청 문서의 문장을 그대로 옮겼다. 경고가 차단인지는 언제나 경고 객체의 `blocking` 이 정한다
 * (내부·테스트 회사는 모두 비차단이다) — 코드로 판정하지 않는다.
 * '@/'·vue·antd 를 import 하지 않는다 — node --test 로 검증한다
 * (service/__tests__/settlementRules.test.mjs).
 */
import {
  EMPTY,
  addMonths,
  autoCloseAtMs,
  formatCount,
  formatDuration,
  formatKstDateTime,
  formatKstTime,
  formatNumber,
  formatUnitPrice,
  formatWon,
  isCurrentMonth,
  isMonthEnded,
  isNumber,
  isValidMonth,
  maskNumber,
  monthEndMsKst,
  monthLabel,
  nextKstHourMs,
  relativeKstDayLabel,
} from './settlementFormat.js';
import { isPaymentMonthScope } from './settlementPayment.js';

// ---------------------------------------------------------------------------
// 공통 도우미
// ---------------------------------------------------------------------------

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const arr = (v) => (Array.isArray(v) ? v : []);
const num = (v) => (isNumber(v) ? v : null);
/** 소수 둘째 자리까지(표기용 — 금액 계산은 서버 값을 쓴다) */
const round2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;
const pad2 = (n) => String(n).padStart(2, '0');

/** 공유 상수가 화면에서 바뀌지 않게 통째로 얼린다 */
function deepFreeze(value) {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
}

/**
 * 경로를 따라 값을 읽는다. 중간에 객체가 아니면 undefined.
 * @param {unknown} obj
 * @param {string[]} path 예: ['counselor', 'pbx', 'mobile', 'price']
 * @returns {unknown}
 */
export function getPath(obj, path) {
  let cur = obj;
  for (const key of path) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = cur[key];
  }
  return cur;
}

/** 경로의 마지막 키까지 실제로 있는가(값이 null 이어도 있다) */
function hasPath(obj, path) {
  let cur = obj;
  for (const key of path) {
    if (cur === null || typeof cur !== 'object' || !hasOwn(cur, key)) return false;
    cur = cur[key];
  }
  return true;
}

function setPath(obj, path, value) {
  let cur = obj;
  for (const key of path.slice(0, -1)) {
    if (!cur[key] || typeof cur[key] !== 'object') cur[key] = {};
    cur = cur[key];
  }
  cur[path[path.length - 1]] = value;
  return obj;
}

/** 사전에 없는 키는 키 그대로, 값이 없으면 '—' */
function labelOf(map, key) {
  if (key === null || key === undefined || key === '') return EMPTY;
  return hasOwn(map, key) ? map[key] : String(key);
}

// ---------------------------------------------------------------------------
// 1. 라벨
// ---------------------------------------------------------------------------

/** 상담사 연결 방식(그 달 실제 통화 기준) — 요청 계약 1 `connect_mode.mode`, 2 `connect_mode` */
export const CONNECT_MODE_LABELS = deepFreeze({
  pbx: 'PBX',
  vox_direct: 'vox 직접',
  mixed: 'vox 직접 + PBX',
  none: '없음',
});

/** 상담사 연결 경로 — `usage.counselor.legs[].route`(v70 이전 스냅샷의 `ROUTE_RATE_MISSING` details 에도) */
export const ROUTE_LABELS = deepFreeze({ pbx: 'PBX', vox_direct: 'vox 직접' });

/** 상담사가 받은 번호 종류(요율 버킷). `other` 는 분류 규칙에 맞지 않는 번호다 */
export const NUMBER_TYPE_LABELS = deepFreeze({
  extension: '내선',
  landline_070: '유선·070',
  mobile: '휴대폰',
  uan: '대표번호',
  other: '미분류',
});

/** 회사 구분 — 그 달 유효 요율의 `internal` 로 정한다 */
export const CATEGORY_LABELS = deepFreeze({ hospital: '병원', internal: '내부·테스트' });

/**
 * 회선 종류. `usage.lines[].type` 은 vox 회선 유형(`voip`·`uan`)이고, 요율 칸의 키는 `070`·`uan` 이다.
 * `LINE_RATE_UNSET.details[].line_type` 이 어느 쪽으로 와도 읽히게 둘 다 둔다.
 */
export const LINE_TYPE_LABELS = deepFreeze({ voip: '070', '070': '070', uan: '대표번호' });

/** 공급가액의 출처 배지 — `charge.source` */
export const CHARGE_SOURCE_LABELS = deepFreeze({ confirmed: '확정', review: '검토', estimate: '추정' });

/** 요율 칸의 출처 배지 — 칸마다 `source`, 금액 줄마다 `rate_source` */
export const RATE_SOURCE_LABELS = deepFreeze({ default: '기본', override: '예외' });

/** 마감 이력 동작 — 4-3 `revisions[].action` */
export const REVISION_ACTION_LABELS = deepFreeze({
  auto_confirm: '자동 확정',
  auto_review: '자동 검토 필요',
  auto_recalculate: '자동 다시 계산',
  recalculate: '다시 계산',
  save: '저장',
  one_off: '일회성 항목',
  confirm: '확정',
  reopen: '재수정',
});

/** 통화 단건(`GET .../pbx-calls/:voxCallId`)의 `connect_via` */
export const PBX_CONNECT_VIA_LABELS = deepFreeze({ pbx: 'PBX', 'vox-direct': 'vox 직접' });

/** @param {string|null|undefined} mode @returns {string} */
export const connectModeLabel = (mode) => labelOf(CONNECT_MODE_LABELS, mode);

/**
 * 상세의 상담사 연결 방식. call-api 는 상세에도 목록과 같은 객체 `{ mode, pbx, vox_direct }` 를 싣는다
 * (회신 표 4·A12). 예전 계약 모양(문자열 'pbx')이 와도 읽는다 — 그때 경로별 수는 null.
 * @param {object|null|undefined} detail 상세 응답(요청 계약 2)
 * @returns {{ mode: string|null, pbx: number|null, vox_direct: number|null }}
 */
export function detailConnectMode(detail) {
  const raw = detail?.connect_mode ?? detail?.usage?.connect_mode ?? null;
  if (raw && typeof raw === 'object') {
    return { mode: raw.mode ?? null, pbx: num(raw.pbx), vox_direct: num(raw.vox_direct) };
  }
  if (typeof raw === 'string' && raw) return { mode: raw, pbx: null, vox_direct: null };
  return { mode: null, pbx: null, vox_direct: null };
}
/** @param {string|null|undefined} route @returns {string} */
export const routeLabel = (route) => labelOf(ROUTE_LABELS, route);
/** @param {string|null|undefined} type @returns {string} */
export const numberTypeLabel = (type) => labelOf(NUMBER_TYPE_LABELS, type);
/** @param {string|null|undefined} type @returns {string} */
export const lineTypeLabel = (type) => labelOf(LINE_TYPE_LABELS, type);
/** @param {string|null|undefined} source @returns {string} */
export const rateSourceLabel = (source) => labelOf(RATE_SOURCE_LABELS, source);
/** @param {string|null|undefined} source @returns {string} */
export const chargeSourceLabel = (source) => labelOf(CHARGE_SOURCE_LABELS, source);

/**
 * 구분 배지 — 목록은 `internal`(boolean), 상세는 `category`('hospital'|'internal')를 준다.
 * @param {boolean|'hospital'|'internal'|null|undefined} internalOrCategory
 * @returns {string}
 */
export function categoryLabel(internalOrCategory) {
  if (internalOrCategory === true || internalOrCategory === 'internal') return CATEGORY_LABELS.internal;
  if (internalOrCategory === false || internalOrCategory === 'hospital') return CATEGORY_LABELS.hospital;
  return EMPTY;
}

/** PBX 통화 전환료(STT) 칸 이름 — 번호 종류가 아니라 PBX 경로 전체에 붙는 칸이다 */
const PBX_STT_LABEL = 'PBX 통화 전환료(STT)';

/**
 * 상담사 연결 금액 줄·사용량 leg 의 이름.
 * STT 판매가가 비면 `ROUTE_RATE_MISSING.details` 에 `number_type: 'stt'` 로 올 수 있어 칸 이름으로 읽는다.
 * @param {{ route?: string, number_type?: string }|null|undefined} item
 * @returns {string} 예: 'PBX → 유선·070', 'vox 직접 → 대표번호', 'PBX 통화 전환료(STT)'
 */
export function counselorItemLabel(item) {
  if (item?.number_type === 'stt') return PBX_STT_LABEL;
  return `${routeLabel(item?.route)} → ${numberTypeLabel(item?.number_type)}`;
}

/**
 * 사람 또는 system. 이름이 없으면 id 로 보인다.
 * @param {string|null|undefined} by `'system'` | 사용자 id
 * @param {string|null|undefined} [byName]
 * @returns {string}
 */
export function actorLabel(by, byName) {
  if (by === SYSTEM_ACTOR) return SYSTEM_ACTOR;
  return byName || by || EMPTY;
}

// ---------------------------------------------------------------------------
// 2. 마감 상태
// ---------------------------------------------------------------------------

/** 자동 마감이 확정한 정산의 `confirmed_by` */
export const SYSTEM_ACTOR = 'system';

/** 마감 상태별 배지 이름과 antd `a-tag` 색 */
export const STATEMENT_STATUS_META = deepFreeze({
  estimating: { label: '추정 중', color: 'default' },
  closing: { label: '마감 중', color: 'processing' },
  review: { label: '검토 필요', color: 'warning' },
  confirmed: { label: '확정', color: 'success' },
  failed: { label: '마감 실패', color: 'error' },
  // 정산 시작일 전 달(billing_start.scope 'before_start') — 정산서 상태가 아니라 "이 달 상태"다(detailStatusBadge)
  not_billable: { label: '정산 대상 아님', color: 'default' },
});

/** 마감 중일 때의 안내(화면 명세 2-4) */
export const CLOSING_TEXT = '자동 마감이 이 병원을 계산하는 중입니다.';

/**
 * 상세의 마감 상태. 정산 문서가 없으면(`statement: null`) 추정 중이다.
 * @param {{ statement?: { status?: string }|null }|null|undefined} detail
 * @returns {'estimating'|'closing'|'review'|'confirmed'|'failed'|string}
 */
export function statementStatusOf(detail) {
  return detail?.statement?.status ?? 'estimating';
}

/**
 * 확정자 표기 — "자동(system)" 또는 사람 이름(없으면 id)
 * @param {object|null|undefined} statement
 * @returns {string}
 */
export function confirmedByLabel(statement) {
  if (!statement?.confirmed_by) return EMPTY;
  if (statement.confirmed_by === SYSTEM_ACTOR) return '자동(system)';
  return statement.confirmed_by_name || statement.confirmed_by;
}

/**
 * 마감 실패 사유(`close_error`) 한 줄
 * @param {{ code?: string, message?: string }|null|undefined} closeError
 * @returns {string}
 */
export function closeErrorText(closeError) {
  if (!closeError) return '자동 마감이 이 병원을 계산하지 못했습니다.';
  const { code, message } = closeError;
  if (message && code) return `${message} (${code})`;
  return message || code || '자동 마감이 이 병원을 계산하지 못했습니다.';
}

/**
 * 검토 필요 정산에 남은 마감 실패 사유 — 자동 마감이 다시 집었다가 vox 가 잠깐 실패하면 상태는 검토 필요 그대로
 * `close_error` 만 남는다(회신 표 2). call-api 는 사람의 처리(저장·다시 계산·확정·재수정)로 이 값을 지우지 않으므로,
 * 실패 뒤에 이력이 하나라도 쌓였으면(사람이 손댔거나 다시 계산됨) 지난 일로 보고 보이지 않는다.
 * @param {object|null|undefined} statement 상세 `statement`
 * @returns {{ code?: string, message?: string, at?: number }|null}
 */
export function currentCloseError(statement) {
  const err = statement?.close_error ?? null;
  if (!err) return null;
  const at = num(err.at);
  if (at === null) return err;
  return arr(statement.revisions).some((r) => num(r?.at) !== null && r.at > at) ? null : err;
}

/**
 * 상세 헤더의 마감 상태 배지
 * @param {object|null|undefined} statement 상세의 `statement`(없으면 추정 중)
 * @returns {{ status: string, label: string, color: string, tooltip: string }}
 */
export function statementBadge(statement) {
  const make = (status, label, tooltip) => ({
    status,
    label,
    color: STATEMENT_STATUS_META[status]?.color ?? 'default',
    tooltip,
  });
  const status = statement?.status ?? 'estimating';
  switch (status) {
    case 'estimating':
      return make('estimating', '추정 중', '마감 전입니다. 금액은 지금 사용량과 이 달 요율로 계산한 추정치입니다.');
    case 'closing':
      return make('closing', '마감 중', CLOSING_TEXT);
    case 'review': {
      if (!statement.reopened) {
        return make(
          'review',
          '검토 필요',
          '차단 경고가 있어 자동 마감이 확정하지 않았습니다. 정산 탭에서 검토하고 확정합니다.'
        );
      }
      const last = num(statement.last_confirmed?.total);
      return make(
        'review',
        '검토 필요(재수정)',
        last === null
          ? '재수정으로 검토 필요로 돌아온 정산입니다.'
          : `재수정 중입니다. 다시 확정할 때까지 직전 확정 금액 ${formatWon(last)}이 확정 정산 금액으로 남습니다.`
      );
    }
    case 'confirmed': {
      const at = formatKstDateTime(statement.confirmed_at);
      if (statement.confirmed_by === SYSTEM_ACTOR) {
        return make(
          'confirmed',
          // '자동' already says who — the raw actor code ('system') stays out of the label
          `자동 확정 · ${at}`,
          '차단 경고가 없어 자동 마감이 확정했습니다. 바꾸려면 재수정합니다.'
        );
      }
      const who = statement.confirmed_by_name || statement.confirmed_by || EMPTY;
      return make('confirmed', `확정 · ${at} · ${who}`, '사람이 검토하고 확정했습니다. 바꾸려면 재수정합니다.');
    }
    case 'failed':
      return make('failed', '마감 실패', closeErrorText(statement.close_error));
    case 'not_billable':
      // 정산서에는 없는 상태 — 내보내기 응답의 statement_status 를 같은 배지로 그릴 때
      return make('not_billable', STATEMENT_STATUS_META.not_billable.label, notBillableTooltip(null));
    default:
      return { status, label: String(status), color: 'default', tooltip: '' };
  }
}

/**
 * 정산 대상 아님(정산 시작일 전 달)인가 — 상세 `billing_start.scope === 'before_start'`. 칸이 없으면(call-api 배포 전) 아니다.
 * 기존 statementStatusOf·statementBadge·canEditOneOff 는 정산서 축이라 바꾸지 않고, 정산 대상 아님은 이것으로 따로 본다.
 * @param {object|null|undefined} detail 상세 응답
 * @returns {boolean}
 */
export function isNotBillableDetail(detail) {
  return detail?.billing_start?.scope === 'before_start';
}

/**
 * 정산 대상 아님 설명(칩·알약·태그 공용)
 * @param {string|null|undefined} startDate 'YYYY-MM-DD'
 * @returns {string}
 */
export function notBillableTooltip(startDate) {
  const when = typeof startDate === 'string' && startDate ? `(${startDate})` : '';
  return `정산 시작일${when} 전 달입니다. 사용량은 참고로 보이고 마감·청구하지 않습니다.`;
}

/**
 * 헤더 알약 등 "이 달 상태" 배지. 정산 대상 아님이면 회색 '정산 대상 아님'(정산서가 없어도 '추정 중' 이 아니다 — 그 달에
 * 지우지 못한 정산서가 남았으면 tooltip 에 덧붙인다), 아니면 statementBadge(detail.statement) 그대로.
 * @param {object|null|undefined} detail 상세 응답
 * @returns {{ status: string, label: string, color: string, tooltip: string }}
 */
export function detailStatusBadge(detail) {
  if (!isNotBillableDetail(detail)) return statementBadge(detail?.statement ?? null);
  const left = detail?.statement?.status;
  const leftText = left ? ` 이 달에 남은 정산서(${STATEMENT_STATUS_META[left]?.label ?? left})가 있습니다.` : '';
  return {
    status: 'not_billable',
    label: STATEMENT_STATUS_META.not_billable.label,
    color: STATEMENT_STATUS_META.not_billable.color,
    tooltip: `${notBillableTooltip(detail?.billing_start?.start_date)}${leftText}`,
  };
}

// ---------------------------------------------------------------------------
// 3. 경고 (요청 계약 7)
// ---------------------------------------------------------------------------

/**
 * 해결 방법 하나 — 정산 탭 2-4 가 사유마다 버튼·링크로 그린다.
 * action:
 *  - 'rates-page'        기본 요율 화면(/callbot/settlement/rates)으로 간다
 *  - 'override-modal'    요율 탭으로 가서 예외 모달을 연다
 *  - 'line-input'        검토 폼의 그 줄(`line`: 'base' | 'inbound' | 'outbound' | 'web_text' | 'line' | 'counselor_number')
 *                        금액 칸으로 간다
 *  - 'recalculate'       [다시 계산]
 *  - 'acknowledge'       경고를 확인하고 그대로 확정(체크박스)
 *  - 'recon-missing-ids' 대사 탭 2-2 의 [누락 통화 ID 확인]
 *  - 'recon-count'       대사 탭 2-2 의 [vox 건수 확인](vox 웹 요약까지 다시 읽는다)
 *  - 'wait'              할 일 없음(안내만). NOT_FINAL_CALLS 의 안내는 정산 상태에 따라 다르므로 `hint` 를 두지 않는다 —
 *                        notFinalWaitHint(detail, month) 로 만든다. 상태와 상관없는 안내면 `hint` 에 둔다.
 *  - 'hint'              버튼 없는 안내 한 문장(`label` 이 그 문장이다) — 상태와 상관없이 보인다
 * @typedef {Object} WarningResolution
 * @property {string} key
 * @property {string} label 요청 문서 2-4 의 문구 그대로
 * @property {'rates-page'|'override-modal'|'line-input'|'recalculate'|'acknowledge'|'recon-missing-ids'|'recon-count'|'wait'|'hint'} action
 * @property {'base'|'inbound'|'outbound'|'web_text'|'line'|'counselor_number'} [line]
 * @property {string} [hint]
 */

/** @typedef {{ label: string, description: string, resolutions: WarningResolution[] }} WarningMeta */

const ACKNOWLEDGE = { key: 'acknowledge', label: '그대로 확정(경고 확인)', action: 'acknowledge' };
const OVERRIDE_MODAL = { key: 'override-modal', label: '이 병원 예외로 넣기', action: 'override-modal' };
/** 상담사 번호 추가 줄 금액 칸으로(번호 요청 A6) */
const COUNSELOR_LINE_INPUT = {
  key: 'line-input',
  label: '상담사 번호 추가 금액 고치기',
  action: 'line-input',
  line: 'counselor_number',
};

/**
 * COUNSELOR_FEE_NO_CALLS 의 둘째 문장("받을 금액이 있으면 줄 금액을 직접 입력하세요")과 해결 방법('상담사 번호 추가 줄 금액
 * 직접 입력')을 보이는가(번호 요청 A6 "열린 결정에 걸린 부분"). 병원 공지 문구(병원 공지 초안 2절 15번)가 정해지기 전에는
 * 공지가 그대로 나가는 쪽(통화가 없는 달은 이 요금을 받지 않는다 — 운영 런북 절차 13)이 기본이라 끈다. 공지에 "원칙적으로"가
 * 붙으면 켠다 — 첫 문장 뒤의 문장과 해결 방법이 함께 켜진다.
 */
export const COUNSELOR_NO_CALLS_LINE_INPUT = false;

/** 통화 0건 달 경고의 첫 문장(번호 요청 A6) — "(계산했다면 n원)"은 warningText 가 details 로 붙인다 */
const COUNSELOR_NO_CALLS_LEAD = '이 달은 통화가 없어 상담사 번호 추가 요금을 0원으로 계산했습니다';
const COUNSELOR_NO_CALLS_TAIL = '받을 금액이 있으면 항목 금액을 직접 고치세요.';

/**
 * 상담사 번호 추가 줄의 경고(번호 요청 A6, call-api 1-2) — 줄 배지는 이 순서의 첫 코드다(call-api 줄 `warning` 과 같은 순서).
 * 막는지는 늘 경고의 `blocking` 이다(GAP·DUPLICATE 는 막고 NO_CALLS 는 막지 않는다, 내부·테스트는 모두 비차단).
 */
export const COUNSELOR_LINE_WARNING_CODES = deepFreeze([
  'COUNSELOR_COUNT_GAP',
  'COUNSELOR_FEE_DUPLICATE_SUSPECT',
  'COUNSELOR_FEE_NO_CALLS',
]);

/**
 * 경고 코드 사전. 차단/비차단 구분은 여기 두지 않는다 — 경고 객체의 `blocking` 을 쓴다.
 * @type {Readonly<Record<string, WarningMeta>>}
 */
export const WARNING_META = deepFreeze({
  DEFAULT_PLAN_MISSING: {
    label: '기본 요율 없음',
    description:
      '그 달에 적용할 기본 요율 버전이 없거나, 그 버전이 요금표 v70 이전 모양이라 금액을 계산하지 않았습니다. 예전 모양 버전은 자동으로 바꾸지 않습니다.',
    resolutions: [
      { key: 'rates-page', label: '그 달에 적용되는 기본 요율 버전 만들기', action: 'rates-page' },
      {
        key: 'recalculate',
        label: '다시 계산',
        action: 'recalculate',
        hint: '그 달에 적용되는 버전을 만들거나 새 칸으로 다시 저장한 뒤 지금 값으로 다시 계산합니다.',
      },
    ],
  },
  NOT_FINAL_CALLS: {
    label: '안 끝난 통화',
    description: '끝나지 않은 통화가 있어 통화 시간·120분 사용량·요금이 적게 잡혔을 수 있습니다.',
    resolutions: [
      { key: 'wait', label: '통화가 끝나길 기다리기', action: 'wait' },
      { key: 'recalculate', label: '다시 계산', action: 'recalculate', hint: '통화가 끝났으면 지금 값으로 다시 계산합니다.' },
      ACKNOWLEDGE,
    ],
  },
  // ROUTE_RATE_MISSING·TELECOM_RATE_UNSET — 요금표 v70 부터는 나오지 않는다(상담사 경로에 판매가가 없다, 개정 7). 그 전에
  // 마감한 정산 스냅샷에 남은 것을 그리려고 둔다. 새 요율로 [다시 계산]하면 사라진다
  ROUTE_RATE_MISSING: {
    label: '판매가 미정(v70 이전)',
    description:
      '요금표 v70 이전에 계산한 정산에만 있는 경고입니다. 상담사가 받은 통화의 경로 × 번호 종류에 판매가가 없어 상담사 연결 요금을 금액에서 뺐습니다. 요금표 v70 은 상담사 연결을 월 사용료에 넣어 다시 계산하면 사라집니다.',
    resolutions: [
      { key: 'recalculate', label: '다시 계산', action: 'recalculate', hint: '요금표 v70 요율로 다시 계산합니다.' },
      ACKNOWLEDGE,
    ],
  },
  TELECOM_RATE_UNSET: {
    label: '통신료 미정(v70 이전)',
    description:
      '요금표 v70 이전에 계산한 정산에만 있는 경고입니다. vox 직접 연결의 통신료 판매가가 미정이라 통신료를 금액에서 뺐습니다. 요금표 v70 은 상담사 연결을 월 사용료에 넣어 다시 계산하면 사라집니다.',
    resolutions: [
      { key: 'recalculate', label: '다시 계산', action: 'recalculate', hint: '요금표 v70 요율로 다시 계산합니다.' },
      ACKNOWLEDGE,
    ],
  },
  VOX_DIRECT_COLD_UNMEASURED: {
    label: 'vox 직접 cold 전환',
    description:
      'cold 전환은 상담사 leg 기록이 없어 그 상담사 통화 시간이 120분 사용량(120분 초과 통화)에서 빠졌습니다. 그 vox 원가는 기타 원가에 있습니다.',
    resolutions: [
      {
        key: 'line-input',
        label: '120분 초과 통화 금액 고치기',
        action: 'line-input',
        line: 'inbound',
        hint: "'원가 · 마진 자세히'의 기타 원가를 보고 판단합니다.",
      },
      ACKNOWLEDGE,
    ],
  },
  LINE_RATE_UNSET: {
    label: '회선 판매가 미정',
    description: '판매가가 미정인 회선(추가 070·대표번호)이 있어 그 회선 금액을 뺐습니다. 월 사용료에 든 포함 회선은 해당 없습니다.',
    resolutions: [
      { key: 'rates-page', label: '기본 요율에 회선 판매가 넣기', action: 'rates-page' },
      OVERRIDE_MODAL,
      { key: 'line-input', label: '회선 금액 고치기', action: 'line-input', line: 'line' },
    ],
  },
  // 상담사 번호 추가 요금(번호 요청 A6) — 설명은 고정 문구라 날짜·개수가 든 문장은 warningText 가 details 로 만든다.
  // 경고 카드는 warningText(굵게) 다음에 설명을 붙여 요청 문서의 문장 하나가 된다
  COUNSELOR_COUNT_GAP: {
    label: '상담사 번호 매시 기록 없는 날',
    description: '일별 기록 작업을 확인하고, 받을 금액이 있으면 항목 금액을 직접 고친 뒤 확정하세요.',
    resolutions: [COUNSELOR_LINE_INPUT, ACKNOWLEDGE],
  },
  COUNSELOR_FEE_DUPLICATE_SUSPECT: {
    label: '상담사 번호 요금 중복 의심',
    description: '상담사 번호 추가 요금은 이제 정산서 항목으로 받으므로, 같은 요금을 두 번 받지 않는지 확인하세요.',
    resolutions: [
      { key: 'one-off-hint', label: '같은 요금이면 정산 탭의 일회성 항목 목록에서 그 항목을 지웁니다', action: 'hint' },
      COUNSELOR_LINE_INPUT,
      ACKNOWLEDGE,
    ],
  },
  COUNT_MISMATCH: {
    label: '건수 불일치',
    description:
      '웹 테스트를 뺀 통화 수가 vox 와 DB 에서 다릅니다(월말 뒤에 읽은 vox 건수로만 판정합니다). DB 에 빠진 통화가 있으면 덜 청구됩니다.',
    resolutions: [{ key: 'recon-missing-ids', label: '누락 통화 ID 확인', action: 'recon-missing-ids' }],
  },
  WEB_USAGE_UNVERIFIED: {
    label: '웹 사용량 미확인',
    description:
      '끝난 달인데 월말 뒤에 끝까지 읽은 vox 웹 요약이 없어 웹 테스트 사용량을 확정하지 못했습니다. 웹 테스트는 vox 에서만 읽을 수 있어, 그대로 확정하면 덜 청구될 수 있습니다.',
    resolutions: [
      {
        key: 'recon-count',
        label: 'vox 건수 확인(대사 탭)',
        action: 'recon-count',
        hint: '대사 탭에서 vox 건수와 웹 요약을 다시 읽습니다.',
      },
      {
        key: 'recalculate',
        label: '다시 계산',
        action: 'recalculate',
        hint: 'vox 건수를 확인한 뒤 다시 계산하면 웹 사용량이 정산에 들어갑니다.',
      },
      ACKNOWLEDGE,
    ],
  },
  WEB_VOICE_COST_MISMATCH: {
    label: '웹 음성 원가 불일치',
    description:
      '웹 음성으로 나뉜 통화의 vox 원가 합이 10초 단위 × 22원과 다릅니다. 웹 텍스트 상담이 웹 음성으로 잡혔을 수 있습니다 — 그대로 확정하면 메시지당 원가 그대로 넘길 텍스트를 120분 사용량(넘으면 10초 단위 판매가)으로 더 청구합니다.',
    resolutions: [
      {
        key: 'recon-count',
        label: 'vox 건수 확인(대사 탭)',
        action: 'recon-count',
        hint: '웹 요약을 다시 읽어 웹 통화를 원가로 다시 나눕니다. 웹 통화를 원가로 가르기 전에 읽은 웹 요약이었다면 이것으로 풀립니다.',
      },
      {
        key: 'recalculate',
        label: '다시 계산',
        action: 'recalculate',
        hint: 'vox 건수를 확인한 뒤 다시 계산하면 다시 나눈 웹 사용량이 정산에 들어갑니다.',
      },
      {
        key: 'line-input',
        label: '120분 초과 통화 금액 고치기',
        action: 'line-input',
        line: 'inbound',
        hint: '다시 읽어도 남으면 대사 탭의 웹 음성 건수·단위·원가와 vox 청구 상세로 어느 웹콜이 텍스트인지 확인하고, 120분 초과 통화 항목 금액(웹 음성은 120분에 든다)을 고쳐 저장합니다.',
      },
      ACKNOWLEDGE,
    ],
  },
  // 2026-10-16 사용자 결정 — vox 070 회선이 없는 달 중 웹이 아닌 전화도 없는 달(개통 전 웹 테스트·일회성 항목만)은
  // 차단한다. 전화가 있으면 INCLUDED_LINE_MISSING(비차단)이다 — 둘은 함께 나오지 않는다
  NO_PHONE_USAGE: {
    label: '070 회선·전화 없음 — 월 사용료 확인',
    description:
      '그 달 vox 070 회선도, 웹이 아닌 전화 통화도 없어(웹 테스트·일회성 항목만) 월 사용료를 전액 계산했습니다. 개통 전 달일 가능성이 커 자동으로 확정하지 않았습니다.',
    resolutions: [
      {
        key: 'line-input',
        label: '월 사용료 금액 고치기',
        action: 'line-input',
        line: 'base',
        hint: '개통 전 달이면 월 사용료 항목을 0 으로 저장하고 확정합니다.',
      },
      {
        ...ACKNOWLEDGE,
        hint: '월 사용료를 받아야 하는 달이면 경고를 확인하고 그대로 확정합니다.',
      },
    ],
  },
  UNIT_RULE_MISMATCH: {
    label: '10초 규칙 불일치',
    description: '10초 규칙과 맞지 않는 vox 원가가 있습니다. 원가 쪽 검증이라 청구액은 바뀌지 않습니다.',
    resolutions: [],
  },
  UNIT_RULE_UNCOSTED: {
    label: '원가 없는 통화',
    description:
      'AI 가 받은 통화 중 vox 원가가 미러에 없는 통화가 있습니다. 그 원가가 빠져 vox 원가는 적게, 마진은 그만큼 많게 잡힙니다. 청구액은 바뀌지 않습니다.',
    resolutions: [
      {
        key: 'wait',
        label: '마감이 vox 에서 다시 읽기',
        action: 'wait',
        hint: '이 통화들은 미러에 vox 원가가 없어 원가가 적게(마진은 많게) 잡혀 있습니다. 다음 마감(자동 또는 수동 마감 실행)이 vox 에서 다시 읽으면 대개 채워집니다([다시 계산]은 vox 를 읽지 않습니다). 남으면 대사 탭의 통화 ID 로 vox 원가를 확인합니다.',
      },
    ],
  },
  INCLUDED_LINE_MISSING: {
    label: '포함 회선 없음',
    description:
      '그 달 vox 070 회선이 하나도 없어 월 사용료를 전액 받습니다(개통 달 일할 없음). 전화 통화는 있어 병원이 자기 번호나 대표번호만 쓰는 달이고, 그대로 둡니다. 전화도 없는 달은 NO_PHONE_USAGE 로 따로 멈춥니다.',
    resolutions: [
      {
        key: 'line-input',
        label: '월 사용료 금액 고치기',
        action: 'line-input',
        line: 'base',
        hint: '계약상 월 사용료를 달리 받는 달이면 월 사용료 항목 금액을 직접 넣습니다.',
      },
    ],
  },
  // 막지 않음 — 둘째 문장과 해결 방법은 COUNSELOR_NO_CALLS_LINE_INPUT 이 켤 때만(병원 공지 문구에 걸린다)
  COUNSELOR_FEE_NO_CALLS: {
    label: '통화 없는 달 — 상담사 번호 요금 0원',
    description: COUNSELOR_NO_CALLS_LINE_INPUT
      ? `${COUNSELOR_NO_CALLS_LEAD}. ${COUNSELOR_NO_CALLS_TAIL}`
      : `${COUNSELOR_NO_CALLS_LEAD}.`,
    resolutions: COUNSELOR_NO_CALLS_LINE_INPUT ? [COUNSELOR_LINE_INPUT] : [],
  },
  WEB_ONLY_USAGE: {
    label: '웹 테스트만 사용',
    description: '웹 테스트 사용량만 있습니다. 웹 테스트도 요율대로 청구됩니다(웹 텍스트는 원가 그대로, 웹 음성은 120분 사용량).',
    resolutions: [],
  },
  // 막지 않음 — 끝난 달에 vox 웹 조회를 끝까지 했더니 저장한 웹 기록(webTestCalls)과 달랐다(2026-11-02). 웹훅 경로 진단용
  WEB_STORED_MISMATCH: {
    label: '웹 기록 대조 차이',
    description: 'vox 웹 통화 조회와 저장한 웹 기록이 달랐습니다. 청구는 vox 조회 기준이라 금액에는 영향이 없습니다.',
    resolutions: [],
  },
  RECONCILIATION_UNAVAILABLE: {
    label: '건수 대사 생략',
    description:
      '자동 마감이 vox 를 읽지 못해(하위 조직 없음 등) 건수 대사 없이 마감했습니다. 통화 청구 근거는 DB 통화이고, 웹 테스트는 웹 사용량 미확인으로 따로 알립니다.',
    resolutions: [],
  },
  // 정산 시작일(2026-11-02 결정 3) — 시작 월(1일 아님)에 웹 사용량이 있는데 vox 웹 요약에 일별 칸이 없다(기능 전에 읽은 요약).
  // 시작일 전 웹 테스트를 가를 수 없어 막는다(차단)
  WEB_START_SPLIT_UNAVAILABLE: {
    label: '시작일 웹 분할 정보 없음',
    description:
      '웹 사용량을 정산 시작일로 나눌 일별 정보가 없습니다(이 기능 전에 읽은 vox 웹 요약). 대사 탭에서 vox 를 다시 읽으면 풀립니다.',
    resolutions: [
      { key: 'recon-count', label: '대사 탭에서 vox 다시 읽기', action: 'recon-count' },
      { key: 'recalculate', label: '다시 계산', action: 'recalculate', hint: 'vox 를 다시 읽은 뒤 지금 값으로 다시 계산합니다.' },
    ],
  },
  // 검토 필요 정산서를 만든 뒤 정산 시작일이 바뀌었다(얼린 시작일 ≠ 지금 시작일, 차단) — 다시 계산하면 풀린다
  SETTLEMENT_START_CHANGED: {
    label: '정산 시작일 바뀜',
    description: '정산서를 만든 뒤 정산 시작일이 바뀌었습니다. 다시 계산해야 확정할 수 있습니다.',
    resolutions: [{ key: 'recalculate', label: '다시 계산', action: 'recalculate', hint: '바뀐 시작일로 다시 계산합니다.' }],
  },
  STATEMENT_DRIFT: {
    label: '확정 뒤 사용량 변동',
    description: '확정 이후 사용량이 바뀌었습니다. 확정 금액은 그대로이고, 반영하려면 재수정합니다.',
    resolutions: [],
  },
});

/**
 * @param {string} code
 * @returns {WarningMeta} 모르는 코드는 코드 그대로의 기본값
 */
export function warningMeta(code) {
  if (code && hasOwn(WARNING_META, code)) return WARNING_META[code];
  return { label: code ? String(code) : EMPTY, description: '알 수 없는 경고 코드입니다.', resolutions: [] };
}

/**
 * DEFAULT_PLAN_MISSING `details.legacy_version` — 그 달에 적용되는 기본 버전이 요금표 v70 이전 모양이면 그 버전
 * (개정 7). null 이면 그 달 버전이 없는 것이다.
 * @param {Array<object>|null|undefined} warnings
 * @returns {string|null}
 */
export function legacyPlanVersionOf(warnings) {
  const w = arr(warnings).find((x) => x?.code === 'DEFAULT_PLAN_MISSING');
  const v = w?.details && typeof w.details === 'object' ? w.details.legacy_version : null;
  return typeof v === 'string' && v ? v : null;
}

/**
 * 경고 하나의 해결 방법 — WARNING_META 의 것을 그 경고에 맞춘다. 기본 버전이 v70 이전 모양인 DEFAULT_PLAN_MISSING 은
 * "그 버전을 새 칸으로 다시 저장"으로 바꿔 말한다(개정 7).
 * @param {{ code: string, details?: any }} warning
 * @returns {WarningResolution[]}
 */
export function warningResolutions(warning) {
  const list = warningMeta(warning?.code).resolutions ?? [];
  const legacy = warning?.code === 'DEFAULT_PLAN_MISSING' ? legacyPlanVersionOf([warning]) : null;
  if (!legacy) return list;
  return list.map((r) =>
    r.action === 'rates-page' ? { ...r, label: `기본 요율 ${legacy} 버전을 새 칸으로 다시 저장하기` } : r
  );
}

/** Resolutions that only navigate or explain — the only ones offered outside review */
const TODO_NAV_ACTIONS = new Set(['rates-page', 'override-modal', 'recon-missing-ids', 'recon-count', 'wait', 'hint']);
/** Resolutions that are text only (no button) */
const TODO_TEXT_ACTIONS = new Set(['wait', 'hint']);

/**
 * 정산 탭 '확정 전에 볼 것' 목록에서 경고 하나의 해결 방법 배치 — 버튼은 두 개까지 앞에(primary, 경고 확인은 빼고 — 곁 카드의
 * 체크가 한다), 나머지 버튼(more)과 글만 있는 방법(texts)은 '다른 방법' 안에. 검토 필요가 아니면 화면 이동·안내만 남긴다.
 * 곁 카드는 이 배치로 [다시 계산]이 이미 목록 앞에 있는지 본다(같은 버튼을 두 번 보이지 않는다).
 * @param {{ code: string, details?: any }} warning
 * @param {string} status statementStatusOf(detail)
 * @returns {{ primary: WarningResolution[], more: WarningResolution[], texts: WarningResolution[], all: WarningResolution[] }}
 */
export function todoResolutionPlan(warning, status) {
  const all = warningResolutions(warning).filter((r) => status === 'review' || TODO_NAV_ACTIONS.has(r.action));
  const buttons = all.filter((r) => !TODO_TEXT_ACTIONS.has(r.action));
  const primary = buttons.filter((r) => r.action !== 'acknowledge').slice(0, 2);
  const more = buttons.filter((r) => !primary.includes(r));
  const texts = all.filter((r) => TODO_TEXT_ACTIONS.has(r.action));
  return { primary, more, texts, all };
}

/** @param {{ blocking?: boolean }|null|undefined} warning */
export function isBlockingWarning(warning) {
  return warning?.blocking === true;
}

/**
 * 경고의 건수 — `count` 가 없으면 `details[].count` 합. 셀 수 없으면 null.
 * @param {{ count?: number|null, details?: Array<{ count?: number }> }|null|undefined} warning
 * @returns {number|null}
 */
export function warningCount(warning) {
  if (isNumber(warning?.count)) return warning.count;
  const details = arr(warning?.details);
  if (details.length && details.every((d) => isNumber(d?.count))) {
    return details.reduce((sum, d) => sum + d.count, 0);
  }
  return null;
}

/**
 * WEB_USAGE_UNVERIFIED `details.reason` — 웹 사용량을 확정하지 못한 까닭(회신 표 1).
 * 끝난 달의 웹 테스트는 월말 뒤에 끝까지 읽은 vox 웹 요약으로만 청구한다.
 */
export const WEB_USAGE_REASON_TEXT = deepFreeze({
  not_read: '아직 안 읽음',
  incomplete: '끝까지 못 읽음',
  before_month_end: '월말 전에 읽은 값뿐',
});

/** 경고 `details` 가 객체(배열 아님)일 때만 — 코드마다 details 모양이 다르다 */
const detailsObject = (warning) =>
  warning?.details && typeof warning.details === 'object' && !Array.isArray(warning.details) ? warning.details : null;

/**
 * KST 날 'YYYY-MM-DD' → '10/12'(달·날 앞 0 없이)
 * @param {unknown} day
 * @returns {string|null} 모양이 아니면 null
 */
export function shortDayLabel(day) {
  const m = typeof day === 'string' ? /^\d{4}-(\d{2})-(\d{2})$/.exec(day) : null;
  return m ? `${Number(m[1])}/${Number(m[2])}` : null;
}

/**
 * COUNSELOR_COUNT_GAP `details`(`[{ day, count, before: { day, count }|null, after: { day, count }|null }]`) → 날마다 그날 쓴
 * 값과 앞뒤 기록(번호 요청 A6·A7). 그날 쓴 값은 번호를 추가할 때의 기록이 있으면 그 값, 없으면 0 이라 '0개'로 못박지 않고
 * details 값을 그대로 쓴다. 앞뒤 기록(매시 기록이 있는 가장 가까운 날)은 참고값이고 날짜순으로 한 번씩만 적는다 — 그날 개수의
 * 위 한계가 아니므로 화면이 그 값으로 금액을 채우지 않는다.
 * @param {unknown} details
 * @returns {{ days: string[], refs: string[] }} 예: { days: ['10/12 0개', '10/13 6개'], refs: ['10/11 5개', '10/14 6개'] }
 */
export function counselorGapParts(details) {
  const days = [];
  const refs = new Map();
  for (const d of arr(details)) {
    const label = shortDayLabel(d?.day);
    if (!label || !isNumber(d?.count)) continue;
    days.push(`${label} ${formatNumber(d.count)}개`);
    for (const ref of [d.before, d.after]) {
      const refLabel = shortDayLabel(ref?.day);
      if (refLabel && isNumber(ref?.count)) refs.set(ref.day, `${refLabel} ${formatNumber(ref.count)}개`);
    }
  }
  const sortedRefs = [...refs.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([, text]) => text);
  return { days, refs: sortedRefs };
}

/**
 * 경고 한 줄(사람이 읽는 문장)
 * @param {{ code: string, count?: number|null, details?: any, blocking?: boolean }} warning
 * @param {{ compact?: boolean, otherCost?: number|null }} [opts]
 *   compact: 통신료 미정의 번호 종류 분해·웹 사용량 미확인 사유·미종료 PBX 연결 보조 표기를 뺀다(미리보기 한 줄용).
 *   otherCost: 건수 없이 뜬 cold 전환의 기타 원가(상세 `cost.split.other`) — 요청 문서 문구의 '기타 원가'에 금액을 넣는다
 *   ('… — 기타 원가 n원으로 판단'). compact 면 짧은 이름('상담사 직접 연결 시간 빠짐(기타 원가 n원)')에 붙인다.
 * @returns {string} 건수 없는 cold 전환은 요청 문서 문구 'vox 직접 cold 전환 — 셀 수 없음 — 기타 원가로 판단'
 */
export function warningText(warning, { compact = false, otherCost = null } = {}) {
  if (!warning) return '';
  const n = warningCount(warning);
  const details = arr(warning.details);
  switch (warning.code) {
    case 'DEFAULT_PLAN_MISSING': {
      const legacy = legacyPlanVersionOf([warning]);
      if (!legacy) return '그 달에 적용할 기본 요율 버전 없음';
      return compact
        ? `기본 요율 ${legacy} 버전이 v70 이전 모양`
        : `기본 요율 ${legacy} 버전이 요금표 v70 이전 모양 — 새 칸으로 다시 저장할 때까지 계산하지 않음`;
    }
    case 'NOT_FINAL_CALLS': {
      const head = n === null ? '안 끝난 통화' : `안 끝난 통화 ${formatNumber(n)}건`;
      // 종료 이벤트를 놓친 PBX 연결은 기다려도 끝나지 않아 차단 사유가 아니다 — 진단값으로만 덧붙인다(회신 표 17)
      const legs = num(detailsObject(warning)?.pbx_legs);
      return !compact && legs ? `${head} · 종료 기록을 놓친 상담사 연결 ${formatNumber(legs)}건(마감을 막지 않음)` : head;
    }
    case 'ROUTE_RATE_MISSING': {
      const parts = details.map((d) => `${counselorItemLabel(d)} ${formatCount(d?.count, '통')}`);
      if (parts.length) return `판매가 미정 ${parts.join(', ')}`;
      return n === null ? '판매가 미정 상담사 연결' : `판매가 미정 상담사 연결 ${formatNumber(n)}통`;
    }
    case 'TELECOM_RATE_UNSET': {
      const head = n === null ? '통신료 미정 vox 직접' : `통신료 미정 vox 직접 ${formatNumber(n)}통`;
      const parts = details.map((d) => `${numberTypeLabel(d?.number_type)} ${formatNumber(d?.count)}`);
      return !compact && parts.length ? `${head}(${parts.join(' · ')})` : head;
    }
    case 'VOX_DIRECT_COLD_UNMEASURED': {
      // compact(곁 카드 체크박스·미리보기 한 줄) — 좁은 자리에 맞는 짧은 이름. 'cold' 같은 말을 쓰지 않는다
      if (compact) {
        if (n !== null) return `상담사 직접 연결 시간 빠짐 ${formatNumber(n)}건`;
        return isNumber(otherCost) ? `상담사 직접 연결 시간 빠짐(기타 원가 ${formatWon(otherCost)})` : '상담사 직접 연결 시간 빠짐';
      }
      if (n !== null) return `vox 직접 cold 전환 ${formatNumber(n)}건`;
      // 화면 명세 2-4: 건수 없이 뜬 경우(스냅샷 전 달)는 "셀 수 없음 — 기타 원가로 판단". 금액을 알면 그 자리에 넣는다
      // (같은 말을 꼬리로 되풀이하지 않는다)
      const basis = isNumber(otherCost) ? `기타 원가 ${formatWon(otherCost)}으로` : '기타 원가로';
      return `vox 직접 cold 전환 — 셀 수 없음 — ${basis} 판단`;
    }
    case 'LINE_RATE_UNSET': {
      const lines = num(warning.count) ?? (details.length || null);
      return lines === null ? '회선 판매가 미정' : `회선 판매가 미정 ${formatNumber(lines)}개`;
    }
    case 'COUNSELOR_COUNT_GAP': {
      // count = 빈 날 수(details 의 count 는 그날 쓴 값이라 더하지 않는다). 설명(고정)이 뒤 문장을 잇는다(번호 요청 A6)
      const gapDays = num(warning.count) ?? (details.length || null);
      if (compact) {
        return gapDays === null ? WARNING_META.COUNSELOR_COUNT_GAP.label : `상담사 번호 매시 기록 없는 날 ${formatNumber(gapDays)}일`;
      }
      const { days, refs } = counselorGapParts(details);
      const head = '상담사 번호 매시 기록이 없는 날이 있습니다.';
      if (!days.length) return head;
      return `${head} 그 날은 ${days.join(', ')}로 계산했습니다${refs.length ? `(앞뒤 기록: ${refs.join(', ')})` : ''}.`;
    }
    case 'COUNSELOR_FEE_DUPLICATE_SUSPECT':
      return compact
        ? WARNING_META.COUNSELOR_FEE_DUPLICATE_SUSPECT.label
        : "이 달 일회성 항목에 '상담사 번호'가 들어간 항목이 있습니다.";
    case 'COUNSELOR_FEE_NO_CALLS': {
      // details { extra_number_days, amount } — amount 는 계산했다면 받았을 금액
      if (compact) return WARNING_META.COUNSELOR_FEE_NO_CALLS.label;
      const amount = num(detailsObject(warning)?.amount);
      const lead = `${COUNSELOR_NO_CALLS_LEAD}${amount === null ? '' : `(계산했다면 ${formatWon(amount)})`}.`;
      return COUNSELOR_NO_CALLS_LINE_INPUT ? `${lead} ${COUNSELOR_NO_CALLS_TAIL}` : lead;
    }
    case 'COUNT_MISMATCH': {
      // 판정은 웹 테스트를 뺀 건수끼리다(회신 표 3) — 그 두 값이 오면 그것을, 예전 모양(전체 건수)도 읽는다
      const d = detailsObject(warning);
      if (isNumber(d?.db_non_web_total) && isNumber(d?.vox_live_non_web_total)) {
        return `건수 불일치(웹 제외) — DB ${formatNumber(d.db_non_web_total)}건 · vox ${formatNumber(d.vox_live_non_web_total)}건`;
      }
      if (isNumber(d?.db_total) && isNumber(d?.vox_live_total)) {
        return `건수 불일치 — DB ${formatNumber(d.db_total)}건 · vox ${formatNumber(d.vox_live_total)}건`;
      }
      return n === null ? '건수 불일치(vox ≠ DB)' : `건수 불일치 ${formatNumber(n)}건`;
    }
    case 'WEB_USAGE_UNVERIFIED': {
      const reason = detailsObject(warning)?.reason;
      const why = typeof reason === 'string' && hasOwn(WEB_USAGE_REASON_TEXT, reason) ? WEB_USAGE_REASON_TEXT[reason] : '';
      return !compact && why ? `웹 사용량 미확인 — vox 웹 요약 ${why}` : '웹 사용량 미확인';
    }
    case 'WEB_VOICE_COST_MISMATCH': {
      // details { voice_calls, voice_units_10s, expected_vox_cost, vox_cost, diff }
      const d = detailsObject(warning);
      const expected = num(d?.expected_vox_cost);
      const vox = num(d?.vox_cost);
      if (compact || expected === null || vox === null) return '웹 음성 원가 불일치';
      const calls = num(d?.voice_calls);
      const units = num(d?.voice_units_10s);
      const basis = [calls === null ? '' : `${formatNumber(calls)}통`, units === null ? '' : unitsMinutesText(units)]
        .filter(Boolean)
        .join(' · ');
      return `웹 음성 원가 불일치 — ${basis ? `${basis} → ` : ''}기대 ${formatWon(expected)} · vox ${formatWon(vox)}`;
    }
    case 'UNIT_RULE_MISMATCH':
      return n === null ? '10초 규칙 불일치' : `10초 규칙 불일치 ${formatNumber(n)}건`;
    case 'UNIT_RULE_UNCOSTED': {
      // details { count, call_ids } — 위 count 가 없는 응답이면 details.count
      const uncosted = n ?? num(detailsObject(warning)?.count);
      return uncosted === null ? '원가 없는 통화' : `원가 없는 통화 ${formatNumber(uncosted)}건`;
    }
    case 'INCLUDED_LINE_MISSING': {
      // details { vox_lines, real_calls } — 그 달 다른 vox 회선 수와 웹 제외 통화 수(개정 7)
      const d = detailsObject(warning);
      const voxLines = num(d?.vox_lines);
      const realCalls = num(d?.real_calls);
      if (compact) return '포함 회선 없음(월 사용료 전액)';
      const tail = [
        realCalls === null ? '' : `전화 ${formatNumber(realCalls)}통`,
        voxLines ? `다른 vox 회선 ${formatNumber(voxLines)}개` : '',
      ]
        .filter(Boolean)
        .join(' · ');
      return `포함 회선 없음 — vox 070 회선이 없어 월 사용료 전액${tail ? ` · ${tail}` : ''}`;
    }
    case 'NO_PHONE_USAGE': {
      // details { vox_lines, real_calls } — 웹 제외 통화는 0 이다(2026-10-16 사용자 결정)
      const voxLines = num(detailsObject(warning)?.vox_lines);
      if (compact) return '070 회선·전화 없음(월 사용료 확인)';
      const lines = voxLines ? ` · 다른 vox 회선 ${formatNumber(voxLines)}개` : '';
      return `070 회선·전화 없음 — vox 070 회선도 웹 제외 통화도 없이 월 사용료 전액${lines} · 개통 전이면 월 사용료 항목을 0 으로`;
    }
    case 'WEB_ONLY_USAGE':
      return '웹 테스트만 사용(청구됨)';
    case 'WEB_STORED_MISMATCH': {
      // details { vox_calls, stored_calls, missing_count, extra_count, ... } — 빠진 통화는 vox 조회가 이미 채워 넣었다
      const d = detailsObject(warning);
      const missing = num(d?.missing_count);
      const extra = num(d?.extra_count);
      const head = n === null ? '웹 기록 대조 차이' : `웹 기록 대조 차이 ${formatNumber(n)}건`;
      if (compact) return head;
      const parts = [
        missing ? `웹훅이 놓친 통화 ${formatNumber(missing)}건(vox 조회로 채움)` : '',
        extra ? `저장분에만 있는 통화 ${formatNumber(extra)}건` : '',
      ].filter(Boolean);
      return `${head}${parts.length ? ` — ${parts.join(' · ')}` : ''} · 금액 영향 없음`;
    }
    case 'RECONCILIATION_UNAVAILABLE':
      return 'vox 건수를 읽지 못해 건수 대사 없이 마감';
    case 'STATEMENT_DRIFT':
      return '확정 이후 사용량이 바뀌었습니다';
    // 정산 시작일(2·3단계) — 이름은 WARNING_META label 그대로(해결 방법은 패널의 resolutions)
    case 'SETTLEMENT_START_CHANGED':
    case 'WEB_START_SPLIT_UNAVAILABLE':
      return WARNING_META[warning.code].label;
    default:
      return n === null ? String(warning.code ?? EMPTY) : `${warning.code} ${formatNumber(n)}건`;
  }
}

/**
 * 차단 / 비차단으로 나눈다(순서 유지). 판정은 각 경고의 `blocking` 이다.
 * @param {Array<object>|null|undefined} warnings
 * @returns {{ blocking: object[], nonBlocking: object[] }}
 */
export function splitWarnings(warnings) {
  const blocking = [];
  const nonBlocking = [];
  for (const w of arr(warnings)) (isBlockingWarning(w) ? blocking : nonBlocking).push(w);
  return { blocking, nonBlocking };
}

/**
 * 정산 요약 줄의 "경고 n건" — 차단이 하나라도 있으면 빨강, 비차단만 있으면 회색, 없으면 초록 "정상".
 * @param {Array<object>|null|undefined} warnings
 * @returns {{ total: number, blocking: number, nonBlocking: number, tone: 'danger'|'muted'|'ok', label: string, tooltipLines: string[] }}
 */
export function warningSummary(warnings) {
  const { blocking, nonBlocking } = splitWarnings(warnings);
  const total = blocking.length + nonBlocking.length;
  const describe = (list) => list.map((w) => `${warningMeta(w.code).label}(${w.code})`).join(', ');
  const tooltipLines = [];
  if (blocking.length) tooltipLines.push(`차단: ${describe(blocking)}`);
  if (nonBlocking.length) tooltipLines.push(`비차단: ${describe(nonBlocking)}`);
  if (!total) tooltipLines.push('경고 없음');
  return {
    total,
    blocking: blocking.length,
    nonBlocking: nonBlocking.length,
    tone: blocking.length ? 'danger' : nonBlocking.length ? 'muted' : 'ok',
    label: total ? `경고 ${formatNumber(total)}건` : '정상',
    tooltipLines,
  };
}

/**
 * 추정 중(마감 전) 미리보기 한 줄 — 화면 명세 2-4.
 * WEB_USAGE_UNVERIFIED 는 마감이 vox 웹 요약을 먼저 읽으면 대개 풀린다(회신 표 1) — "검토 필요"로 단정하지 않는다.
 * @param {Array<object>|null|undefined} warnings
 * @returns {string} 예: '지금 마감하면 검토 필요: 안 끝난 통화 3건, 판매가 미정 PBX → 대표번호 39통, 통신료 미정 vox 직접 368통'
 */
export function closePreviewText(warnings) {
  const { blocking: allBlocking, nonBlocking } = splitWarnings(warnings);
  const webUnverified = allBlocking.some((w) => w?.code === 'WEB_USAGE_UNVERIFIED');
  const blocking = allBlocking.filter((w) => w?.code !== 'WEB_USAGE_UNVERIFIED');
  if (blocking.length) {
    const head = `지금 마감하면 검토 필요: ${blocking.map((w) => warningText(w, { compact: true })).join(', ')}`;
    return webUnverified ? `${head} · 웹 사용량 미확인은 마감이 vox 웹 요약을 읽으면 풀립니다` : head;
  }
  if (webUnverified) {
    return '지금 마감하면 vox 웹 요약을 먼저 읽습니다 — 읽히면 웹 사용량 미확인이 풀려 그대로 판정하고, 못 읽으면 검토 필요(웹 사용량 미확인)로 멈춥니다.';
  }
  if (nonBlocking.length) {
    return `지금 마감하면 자동 확정됩니다. 비차단 경고 ${formatNumber(nonBlocking.length)}건은 기록만 합니다.`;
  }
  return '지금 마감하면 경고 없이 자동 확정됩니다.';
}

/**
 * 판매가 미정이라 금액에서 빠진 줄 — 공급가액 `일부 제외` 툴팁, 검토 폼의 "미정으로 제외:" 줄.
 * 요금표 v70 에서 빠지는 것은 회선(LINE_RATE_UNSET)뿐이다. 상담사 연결 경고(ROUTE_RATE_MISSING·TELECOM_RATE_UNSET)는 v70
 * 이전 정산 스냅샷에만 남아 줄을 지정하지 않을 때만 센다.
 * @param {Array<object>|null|undefined} warnings
 * @param {'base'|'inbound'|'outbound'|'web_text'|'line'} [line] 그 줄 것만(없으면 전부)
 * @returns {string[]} 예: ['대표번호 회선 ···1588 (27일)']
 */
export function excludedChargeLines(warnings, line) {
  const out = [];
  const wantCounselor = !line;
  const wantLine = !line || line === 'line';
  for (const w of arr(warnings)) {
    const details = arr(w?.details);
    if (w?.code === 'ROUTE_RATE_MISSING' && wantCounselor) {
      if (details.length) {
        details.forEach((d) => out.push(`${counselorItemLabel(d)} ${formatCount(d?.count, '통')}`));
      } else {
        const n = warningCount(w);
        out.push(n === null ? '판매가 미정 상담사 연결' : `판매가 미정 상담사 연결 ${formatNumber(n)}통`);
      }
    } else if (w?.code === 'TELECOM_RATE_UNSET' && wantCounselor) {
      const n = warningCount(w);
      out.push(n === null ? 'vox 직접 통신료' : `vox 직접 통신료 ${formatNumber(n)}통`);
    } else if (w?.code === 'LINE_RATE_UNSET' && wantLine) {
      if (details.length) {
        details.forEach((d) => {
          const days = isNumber(d?.days) ? ` (${d.days}일)` : '';
          out.push(`${lineTypeLabel(d?.line_type)} 회선 ${maskNumber(d?.number_last4)}${days}`);
        });
      } else {
        const n = num(w.count);
        out.push(n === null ? '판매가 미정 회선' : `판매가 미정 회선 ${formatNumber(n)}개`);
      }
    }
  }
  return out;
}

/** 판매가가 없어 금액에서 뺀 줄을 알리는 경고가 있는가(일부 제외의 근거) */
function hasExcludable(warnings) {
  return arr(warnings).some((w) => ['ROUTE_RATE_MISSING', 'TELECOM_RATE_UNSET', 'LINE_RATE_UNSET'].includes(w?.code));
}

/**
 * 비차단 경고의 할 일 한 줄 — 경고 목록(회색)에 붙인다. INCLUDED_LINE_MISSING 은 전화가 있는 달(병원 자기 번호·대표번호)
 * 이라 그대로 둔다 — 개통 전 달(전화도 없음)은 NO_PHONE_USAGE(차단)가 따로 멈춘다. 내부·테스트처럼 NO_PHONE_USAGE 가
 * 비차단으로 온 경우에는 그 안내를 붙인다. STATEMENT_DRIFT 는 재수정으로 반영한다.
 * @param {{ code: string }} warning
 * @param {string} status statementStatusOf(detail)
 * @returns {string} 없으면 ''
 */
export function nonBlockingHint(warning, status) {
  switch (warning?.code) {
    case 'INCLUDED_LINE_MISSING':
      return '병원 자기 번호·대표번호만 쓰는 달이라 그대로 둡니다';
    case 'NO_PHONE_USAGE':
      if (status === 'confirmed') return '개통 전 달이었다면 재수정 → 월 사용료 항목 0 저장 → 확정합니다';
      return '개통 전 달이면 월 사용료 항목을 0 으로 저장하고 확정합니다';
    case 'STATEMENT_DRIFT':
      return '반영하려면 재수정합니다';
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// 4. 요율 칸 (요청 계약 3-1·3-2, 화면 명세 2-1·3, 요금표 v70 개정 1·2·3)
// ---------------------------------------------------------------------------

/** PBX STT 측정 기준(읽기 전용, 전역) — 요금표 v70 부터 원가 추정에만 쓴다 */
export const STT_BASIS = 'talk_seconds_x2_monthly_prorata';
export const STT_BASIS_LABEL = '상담사 통화 시간 × 2 의 월 합계 비례';
/** 부가세 표기(읽기 전용, `vat_mode: excluded` 고정) */
export const VAT_LABEL = '별도(공급가액의 10%, 원 미만 절사)';
/** 원가·단위를 모르는 칸의 판매가 입력을 막을 때의 툴팁 */
export const COST_UNKNOWN_TOOLTIP = '원가 확인 뒤 입력';
/** 판매가 대 원가 보조 표기 */
export const PRICE_COST_LABELS = deepFreeze({ below: '원가 미만', equal: '마진 0' });
/** 월 사용료에 들어 있는 것(요금표 v70) — 상담사 번호 요금이 없는 달의 문구(baseIncludesText) */
const BASE_INCLUDES_PLAIN = '120분 통화 · 070 1회선 · 상담사 연결 · 저장 공간 10GB 포함';
/** 상담사 연결은 판매가가 없다(월 사용료에 포함, 통화 시간은 120분에 든다) — 원가 참고 칸의 안내(counselorIncludedText) */
const COUNSELOR_CONNECT_COST_TEXT =
  '상담사 연결은 월 사용료에 들어 있어 판매가가 없습니다. 상담사 통화 시간은 120분 사용량에 들고, 아래 원가는 원가·마진 추정에만 씁니다.';

/**
 * 상담사 번호 요율 블록(번호 요청 요청 계약 1·3) — 상세 `rates.counselor_number`(`{ included: RatedValue, monthly: RatedValue }
 * | null`)와 기본 요율 버전·편집 값의 블록(`{ included, monthly }` | null | 키 없음)을 같은 모양으로 읽는다.
 * @param {object|null|undefined} ratesOrPlan
 * @returns {{ included: number|null, monthly: number|null }|null} 블록이 없으면(null·키 없음·객체 아님) null — 요금 없음
 */
export function counselorNumberRates(ratesOrPlan) {
  const block = ratesOrPlan && typeof ratesOrPlan === 'object' ? ratesOrPlan.counselor_number : null;
  if (!block || typeof block !== 'object' || Array.isArray(block)) return null;
  return {
    included: readCell(ratesOrPlan, 'counselor_number.included').value,
    monthly: readCell(ratesOrPlan, 'counselor_number.monthly').value,
  };
}

/**
 * 상담사 번호 요금이 있는 달(버전)의 블록 — 블록이 있고 포함 수와 (유효) 1개당 월 금액이 숫자이며 월 금액이 0 보다 크다.
 * 블록이 있어도 `monthly` 가 0 이거나 예외로 0(면제)이면 요금이 없다(번호 요청 요청 계약 1 "`monthly` 가 0 이어도 요금이
 * 없다", 요청 계약 3 의 `no_rate` — 예외 0(면제) 포함, call-api C4). 편집 중 비운 칸(null)도 요금이 정해지지 않았으니 없다.
 * 월 사용료 안의 것(baseIncludesText)과 상담사 연결 원가 안내(counselorIncludedText)가 같은 판정을 쓴다 — 그런 달에 상담사
 * 번호를 말하면 같은 상세의 원가표 "이 달 요율에 상담사 번호 요금 없음"과 어긋난다.
 * @param {object|null|undefined} ratesOrPlan
 * @returns {{ included: number, monthly: number }|null} 요금이 없으면 null
 */
function chargedCounselorNumber(ratesOrPlan) {
  const block = counselorNumberRates(ratesOrPlan);
  return block && isNumber(block.included) && isNumber(block.monthly) && block.monthly > 0 ? block : null;
}

/**
 * 월 사용료에 들어 있는 것 — 카드·편집 모달·원가표·근거 패널의 안내(번호 요청 A4). 상담사 번호 요금이 있는 달(버전)은
 * 그 달 기본 포함 수를 붙인다(할 일 2-1 "· 상담사 번호 3개를 붙인다"). 요금이 있는 달은 블록이 있고 유효 `monthly` 가 0 보다
 * 큰 달이다(chargedCounselorNumber) — `monthly` 0·예외 면제 0 은 블록이 없는 달과 같다.
 * @param {object|null|undefined} ratesOrPlan 상세 `rates`·기본 요율 버전·편집 중인 값(counselorNumberRates 가 읽는 모양)
 * @returns {string} 예: '120분 통화 · 070 1회선 · 상담사 연결 · 저장 공간 10GB 포함 · 상담사 번호 3개'
 */
export function baseIncludesText(ratesOrPlan) {
  const block = chargedCounselorNumber(ratesOrPlan);
  if (!block) return BASE_INCLUDES_PLAIN;
  return `${BASE_INCLUDES_PLAIN} · 상담사 번호 ${formatNumber(block.included)}개`;
}

/**
 * 상담사 연결 원가 칸의 안내(번호 요청 A4) — 지금 문장 앞에 상담사 연결(통화 넘기기)과 상담사 번호를 구분하는 문장을 붙인다.
 * 요금이 없는 버전·달은 구분 문장의 '상담사 번호는 …' 부분을 뺀다. 블록이 없을 때만이 아니라 (유효) `monthly` 가 0 일 때도
 * 요금이 없다(chargedCounselorNumber). 그런 달에 '추가 요금'이라고 쓰면 같은 상세의 원가표 "이 달 요율에 상담사 번호 요금
 * 없음"과 어긋난다.
 * @param {object|null|undefined} ratesOrPlan
 * @returns {string}
 */
export function counselorIncludedText(ratesOrPlan) {
  const block = chargedCounselorNumber(ratesOrPlan);
  const lead = block
    ? `상담사 연결(통화 넘기기)은 포함, 상담사 번호는 ${formatNumber(block.included)}개를 넘으면 추가 요금`
    : '상담사 연결(통화 넘기기)은 포함';
  return `${lead}. ${COUNSELOR_CONNECT_COST_TEXT}`;
}

/** 상담사 번호 요금을 켤 때 채우는 값(번호 요청 A1 — 기본 포함 3개, 추가 1개당 월 49,000원 VAT 별도) */
export const COUNSELOR_NUMBER_DEFAULTS = deepFreeze({ included: 3, monthly: 49000 });

/**
 * 요율 카드 타일의 요금 문장(번호 요청 A3) — '4번째'는 포함 수 + 1 이다.
 * @param {unknown} included
 * @param {unknown} monthly
 * @returns {string} 예: '기본 3개 포함 · 4번째부터 1개당 월 49,000원(VAT 별도)', 숫자가 아니면 '요금 없음'
 */
export function counselorNumberRateText(included, monthly) {
  if (!isNumber(included) || !isNumber(monthly)) return '요금 없음';
  return `기본 ${formatNumber(included)}개 포함 · ${formatNumber(included + 1)}번째부터 1개당 월 ${formatNumber(monthly)}원(VAT 별도)`;
}

/** 수납 시작 월이 아직 없을 때의 상담사 번호 요금 경고 — 서버 문장의 '수납 시작 월보다 먼저'가 맞지 않아 화면이 바꾼다 */
export const FEE_START_NO_FROM_TEXT =
  '상담사 번호 요금은 수납 시작 월이 정해지기 전까지 정산서에만 나오고 받지 않습니다';

/** 상담사 번호 요금 시작 월 경고(G-FEE-START) — 결제 계획 3.6·12.1 문구. call-api 가 `message` 로 같은 문장을 준다 */
export const FEE_START_WARNING_TEXT =
  '상담사 번호 요금이 수납 시작 월보다 먼저 시작합니다. 그 달들은 정산서에만 나오고 받지 않습니다';

/**
 * 기본 요율 목록의 상담사 번호 요금 시작 월 경고(번호 요청 B, call-api 1-3 `fee_start_warning`, 계약 묶음 9.5).
 * 보일지는 call-api 가 준 값만 따른다 — 판정(요금 블록이 있는 가장 이른 버전의 달이 수납 시작 월보다 이르거나 수납 시작 월이
 * 없음)은 call-api 가 한다. 값이 null 이거나 키가 없으면(옛 call-api) 숨긴다(오류로 보이지 않는다).
 * @param {object|null|undefined} res `GET /api/v1/callbot/billing/default-plans` 응답
 * @returns {{ message: string, months: string }|null} months: 문구 뒤에 붙이는 두 달 — 예:
 *   '2026-10 버전부터 · 수납 시작 월 2026-11', 수납 시작 월이 없으면 '… · 수납 시작 월 미정'(이때 message 는
 *   FEE_START_NO_FROM_TEXT — 서버 문장의 '보다 먼저'가 맞지 않는다)
 */
export function feeStartWarningView(res) {
  const w = res && typeof res === 'object' ? res.fee_start_warning : null;
  if (!w || typeof w !== 'object' || Array.isArray(w)) return null;
  const message = typeof w.message === 'string' && w.message.trim() ? w.message.trim() : FEE_START_WARNING_TEXT;
  const first = isValidMonth(w.first_fee_month) ? w.first_fee_month : null;
  const from = isValidMonth(w.from_month) ? w.from_month : null;
  const months = first ? `${first} 버전부터 · 수납 시작 월 ${from ?? '미정'}` : '';
  // No start month yet — the server sentence ('수납 시작 월보다 먼저') does not fit; say what happens instead
  return { message: first && !from ? FEE_START_NO_FROM_TEXT : message, months };
}

/** 칸 묶음 이름 */
export const RATE_GROUP_LABELS = deepFreeze({
  basic: '구분',
  base: '기본',
  call: '통화',
  web: '웹 텍스트',
  line: '회선',
  counselor_number: '상담사 번호',
});

/**
 * 요율 칸 하나(요금표 v70 — 덮을 수 있는 칸 아홉, 개정 2).
 * - `pricePath`: 요율 본문(개정 1)·상세 `rates`(개정 3 — 칸마다 `{ value, source, default_value? }`)·예외 본문(개정 2)에서
 *   그 칸의 자리
 * - `unitPath`: 과금 단위(초)가 사는 자리(수신 초과·받은 발신 통화 — 10초)
 * - `costPath`: `cost_rates` 안의 원가 자리(월 사용료·포함 통화는 원가가 없다)
 * - `snapshotCostPath`·`snapshotUnitPath`: 요율 본문·`rates` 에 실린 원가 스냅샷 자리(없으면 cost_rates 만 본다)
 * - `kind`: 'internal'(boolean 구분) | 'fee'(VAT 포함 월 사용료, 원 정수) | 'minutes'(포함 통화, 분 정수) |
 *   'per_minute'(1분당 — 120분 초과, 10초 단위로 세고 ÷ 6) | 'price'(10초 단가) | 'per_call'(건당) |
 *   'pass_through'(웹 텍스트 boolean) | 'monthly'(회선 월 금액, null = 미정) |
 *   'count'(상담사 번호 기본 포함 수, 개 정수) | 'monthly_excl'(상담사 번호 추가 1개당 월 금액, VAT 별도 원 정수) — 이 둘은
 *   블록째 켜고 끄며 미정이 없다(값이 없으면 '요금 없음')
 * - `overridable`: 회사 예외로 덮을 수 있다(지금은 전부)
 * - `defaultEditable`: 기본 요율 편집 모달에서 고친다(`internal` 은 기본 요율에서 false 고정)
 * - `requiredInDefault`: 기본 요율에서 비울 수 없다(`base`·`inbound`·`outbound` — 서버 null_not_allowed). 상담사 번호 두 칸은
 *   블록을 켰을 때만 비울 수 없어 false 로 두고 편집 모달이 따로 검사한다
 * - `integer`: 0 이상 정수만 받는다(`base` 두 칸·상담사 번호 두 칸 — 서버 not_integer)
 * @typedef {Object} RateCell
 * @property {string} key
 * @property {'basic'|'base'|'call'|'web'|'line'|'counselor_number'} group
 * @property {string} label 예: '120분 초과 통화'
 * @property {string} shortLabel 묶음 안 이름 예: '추가 070'
 * @property {'internal'|'fee'|'minutes'|'per_minute'|'price'|'per_call'|'pass_through'|'monthly'|'count'|'monthly_excl'} kind
 * @property {string[]} pricePath
 * @property {string[]|null} unitPath
 * @property {string[]|null} costPath
 * @property {string[]|null} snapshotCostPath
 * @property {string[]|null} snapshotUnitPath
 * @property {boolean} overridable
 * @property {boolean} defaultEditable
 * @property {boolean} requiredInDefault
 * @property {boolean} integer
 */

/** @returns {RateCell} */
function rateCell(def) {
  return {
    shortLabel: def.label,
    unitPath: null,
    snapshotCostPath: null,
    snapshotUnitPath: null,
    overridable: true,
    defaultEditable: true,
    requiredInDefault: false,
    integer: false,
    ...def,
  };
}

/**
 * 요율 칸 전부(표시 순서): 구분 → 월 사용료(VAT 포함)·포함 통화 → 120분 초과 통화·발신 건당·받은 발신 통화 → 웹 텍스트 →
 * 추가 070 회선·대표번호 회선 → 상담사 번호 기본 포함 수·추가 1개당 월 금액(번호 요청 A1). 상담사 연결은 판매가 칸이 없다
 * (월 사용료에 포함 — 원가만 COUNSELOR_COST_ROWS).
 * @type {ReadonlyArray<RateCell>}
 */
export const RATE_CELLS = deepFreeze([
  rateCell({
    key: 'internal',
    group: 'basic',
    label: '구분',
    kind: 'internal',
    pricePath: ['internal'],
    costPath: null,
    defaultEditable: false,
  }),
  rateCell({
    key: 'base.monthly',
    group: 'base',
    label: '월 사용료',
    shortLabel: '월 사용료(VAT 포함)',
    kind: 'fee',
    pricePath: ['base', 'monthly_vat_included'],
    costPath: null,
    requiredInDefault: true,
    integer: true,
  }),
  rateCell({
    key: 'base.minutes',
    group: 'base',
    label: '포함 통화',
    kind: 'minutes',
    pricePath: ['base', 'included_minutes'],
    costPath: null,
    requiredInDefault: true,
    integer: true,
  }),
  // 1분당 판매가(2026-10-16 사용자 결정 — 1분 200원). 초과분은 여전히 통화마다 10초 단위로 세고, 금액은 단위 × 1분당 ÷ 6
  rateCell({
    key: 'inbound.overage',
    group: 'call',
    label: '120분 초과 통화',
    kind: 'per_minute',
    pricePath: ['inbound', 'overage_price_per_minute'],
    unitPath: ['inbound', 'unit_sec'],
    costPath: ['ai'],
    snapshotCostPath: ['ai', 'cost_per_unit'],
    snapshotUnitPath: ['ai', 'unit_sec'],
    requiredInDefault: true,
  }),
  rateCell({
    key: 'outbound.per_call',
    group: 'call',
    label: '발신 건당',
    kind: 'per_call',
    pricePath: ['outbound', 'per_call'],
    costPath: ['extras', 'outbound_base_per_call'],
    requiredInDefault: true,
  }),
  rateCell({
    key: 'outbound.unit',
    group: 'call',
    label: '받은 발신 통화',
    kind: 'price',
    pricePath: ['outbound', 'price_per_unit'],
    unitPath: ['outbound', 'unit_sec'],
    costPath: ['ai'],
    snapshotCostPath: ['ai', 'cost_per_unit'],
    snapshotUnitPath: ['ai', 'unit_sec'],
    requiredInDefault: true,
  }),
  rateCell({
    key: 'web_text',
    group: 'web',
    label: '웹 텍스트',
    kind: 'pass_through',
    pricePath: ['web_text', 'pass_through'],
    costPath: ['web_text_per_message'],
    snapshotCostPath: ['web_text', 'cost_per_message'],
  }),
  rateCell({
    key: 'line.070',
    group: 'line',
    label: '추가 070 회선',
    shortLabel: '추가 070',
    kind: 'monthly',
    pricePath: ['line', '070', 'monthly'],
    costPath: ['line', '070'],
    snapshotCostPath: ['line', '070', 'cost'],
  }),
  rateCell({
    key: 'line.uan',
    group: 'line',
    label: '대표번호 회선',
    shortLabel: '대표번호',
    kind: 'monthly',
    pricePath: ['line', 'uan', 'monthly'],
    costPath: ['line', 'uan'],
    snapshotCostPath: ['line', 'uan', 'cost'],
  }),
  // 상담사 번호 추가 요금(번호 요청 A1, 요청 계약 1·2) — 원가가 없고, 블록째 켜고 끈다(없거나 null = 요금 없음)
  rateCell({
    key: 'counselor_number.included',
    group: 'counselor_number',
    label: '상담사 번호 기본 포함 수',
    shortLabel: '기본 포함 번호 수',
    kind: 'count',
    pricePath: ['counselor_number', 'included'],
    costPath: null,
    integer: true,
  }),
  rateCell({
    key: 'counselor_number.monthly',
    group: 'counselor_number',
    label: '상담사 번호 추가 1개당 월 금액',
    shortLabel: '추가 번호 1개당 월 금액(VAT 별도)',
    kind: 'monthly_excl',
    pricePath: ['counselor_number', 'monthly'],
    costPath: null,
    integer: true,
  }),
]);

/** key → RateCell */
export const RATE_CELL_MAP = deepFreeze(Object.fromEntries(RATE_CELLS.map((c) => [c.key, c])));

/** boolean 칸 */
const BOOLEAN_KINDS = new Set(['internal', 'pass_through']);

/** 덮을 수 있는 칸의 값 객체인가 — 상세 `rates`(개정 3)는 `{ value, source, default_value? }`, 요율 본문·예외는 값만 */
const isRatedLeaf = (raw) => !!raw && typeof raw === 'object' && !Array.isArray(raw) && hasOwn(raw, 'value');

/** 칸 종류에 맞는 값만 남긴다(아니면 null) */
function cellValue(cell, raw) {
  if (BOOLEAN_KINDS.has(cell.kind)) return typeof raw === 'boolean' ? raw : null;
  return isNumber(raw) ? raw : null;
}

/**
 * 칸 하나를 읽는다. 상세 `rates`(덮을 칸마다 `{ value, source, default_value? }`)와 요율 본문·예외(값만)를 같은 모양으로
 * 돌려준다. 칸이 없으면 value null(= 미정). defaultValue 는 예외 칸의 그 달 기본값(`default_value` — 예외일 때만).
 * @param {object|null|undefined} ratesOrPlan
 * @param {string} key RATE_CELLS 의 key
 * @returns {{ value: number|boolean|null, source: 'default'|'override'|null, defaultValue: number|boolean|null|undefined, unitSec: number|null, cost: number|null }}
 */
export function readCell(ratesOrPlan, key) {
  const cell = RATE_CELL_MAP[key];
  const empty = { value: null, source: null, defaultValue: undefined, unitSec: null, cost: null };
  if (!cell || !ratesOrPlan || typeof ratesOrPlan !== 'object') return empty;
  const raw = getPath(ratesOrPlan, cell.pricePath);
  const leaf = isRatedLeaf(raw) ? raw : null;
  return {
    value: cellValue(cell, leaf ? leaf.value : raw),
    source: leaf && (leaf.source === 'default' || leaf.source === 'override') ? leaf.source : null,
    defaultValue: leaf && hasOwn(leaf, 'default_value') ? cellValue(cell, leaf.default_value) : undefined,
    unitSec: cell.unitPath ? num(getPath(ratesOrPlan, cell.unitPath)) : null,
    cost: cell.snapshotCostPath ? num(getPath(ratesOrPlan, cell.snapshotCostPath)) : null,
  };
}

/**
 * 1분당 칸(120분 초과)의 원가를 같은 단위로 — AI 원가(22원/10초)를 1분당(132원)으로 바꾼다. 판매가와 견주고(원가 미만·
 * 마진 0) 같은 단위로 적으려고. 그 밖의 칸은 그대로다.
 */
function inCellUnit(key, info) {
  if (RATE_CELL_MAP[key]?.kind !== 'per_minute' || !isNumber(info.cost) || !isNumber(info.unitSec) || info.unitSec <= 0) {
    return info;
  }
  return { ...info, cost: round2((info.cost * 60) / info.unitSec), unitSec: 60 };
}

/**
 * `cost_rates` 에서 칸 하나의 원가를 읽는다(읽기 전용 참고값). 수신 초과는 AI 원가를 1분당으로(132원/분), 받은 발신
 * 통화는 AI 원가(22원/10초), 발신 건당은 vox 발신 기본료(20원/건), 웹 텍스트는 메시지당 원가, 회선은 월 원가다.
 * @param {object|null|undefined} costRates
 * @param {string} key
 * @returns {{ unitSec: number|null, cost: number|null, pending: string|null }}
 */
export function readCellCost(costRates, key) {
  const cell = RATE_CELL_MAP[key];
  const empty = { unitSec: null, cost: null, pending: null };
  if (!cell || !cell.costPath || !costRates) return empty;
  const raw = getPath(costRates, cell.costPath);
  if (isNumber(raw)) return { ...empty, cost: raw };
  if (!raw || typeof raw !== 'object') return empty;
  return inCellUnit(key, {
    unitSec: num(raw.unit_sec),
    cost: num(raw.cost) ?? num(raw.cost_per_unit),
    pending: typeof raw.pending === 'string' ? raw.pending : null,
  });
}

/** 요율(상세 `rates`·버전 본문)에 실린 칸의 원가 스냅샷 — 그 자리가 없으면 null */
function cellCostSnapshot(source, key) {
  const cell = RATE_CELL_MAP[key];
  if (!cell?.snapshotCostPath || !source || typeof source !== 'object') return null;
  if (getPath(source, cell.snapshotCostPath) === undefined) return null;
  return inCellUnit(key, {
    unitSec: cell.snapshotUnitPath ? num(getPath(source, cell.snapshotUnitPath)) : null,
    cost: num(getPath(source, cell.snapshotCostPath)),
  });
}

/**
 * 칸 하나의 원가·과금 단위 — 오늘 원가(`cost_rates`)와 요율에 실린 원가 스냅샷 중 어느 것을 먼저 볼지 정해 읽는다.
 *  - 'snapshot'(유효 요율 카드 — 정산 스냅샷·버전은 원가가 나중에 바뀌어도 흔들리지 않는다): `source` 에 원가 스냅샷이
 *    있으면 그것, 없으면 cost_rates.
 *  - 'live'(기본 요율 화면·편집 모달 — 오늘 원가를 보고 판매가를 정한다): cost_rates 에 그 칸이 있으면 그것, 없으면
 *    `source` 의 원가 스냅샷.
 * 둘 다 없으면 null — 원가 줄을 그리지 않는다. 구분·월 사용료·포함 통화 칸은 원가가 없어 늘 null.
 * @param {string} key RATE_CELLS 의 key
 * @param {{ costRates?: object|null, source?: object|null, prefer?: 'snapshot'|'live' }} [opts]
 * @returns {{ unitSec: number|null, cost: number|null, pending: string|null }|null}
 */
export function cellCostInfo(key, { costRates = null, source = null, prefer = 'live' } = {}) {
  const cell = RATE_CELL_MAP[key];
  if (!cell?.costPath) return null;
  const hasLive = !!costRates && typeof costRates === 'object' && getPath(costRates, cell.costPath) !== undefined;
  const snapshot = cellCostSnapshot(source, key);
  if (prefer === 'snapshot') {
    if (snapshot) return { ...snapshot, pending: null };
    return hasLive ? readCellCost(costRates, key) : null;
  }
  if (hasLive) return readCellCost(costRates, key);
  return snapshot ? { ...snapshot, pending: null } : null;
}

/**
 * 과금 단위 이름
 * @param {number|null|undefined} unitSec
 * @param {string} [key] 칸 key — 회선·월 사용료는 '월', 웹 텍스트는 '메시지', 발신 건당·내선은 '건', 포함 통화는 '분'
 * @returns {string} 10 → '10초', 60 → '분', 180 → '3분', 3600 → '1시간', null → '건'
 */
export function unitLabel(unitSec, key) {
  const kind = key ? RATE_CELL_MAP[key]?.kind : null;
  if (kind === 'monthly' || kind === 'fee' || kind === 'monthly_excl') return '월';
  if (kind === 'count') return '개';
  if (kind === 'pass_through') return '메시지';
  if (kind === 'minutes' || kind === 'per_minute') return '분';
  if (kind === 'per_call' || key === 'pbx.extension' || !isNumber(unitSec) || unitSec <= 0) return '건';
  if (unitSec === 60) return '분';
  if (unitSec % 3600 === 0) return `${formatNumber(unitSec / 3600)}시간`;
  if (unitSec % 60 === 0) return `${unitSec / 60}분`;
  return `${unitSec}초`;
}

/**
 * 올림 단위 이름(문장 안에 쓴다 — "통화마다 1분 단위 올림"). unitLabel 과 같고 60초만 '1분'으로 읽는다.
 * @param {unknown} unitSec
 * @returns {string} 10 → '10초', 60 → '1분', 180 → '3분', 3600 → '1시간', 없거나 0 이하면 ''
 */
export function unitWord(unitSec) {
  if (!isNumber(unitSec) || unitSec <= 0) return '';
  return unitSec === 60 ? '1분' : unitLabel(unitSec);
}

/** 10초 단가 칸의 단위(요율에 단위가 없으면 요금표 v70 의 10초) */
const DEFAULT_BILLING_UNIT_SEC = 10;

/**
 * 판매가 표기
 * @param {string} key
 * @param {number|boolean|null|undefined} value
 * @param {number|null} [unitSec] 10초 단가 칸의 단위(없으면 10초)
 * @param {{ perMessage?: number }} [opts] 웹 텍스트 메시지당 원가(`cost_rates.web_text_per_message`, 기본 20)
 * @returns {string} 예: '월 99,000원(VAT 포함)', '120분', '1분당 200원', '50원/10초', '100원/건', '월 9,000원', '미정',
 *   상담사 번호 '3개'·'월 49,000원(VAT 별도)'(값이 없으면 '요금 없음' — 이 요금에는 미정이 없다)
 */
export function formatRateValue(key, value, unitSec = null, { perMessage = 20 } = {}) {
  const kind = RATE_CELL_MAP[key]?.kind ?? 'price';
  if (kind === 'internal') return categoryLabel(typeof value === 'boolean' ? value : null);
  if (kind === 'pass_through') {
    if (value === true) return `메시지당 ${formatNumber(perMessage)}원(원가 그대로)`;
    return value === false ? '청구 안 함' : '미정';
  }
  if (kind === 'count' || kind === 'monthly_excl') {
    if (!isNumber(value)) return '요금 없음';
    return kind === 'count' ? `${formatNumber(value)}개` : `월 ${formatNumber(value)}원(VAT 별도)`;
  }
  if (!isNumber(value)) return '미정';
  if (kind === 'fee') return `월 ${formatNumber(value)}원(VAT 포함)`;
  if (kind === 'minutes') return `${formatNumber(value)}분`;
  if (kind === 'per_minute') return `1분당 ${formatUnitPrice(value)}원`;
  if (kind === 'monthly') return `월 ${formatNumber(value)}원`;
  if (value === 0) return '0원';
  if (kind === 'per_call') return `${formatUnitPrice(value)}원/건`;
  return `${formatUnitPrice(value)}원/${unitLabel(unitSec ?? DEFAULT_BILLING_UNIT_SEC, key)}`;
}

/**
 * 1분당 판매가의 10초 환산 표기(보조) — 금액은 단위 × 1분당 ÷ 6 으로 정확히 계산하고, 이 값은 읽기용이다.
 * @param {unknown} pricePerMinute
 * @returns {string} 예: 200 → '10초당 33.33원', 180 → '10초당 30원', 숫자가 아니면 ''
 */
export function tenSecondEquivalentText(pricePerMinute) {
  if (!isNumber(pricePerMinute)) return '';
  return `10초당 ${formatUnitPrice(round2(pricePerMinute / 6))}원`;
}

/**
 * 원가가 확인되지 않은 칸인가 — 원가 자리에 단위 초와 원가가 둘 다 null. 이런 칸은 판매가를 넣을 수 없다(서버 400).
 * 요금표 v70 의 판매가 칸은 원가가 모두 확인돼 있어 지금은 늘 false 다(원가 미확인 칸이던 상담사 경로는 판매가 칸이 없다).
 * @param {string} key
 * @param {{ unitSec?: number|null, unit_sec?: number|null, cost?: number|null }|null|undefined} costInfo
 * @returns {boolean}
 */
export function isCostUnknown(key, costInfo) {
  const cell = RATE_CELL_MAP[key];
  if (!cell || !['price', 'per_minute', 'per_call'].includes(cell.kind) || !costInfo) return false;
  const unitSec = costInfo.unitSec ?? costInfo.unit_sec ?? null;
  const cost = costInfo.cost ?? null;
  return unitSec === null && cost === null;
}

/**
 * 원가 표기(회색 참고값)
 * @param {string} key
 * @param {{ unitSec?: number|null, cost?: number|null }|null|undefined} costInfo readCellCost 결과
 * @returns {string} 예: '1분당 132원', '22원/10초', '20원/건', '20원/메시지', '7,000원/월', '확인 중', 원가 없는 칸은 '—'
 */
export function formatCostValue(key, costInfo) {
  const kind = RATE_CELL_MAP[key]?.kind;
  if (!kind || ['internal', 'fee', 'minutes', 'count', 'monthly_excl'].includes(kind)) return EMPTY;
  if (isCostUnknown(key, costInfo)) return '확인 중';
  const cost = num(costInfo?.cost);
  if (cost === null) return EMPTY;
  if (kind === 'monthly') return `${formatNumber(cost)}원/월`;
  if (kind === 'pass_through') return `${formatNumber(cost)}원/메시지`;
  if (kind === 'per_call') return `${formatUnitPrice(cost)}원/건`;
  if (kind === 'per_minute') return `1분당 ${formatUnitPrice(cost)}원`;
  if (cost === 0) return '0원';
  return `${formatUnitPrice(cost)}원/${unitLabel(costInfo?.unitSec ?? DEFAULT_BILLING_UNIT_SEC, key)}`;
}

/**
 * 판매가와 원가(같은 단위)의 관계 — 'below' 면 "원가 미만" 경고, 'equal' 이면 "마진 0" 보조 표기.
 * @param {unknown} price
 * @param {unknown} cost
 * @returns {'below'|'equal'|'above'|null}
 */
export function priceCostRelation(price, cost) {
  if (!isNumber(price) || !isNumber(cost)) return null;
  if (Math.abs(price - cost) < 1e-9) return 'equal';
  return price < cost ? 'below' : 'above';
}

const isBlank = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

/**
 * 판매가 입력 검증(`message.warning()` 문구). 0 이상의 숫자, 소수 둘째 자리까지.
 * @param {unknown} value
 * @param {{ required?: boolean, label?: string }} [opts]
 * @returns {string|null} 통과하면 null
 */
export function validatePrice(value, { required = false, label = '' } = {}) {
  if (isBlank(value)) return required ? `${label || '판매가'}은(는) 필수입니다.` : null;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  const prefix = label ? `${label}: ` : '';
  if (!Number.isFinite(n) || n < 0) return `${prefix}0 이상의 숫자를 넣어 주세요.`;
  if (!/^\d+(\.\d{1,2})?$/.test(String(n))) return `${prefix}소수 둘째 자리까지 넣을 수 있습니다.`;
  return null;
}

/**
 * 요율 칸 입력 검증 — 월 사용료·포함 통화는 0 이상 정수(서버 not_integer), 나머지는 validatePrice.
 * @param {string} key RATE_CELLS 의 key
 * @param {unknown} value
 * @param {{ required?: boolean }} [opts] 비울 수 없는 칸(기본 요율의 `base`·`inbound`·`outbound`)
 * @returns {string|null} 통과하면 null
 */
export function validateRateCell(key, value, { required = false } = {}) {
  const cell = RATE_CELL_MAP[key];
  const label = cell?.label ?? '';
  const error = validatePrice(value, { required, label });
  if (error || !cell?.integer || isBlank(value)) return error;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isInteger(n) ? null : `${label}: 0 이상의 정수로 넣어 주세요.`;
}

/**
 * 예외(`source: 'override'`)인 칸 key
 * @param {object|null|undefined} rates 상세 `rates`
 * @returns {string[]}
 */
export function overriddenCellKeys(rates) {
  if (!rates) return [];
  return RATE_CELLS.filter((c) => readCell(rates, c.key).source === 'override').map((c) => c.key);
}

/**
 * 적용 버전 한 줄
 * @param {{ default_version?: string|null, override_version?: string|null }|null|undefined} rates
 * @returns {string} 예: '기본 2026-09 · 예외 없음', '기본 2026-11 · 예외 2026-09'
 */
export function versionsLabel(rates) {
  if (!rates) return '기본 요율 없음';
  return `기본 ${rates.default_version || EMPTY} · 예외 ${rates.override_version || '없음'}`;
}

/** 덮은 칸 이름 — 구분 칸은 "내부·테스트"/"병원"으로 */
function overrideCellName(rates, key) {
  if (key === 'internal') return categoryLabel(readCell(rates, 'internal').value);
  return RATE_CELL_MAP[key]?.label ?? key;
}

/**
 * 상세가 내부·테스트 회사인가(`category` 또는 유효 요율 `internal`)
 * @param {object|null|undefined} detail
 * @returns {boolean}
 */
export function isInternalDetail(detail) {
  return detail?.category === 'internal' || readCell(detail?.rates, 'internal').value === true;
}

/**
 * 그 달 판매가가 미정이라 금액에서 뺀 회선 수(`LINE_RATE_UNSET` 의 count) — 요율 배지의 "미정 회선 n개".
 * 요금표 v70 에서 판매가 미정으로 금액을 빼는 것은 회선뿐이다.
 * @param {Array<object>|null|undefined} warnings
 * @returns {number}
 */
export function unsetLineCount(warnings) {
  return arr(warnings)
    .filter((w) => w?.code === 'LINE_RATE_UNSET')
    .reduce((sum, w) => sum + (num(w.count) ?? arr(w.details).length), 0);
}

/**
 * 상세 헤더의 요율 배지 — `기본` / `예외 n칸` / `내부·테스트`, 툴팁에 적용 버전과 덮은 칸,
 * 보조 표기 "미정 회선 n개".
 * @param {object|null|undefined} detail 상세 응답
 * @returns {{ label: string, tone: 'default'|'override'|'internal'|'danger', tooltip: string, overriddenKeys: string[], missingCount: number, missingLabel: string, legacy: boolean }}
 */
export function rateBadge(detail) {
  const rates = detail?.rates ?? null;
  const missingCount = unsetLineCount(detail?.warnings);
  const missingLabel = missingCount > 0 ? `미정 회선 ${formatNumber(missingCount)}개` : '';
  // v70 이전에 만든 정산서(`statement.legacy`, 개정 8) — 요율 스냅샷이 예전 모양이라 칸별로 읽지 않는다
  if (isLegacyStatementDetail(detail)) {
    return {
      label: 'v70 이전 요율',
      tone: 'default',
      tooltip: rates ? `${versionsLabel(rates)} · ${LEGACY_RATES_TEXT}` : LEGACY_RATES_TEXT,
      overriddenKeys: [],
      missingCount,
      missingLabel,
      legacy: true,
    };
  }
  if (!rates) {
    const legacyVersion = legacyPlanVersionOf(detail?.warnings);
    return {
      label: legacyVersion ? '기본 요율 v70 이전' : '기본 요율 없음',
      tone: 'danger',
      tooltip: legacyVersion
        ? `그 달에 적용되는 기본 요율 ${legacyVersion} 버전이 요금표 v70 이전 모양이라 금액을 계산하지 않았습니다. 기본 요율 화면에서 새 칸으로 다시 저장하세요.`
        : '그 달에 적용할 기본 요율 버전이 없어 금액을 계산하지 않았습니다.',
      overriddenKeys: [],
      missingCount,
      missingLabel,
      legacy: false,
    };
  }
  const overriddenKeys = overriddenCellKeys(rates);
  const internal = readCell(rates, 'internal').value === true;
  const versions = versionsLabel(rates);
  const names = overriddenKeys.map((key) => overrideCellName(rates, key));
  let label = RATE_SOURCE_LABELS.default;
  let tone = 'default';
  if (internal) {
    label = CATEGORY_LABELS.internal;
    tone = 'internal';
  } else if (overriddenKeys.length) {
    label = `예외 ${overriddenKeys.length}칸`;
    tone = 'override';
  }
  return {
    label,
    tone,
    tooltip: names.length ? `${versions}: ${names.join(', ')}` : versions,
    overriddenKeys,
    missingCount,
    missingLabel,
    legacy: false,
  };
}

/**
 * 그 달 M 에 적용되는 버전 — `effective_from` 이 M 이하인 것 중 가장 최근(요청 계약 3-3).
 * 기본 요율·회사 예외 모두 같은 규칙이다.
 * @template {{ effective_from: string }} T
 * @param {T[]|null|undefined} items
 * @param {string} month
 * @returns {T|null}
 */
export function effectiveVersion(items, month) {
  let best = null;
  for (const item of arr(items)) {
    if (!isValidMonth(item?.effective_from) || item.effective_from > month) continue;
    if (!best || item.effective_from > best.effective_from) best = item;
  }
  return best;
}

/** 그 달에 적용되는 기본 요율 버전(없으면 null — DEFAULT_PLAN_MISSING) */
export const effectiveDefaultPlan = effectiveVersion;
/** 그 달에 적용되는 회사 예외 버전(없으면 null) */
export const effectiveOverride = effectiveVersion;

/**
 * 이 달 사용량이 쓴 칸과 그 양 — 요율 카드의 "이 달 쓴 칸" 강조와 보조 표기. 청구 줄(`charge.lines`)이 있으면 그것을,
 * 없으면(기본 요율 없는 달) 사용량(`usage`)을 읽는다. 쓰지 않은 칸은 없다.
 * @param {object|null|undefined} detail 상세 응답
 * @returns {Record<string, string>} 칸 key → 보조 표기(예: '받은 통화 68분 30초', '발신 79건', '추가 4개', 상담사 번호
 *   'counselor_number.monthly' → '이 달 최대 5개 · 초과 2개')
 */
export function cellUsageNotes(detail) {
  const out = {};
  // v70 이전 정산서는 사용량·청구 줄이 예전 모양이다(개정 8) — 쓴 칸을 말하지 않는다
  if (isLegacyStatementDetail(detail)) return out;
  const lines = detail?.rates ? detail?.charge?.lines ?? null : null;
  const usage = detail?.usage ?? {};
  const inbound = lines?.inbound ?? null;
  const overage = num(inbound?.overage_units_10s);
  const used = num(inbound?.used_units_10s) ?? ((num(usage.inbound?.units_10s) ?? 0) + (num(usage.web_voice?.units_10s) ?? 0));
  // Minutes like the settle tab's gauge ('사용 117분') — '단위' is an internal unit
  if (used > 0) out['base.minutes'] = `사용 ${unitsMinutesText(used)}`;
  if (overage !== null && overage > 0) out['inbound.overage'] = `초과 ${unitsMinutesText(overage)}`;
  const dialed = num(lines?.outbound?.dialed) ?? num(usage.outbound?.dialed);
  if (dialed) out['outbound.per_call'] = `발신 ${formatNumber(dialed)}건`;
  const outUnits = num(lines?.outbound?.units_10s) ?? num(usage.outbound?.units_10s);
  if (outUnits) out['outbound.unit'] = `받은 통화 ${unitsMinutesText(outUnits)}`;
  const webCalls = num(lines?.web_text?.calls) ?? num(usage.web_text?.calls);
  if (webCalls) out.web_text = `웹 텍스트 ${formatNumber(webCalls)}건`;
  const base = lines?.base ?? null;
  if (base?.included_line) {
    out['base.monthly'] = base.opening_month
      ? `개통 달 ${formatNumber(base.days)}/${formatNumber(base.days_in_month)}일`
      : `포함 회선 ${maskNumber(base.included_line.number_last4)}`;
  }
  const items = lines ? arr(lines.line?.items) : arr(usage.lines);
  const extra070 = items.filter((i) => (i?.rate_key ?? (i?.type === 'uan' ? 'uan' : '070')) === '070' && i?.included !== true);
  const uan = items.filter((i) => (i?.rate_key ?? (i?.type === 'uan' ? 'uan' : '070')) === 'uan');
  // 기본 요율이 없는 달은 포함 회선을 모른다 — 070 회선 수를 그대로 센다
  if (extra070.length) out['line.070'] = lines ? `추가 ${formatNumber(extra070.length)}개` : `070 ${formatNumber(extra070.length)}개`;
  if (uan.length) out['line.uan'] = `${formatNumber(uan.length)}개`;
  // 상담사 번호 추가(번호 요청 A5) — 이 달 요금을 매겼고(applies) 포함 수를 넘은 날이 있을 때만. 값은 줄의 peak_count·peak_extra
  const counselor = lines?.counselor_number ?? null;
  if (counselor?.applies === true && (num(counselor.peak_extra) ?? 0) > 0 && isNumber(counselor.peak_count)) {
    out['counselor_number.monthly'] = `이 달 최대 ${formatNumber(counselor.peak_count)}개 · 초과 ${formatNumber(counselor.peak_extra)}개`;
  }
  return out;
}

/**
 * 쓰였는데 판매가가 없는 칸(경고색) — `LINE_RATE_UNSET` 의 details(요금표 v70 에서 판매가 미정으로 빠지는 칸은 회선뿐).
 * @param {Array<object>|null|undefined} warnings
 * @returns {string[]}
 */
export function missingPriceCellKeys(warnings) {
  const keys = new Set();
  for (const w of arr(warnings)) {
    if (w?.code !== 'LINE_RATE_UNSET') continue;
    for (const d of arr(w.details)) keys.add(d?.line_type === 'uan' ? 'line.uan' : 'line.070');
  }
  return RATE_CELLS.filter((c) => keys.has(c.key)).map((c) => c.key);
}

/**
 * 상담사 연결 원가 칸(읽기 전용 참고 — 판매가 없음, 월 사용료에 포함). 요율 본문·`rates`·`cost_rates` 의 `counselor`
 * 같은 자리에서 읽는다. PBX 는 세종 트렁크·STT 추정, vox 직접은 vox 가 매기는 전환·통신료다.
 */
export const COUNSELOR_COST_ROWS = deepFreeze([
  ...['extension', 'landline_070', 'mobile', 'uan'].map((t) => ({
    key: `pbx.${t}`,
    route: 'pbx',
    label: `PBX → ${NUMBER_TYPE_LABELS[t]}`,
    path: ['counselor', 'pbx', t],
  })),
  { key: 'pbx.stt', route: 'pbx', label: 'PBX 통화 전환료(STT)', path: ['counselor', 'pbx', 'stt'] },
  { key: 'vox.transfer', route: 'vox_direct', label: 'vox 직접 전환', path: ['counselor', 'vox_direct', 'transfer'] },
  ...['landline_070', 'mobile', 'uan'].map((t) => ({
    key: `vox.telecom.${t}`,
    route: 'vox_direct',
    label: `vox 직접 통신료 → ${NUMBER_TYPE_LABELS[t]}`,
    path: ['counselor', 'vox_direct', 'telecom', t],
  })),
]);

/**
 * 상담사 연결 원가 줄 — 요율에 실린 원가 스냅샷(`source.counselor`)을 먼저, 없으면 오늘 원가(`cost_rates.counselor`).
 * 단위·원가가 둘 다 없으면 "확인 중"(대표번호 — `pending` 이 있으면 그 사유).
 * @param {{ source?: object|null, costRates?: object|null }} [opts]
 * @returns {Array<{ key: string, label: string, route: string, cost: string, pending: string|null }>}
 */
export function counselorCostRows({ source = null, costRates = null } = {}) {
  return COUNSELOR_COST_ROWS.map((row) => {
    const snap = getPath(source, row.path);
    const live = getPath(costRates, row.path);
    const cell = snap && typeof snap === 'object' ? snap : live && typeof live === 'object' ? live : null;
    const unitSec = num(cell?.unit_sec);
    const cost = num(cell?.cost);
    const pending = typeof live?.pending === 'string' ? live.pending : null;
    let text = EMPTY;
    if (unitSec === null && cost === null) text = pending ? `확인 중(${pending})` : '확인 중';
    else if (cost === 0) text = '0원';
    else if (cost !== null) text = `${formatUnitPrice(cost)}원/${unitLabel(unitSec, row.key)}`;
    return { key: row.key, label: row.label, route: row.route, cost: text, pending };
  });
}

/**
 * 요율 본문·상세 `rates` → 편집 폼 값(칸 key → 값). null 은 미정, 구분·웹 텍스트는 boolean.
 * @param {object|null|undefined} planOrRates
 * @returns {Record<string, number|boolean|null>}
 */
export function planToFormValues(planOrRates) {
  return Object.fromEntries(RATE_CELLS.map((c) => [c.key, readCell(planOrRates, c.key).value]));
}

/**
 * 요금표 v70(2026-10-07) 의 기본 요율 칸 값 — 시드 버전과 같다(개정 1). v70 이전 모양 버전을 다시 저장할 때·버전이 없을 때
 * 편집 모달이 이 값으로 채워 연다(자동 변환은 하지 않는다 — 사람이 보고 저장한다). 요금표 v70 시드에는 상담사 번호 블록이
 * 없어 두 칸은 null(꺼짐)이다(번호 요청 A1).
 */
export const V70_DEFAULT_PLAN_VALUES = deepFreeze({
  internal: false,
  'base.monthly': 99000,
  'base.minutes': 120,
  'inbound.overage': 200,
  'outbound.per_call': 100,
  'outbound.unit': 50,
  web_text: true,
  'line.070': 9000,
  'line.uan': 25000,
  'counselor_number.included': null,
  'counselor_number.monthly': null,
});

const toNumberOrNull = (v) => {
  if (isBlank(v)) return null;
  const n = typeof v === 'number' ? v : Number(String(v).trim());
  return Number.isFinite(n) ? n : null;
};

const clone = (v) => (v === undefined ? undefined : JSON.parse(JSON.stringify(v)));

/** 부가 항목 시드(판매가 전부 미정) — 요금표 v70 개정 1 `extras`(발신 기본료·음성사서함 칸은 없어졌다) */
export const DEFAULT_PLAN_EXTRAS = deepFreeze({
  lettering: {
    office_100: { monthly: null },
    office_500: { monthly: null },
    office_2000: { monthly: null },
  },
  sms: {
    sms_out: { per_message: null },
    lms_out: { per_message: null },
    mms_out: { per_message: null },
    inbound: { per_message: null },
    inbound_service: { monthly_per_number: null },
  },
  privacy_protection: { unit_sec: 10, price: null },
});

/**
 * 부가 항목을 새 모양에 얹는다 — 시드 모양의 잎만 남기고, 값이 금액·null 이 아니면 시드 값이다. 예전 버전의
 * `outbound_base`·`voicemail_or_failed` 를 그대로 보내면 서버가 `unknown_key` 400 을 내서 빼고 보낸다.
 * @param {object|null|undefined} extras
 * @returns {object}
 */
export function planExtrasOf(extras, template = DEFAULT_PLAN_EXTRAS) {
  const out = {};
  for (const [k, tpl] of Object.entries(template)) {
    const v = extras && typeof extras === 'object' ? extras[k] : undefined;
    if (tpl && typeof tpl === 'object') out[k] = planExtrasOf(v, tpl);
    else out[k] = v === null || (isNumber(v) && v >= 0) ? v : clone(tpl);
  }
  return out;
}

/**
 * 기본 요율 본문의 상담사 번호 블록(번호 요청 A1·요청 계약 1) — 두 칸이 모두 비었으면 꺼짐(`null` — 그 버전부터 요금 없음),
 * 아니면 `{ included, monthly }`. 켜진 블록의 빈 칸은 null 그대로 둔다(서버가 400 으로 막는다 — 편집 모달이 저장 전에 막는다).
 * 꺼짐을 "두 칸 모두 null"로 두어 바뀐 칸 비교·저장하지 않은 입력 판정이 다른 칸과 같은 식으로 돈다.
 * @param {Record<string, unknown>|null|undefined} values
 * @returns {{ included: number|null, monthly: number|null }|null}
 */
export function counselorNumberBodyOf(values) {
  const included = toNumberOrNull(values?.['counselor_number.included']);
  const monthly = toNumberOrNull(values?.['counselor_number.monthly']);
  return included === null && monthly === null ? null : { included, monthly };
}

/**
 * 기본 요율 편집 폼 → `PUT .../default-plans/:effectiveFrom` 본문(요금표 v70 개정 1). 판매가 칸만 보낸다 — 단위·원가와
 * `ai`·`counselor` 블록은 서버가 cost_rates 로 채운다. 회선 `prorate` 는 빼면 직전 버전 값을 잇지만 화면은 늘 보낸다
 * (base 값, 없으면 true). 부가 항목은 새 모양에 얹어(planExtrasOf) 그대로 보낸다. 비운 회선 칸은 null(미정)이다.
 * 상담사 번호 블록도 늘 보낸다(counselorNumberBodyOf — 꺼지면 `"counselor_number": null`, 번호 요청 A1). 빼면 서버가 직전 버전
 * 블록을 물려받는데, 화면은 뜻을 분명히 하려고 보낸다.
 * @param {Record<string, unknown>} values planToFormValues 모양
 * @param {{ base?: object|null, memo?: string }} [opts] base: 복사해 연 버전(예전 모양일 수 있다 — 일할·부가 항목만 읽는다)
 * @returns {object}
 */
export function formValuesToDefaultPlanBody(values, { base = null, memo = '' } = {}) {
  const body = { internal: false, vat_mode: 'excluded' };
  for (const cell of RATE_CELLS) {
    if (cell.kind === 'internal' || cell.group === 'counselor_number') continue;
    const v = values?.[cell.key];
    if (cell.kind === 'pass_through') {
      const fallback = readCell(base, cell.key).value;
      setPath(body, cell.pricePath, typeof v === 'boolean' ? v : typeof fallback === 'boolean' ? fallback : true);
    } else {
      setPath(body, cell.pricePath, toNumberOrNull(v));
    }
  }
  for (const lineKey of ['070', 'uan']) {
    const prorate = getPath(base, ['line', lineKey, 'prorate']);
    body.line[lineKey].prorate = typeof prorate === 'boolean' ? prorate : true;
  }
  body.counselor_number = counselorNumberBodyOf(values);
  body.extras = planExtrasOf(base?.extras);
  body.rounding = 'floor_per_line';
  body.memo = typeof memo === 'string' ? memo.trim() : '';
  return body;
}

/**
 * 회사 예외 본문(개정 2) → 덮은 칸 key
 * @param {object|null|undefined} override
 * @returns {string[]}
 */
export function overrideBodyCellKeys(override) {
  if (!override) return [];
  return RATE_CELLS.filter((c) => hasPath(override, c.pricePath)).map((c) => c.key);
}

/**
 * 회사 예외 본문 → 예외 모달 값. 덮은 칸만 key 가 있다 — key 가 없는 칸은 "기본 따름".
 * @param {object|null|undefined} override
 * @returns {Record<string, number|boolean>}
 */
export function overrideToFormValues(override) {
  const values = {};
  for (const key of overrideBodyCellKeys(override)) {
    const raw = getPath(override, RATE_CELL_MAP[key].pricePath);
    if (raw !== null && raw !== undefined) values[key] = raw;
  }
  return values;
}

/**
 * 예외 모달 값 → `PUT .../plan-overrides/:effectiveFrom` 본문(개정 2). 값을 넣은 칸만 담는다.
 * 빈 값은 "기본 따름"이다 — 예외로 미정(null)을 만들 수 없다. 칸이 하나도 없으면 "이 달부터 예외 없음".
 * 예전 칸(`ai.price_per_unit`·`counselor.*.price`)은 담지 않는다 — 보내면 400 이다.
 * @param {Record<string, unknown>} values
 * @param {string} memo 예외 사유(필수 — 검증은 호출부)
 * @returns {object}
 */
export function formValuesToOverrideBody(values, memo) {
  const body = {};
  for (const cell of RATE_CELLS) {
    if (!values || !hasOwn(values, cell.key)) continue;
    const v = values[cell.key];
    if (BOOLEAN_KINDS.has(cell.kind)) {
      if (typeof v === 'boolean') setPath(body, cell.pricePath, v);
      continue;
    }
    const n = toNumberOrNull(v);
    if (n !== null) setPath(body, cell.pricePath, n);
  }
  body.memo = typeof memo === 'string' ? memo.trim() : '';
  return body;
}

/**
 * 예외 버전 한 줄 요약(이력 펼치기) — 덮은 칸과 값
 * @param {object|null|undefined} override
 * @param {object|null|undefined} [costRates] 웹 텍스트 메시지당 원가를 읽는다
 * @returns {Array<{ key: string, label: string, text: string }>}
 */
export function overrideCellSummaries(override, costRates = null) {
  return overrideBodyCellKeys(override).map((key) => {
    const cell = RATE_CELL_MAP[key];
    const raw = getPath(override, cell.pricePath);
    const text = formatRateValue(key, raw, null, { perMessage: num(costRates?.web_text_per_message) ?? 20 });
    return { key, label: cell.label, text };
  });
}

// ---- 요금표 v70 이전 모양(개정 1·2·8) ----

/** 요금표 v70 이전 모양 요율·정산서의 안내 */
export const LEGACY_RATES_TEXT =
  '요금표 v70 이전 모양의 요율입니다(AI 음성·상담사 연결 판매가). 칸 모양이 달라 칸별로 보이지 않습니다.';

/**
 * 요금표 v70 이전 모양으로 저장된 기본 요율 버전인가 — 서버가 버전마다 싣는 `legacy`(개정 1) 하나로만 가른다. 칸 모양을
 * 보고 짐작하지 않는다. 그런 버전은 저장된 모양 그대로 오고, 그 버전이 적용되는 달은 금액을 계산하지 않는다.
 * @param {object|null|undefined} plan `GET .../default-plans` 의 items[]
 * @returns {boolean}
 */
export function isLegacyPlan(plan) {
  return plan?.legacy === true;
}

/** 예전 칸 경로 → 이름(예전 예외 버전에 남은 칸·예전 수정 이력의 changed_cells) */
const LEGACY_CELL_LABELS = deepFreeze({
  'ai.price_per_unit': 'AI 음성 판매가',
  'counselor.pbx.extension.price': 'PBX → 내선 판매가',
  'counselor.pbx.landline_070.price': 'PBX → 유선·070 판매가',
  'counselor.pbx.mobile.price': 'PBX → 휴대폰 판매가',
  'counselor.pbx.uan.price': 'PBX → 대표번호 판매가',
  'counselor.pbx.stt.price': 'PBX 통화 전환료(STT) 판매가',
  'counselor.vox_direct.transfer.price': 'vox 직접 전환 판매가',
  'counselor.vox_direct.telecom.landline_070.price': 'vox 직접 통신료 → 유선·070 판매가',
  'counselor.vox_direct.telecom.mobile.price': 'vox 직접 통신료 → 휴대폰 판매가',
  'counselor.vox_direct.telecom.uan.price': 'vox 직접 통신료 → 대표번호 판매가',
  'extras.outbound_base.per_call': '발신 기본료(부가 항목)',
  'extras.voicemail_or_failed.per_call': '음성사서함·통화 실패(부가 항목)',
});

/**
 * 예전 칸 경로의 이름 — 모르는 경로면 null
 * @param {string} path
 * @returns {string|null}
 */
export function legacyCellLabel(path) {
  return hasOwn(LEGACY_CELL_LABELS, path) ? LEGACY_CELL_LABELS[path] : null;
}

/**
 * 예전 예외 버전에 남은 v70 이전 칸 — 계산에 쓰지 않고, 그 버전을 다시 저장하면 빠진다(개정 2).
 * @param {object|null|undefined} override
 * @returns {Array<{ path: string, label: string, value: unknown }>}
 */
export function legacyOverrideCells(override) {
  if (!override || typeof override !== 'object') return [];
  return Object.keys(LEGACY_CELL_LABELS)
    .filter((path) => !path.startsWith('extras.') && hasPath(override, path.split('.')))
    .map((path) => ({ path, label: LEGACY_CELL_LABELS[path], value: getPath(override, path.split('.')) }));
}

/**
 * 요금표 v70 이전 모양 기본 요율 버전의 요약(저장된 그대로 보인다 — 개정 1 `legacy: true`)
 * @param {object|null|undefined} plan
 * @returns {string[]} 예: ['AI 음성 33.33원/10초', '웹 텍스트 원가 그대로', '070 회선 월 7,000원', '대표번호 회선 미정', '상담사 연결 판매가 7칸']
 */
export function legacyPlanSummary(plan) {
  if (!plan || typeof plan !== 'object') return [];
  const out = [];
  const ai = num(getPath(plan, ['ai', 'price_per_unit']));
  if (ai !== null) out.push(`AI 음성 ${formatUnitPrice(ai)}원/10초`);
  const passThrough = getPath(plan, ['web_text', 'pass_through']);
  if (typeof passThrough === 'boolean') out.push(passThrough ? '웹 텍스트 원가 그대로' : '웹 텍스트 청구 안 함');
  for (const [k, name] of [
    ['070', '070 회선'],
    ['uan', '대표번호 회선'],
  ]) {
    const monthly = getPath(plan, ['line', k, 'monthly']);
    if (monthly !== undefined) out.push(`${name} ${isNumber(monthly) ? `월 ${formatNumber(monthly)}원` : '미정'}`);
  }
  const prices = COUNSELOR_COST_ROWS.map((row) => getPath(plan, [...row.path, 'price'])).filter((p) => p !== undefined);
  if (prices.length) {
    const set = prices.filter(isNumber).length;
    out.push(`상담사 연결 판매가 ${formatNumber(set)}칸${set < prices.length ? `(미정 ${formatNumber(prices.length - set)}칸)` : ''}`);
  }
  return out;
}

/**
 * 부가 항목(판매가 미정, 원가만 확인) — 읽기 전용 표시. 사용량 집계가 붙기 전이라 입력을 열지 않는다.
 * pricePath 는 요율 `extras` 안, costPath 는 `cost_rates.extras` 안. 발신 기본료·음성사서함은 요금표 v70 에서 발신 1건
 * 요금(`outbound.per_call`)이 대신해 없어졌다.
 */
export const EXTRA_ITEMS = deepFreeze([
  { key: 'lettering.office_100', label: '레터링 Office 100', unit: '월', pricePath: ['lettering', 'office_100', 'monthly'], costPath: ['lettering', 'office_100'] },
  { key: 'lettering.office_500', label: '레터링 Office 500', unit: '월', pricePath: ['lettering', 'office_500', 'monthly'], costPath: ['lettering', 'office_500'] },
  { key: 'lettering.office_2000', label: '레터링 Office 2000', unit: '월', pricePath: ['lettering', 'office_2000', 'monthly'], costPath: ['lettering', 'office_2000'] },
  { key: 'sms.sms_out', label: 'SMS 발신', unit: '건', pricePath: ['sms', 'sms_out', 'per_message'], costPath: ['sms', 'sms_out'] },
  { key: 'sms.lms_out', label: 'LMS 발신', unit: '건', pricePath: ['sms', 'lms_out', 'per_message'], costPath: ['sms', 'lms_out'] },
  { key: 'sms.mms_out', label: 'MMS 발신', unit: '건', pricePath: ['sms', 'mms_out', 'per_message'], costPath: ['sms', 'mms_out'] },
  { key: 'sms.inbound', label: '문자 수신', unit: '건', pricePath: ['sms', 'inbound', 'per_message'], costPath: ['sms', 'inbound'] },
  { key: 'sms.inbound_service', label: '문자 수신 부가서비스', unit: '번호/월', pricePath: ['sms', 'inbound_service', 'monthly_per_number'], costPath: ['sms', 'inbound_service_monthly_per_number'] },
  { key: 'privacy_protection', label: '개인정보 보호', unit: '10초', pricePath: ['privacy_protection', 'price'], costPath: ['privacy_protection'] },
]);

/**
 * 부가 항목 행 — 판매가(지금은 전부 "미정")와 원가
 * @param {object|null|undefined} planExtras 요율 `extras`
 * @param {object|null|undefined} costExtras `cost_rates.extras`
 * @returns {Array<{ key: string, label: string, price: string, cost: string, note: string }>}
 */
export function extraItemRows(planExtras, costExtras) {
  return EXTRA_ITEMS.map((item) => {
    const price = getPath(planExtras, item.pricePath);
    const rawCost = getPath(costExtras, item.costPath);
    const costValue = isNumber(rawCost) ? rawCost : num(rawCost?.cost);
    const unit = isNumber(rawCost?.unit_sec) ? `${rawCost.unit_sec}초` : item.unit;
    return {
      key: item.key,
      label: item.label,
      price: isNumber(price) ? `${formatUnitPrice(price)}원/${unit}` : '미정',
      cost: costValue === null ? EMPTY : `${formatUnitPrice(costValue)}원/${unit}`,
      note: typeof rawCost?.note === 'string' ? rawCost.note : '',
    };
  });
}

// ---------------------------------------------------------------------------
// 5. 사용량 목록 (화면 명세 1, 요청 계약 1)
// ---------------------------------------------------------------------------

/** 정렬 키 — 열 머리 순서 */
export const USAGE_SORT_KEYS = deepFreeze(['name', 'connect', 'calls', 'web', 'ai', 'handoff', 'talk']);
/** 기본 정렬: 통화(웹 제외) 내림차순 */
export const DEFAULT_USAGE_SORT = deepFreeze({ key: 'calls', dir: 'desc' });
/** 구분 칩 */
export const USAGE_CATS = deepFreeze(['all', 'hospital', 'internal']);

const CONNECT_MODE_ORDER = { pbx: 0, mixed: 1, vox_direct: 2, none: 3 };
const nonEmptyText = (v) => (typeof v === 'string' && v.trim() ? v : undefined);
const numOrUndef = (v) => (isNumber(v) ? v : undefined);

/** 정렬 키 → 비교 값(앞 값이 같으면 다음 값). undefined 는 방향과 상관없이 맨 뒤 */
const USAGE_SORT_ACCESSORS = {
  name: [(it) => nonEmptyText(it.company_name)],
  connect: [(it) => numOrUndef(CONNECT_MODE_ORDER[it.connect_mode?.mode])],
  calls: [(it) => numOrUndef(it.calls)],
  web: [(it) => numOrUndef(it.web)],
  ai: [(it) => numOrUndef(it.ai_seconds)],
  handoff: [(it) => numOrUndef(it.answered), (it) => numOrUndef(it.handoffs)],
  talk: [(it) => numOrUndef(it.counselor_talk_seconds)],
};

/**
 * @param {{ key?: string, dir?: string }|null|undefined} sort
 * @returns {{ key: string, dir: 'asc'|'desc' }}
 */
export function normalizeUsageSort(sort) {
  if (sort && USAGE_SORT_KEYS.includes(sort.key) && (sort.dir === 'asc' || sort.dir === 'desc')) {
    return { key: sort.key, dir: sort.dir };
  }
  return { ...DEFAULT_USAGE_SORT };
}

/**
 * 쿼리 `sort`('<key>.<asc|desc>') → 정렬. 없거나 모르는 값이면 통화 내림차순.
 * @param {unknown} str
 * @returns {{ key: string, dir: 'asc'|'desc' }}
 */
export function parseUsageSort(str) {
  const raw = Array.isArray(str) ? str[0] : str;
  const match = typeof raw === 'string' ? /^([a-z]+)\.(asc|desc)$/.exec(raw) : null;
  return normalizeUsageSort(match ? { key: match[1], dir: match[2] } : null);
}

/**
 * 정렬 → 쿼리 `sort` 값. 기본 정렬이면 undefined(쿼리에서 뺀다).
 * @param {{ key?: string, dir?: string }|null|undefined} sort
 * @returns {string|undefined}
 */
export function formatUsageSort(sort) {
  const s = normalizeUsageSort(sort);
  if (s.key === DEFAULT_USAGE_SORT.key && s.dir === DEFAULT_USAGE_SORT.dir) return undefined;
  return `${s.key}.${s.dir}`;
}

/**
 * 열 머리를 눌렀을 때의 다음 정렬 — 같은 열이면 방향만 뒤집고, 새 열은 이름만 오름차순·나머지 내림차순.
 * @param {{ key?: string, dir?: string }|null|undefined} sort
 * @param {string} key
 * @returns {{ key: string, dir: 'asc'|'desc' }}
 */
export function toggleUsageSort(sort, key) {
  const s = normalizeUsageSort(sort);
  if (s.key === key) return { key, dir: s.dir === 'asc' ? 'desc' : 'asc' };
  return normalizeUsageSort({ key, dir: key === 'name' || key === 'connect' ? 'asc' : 'desc' });
}

/**
 * 안정 정렬(같으면 원래 순서). 숫자가 아닌 값(미집계 `"…"`, null)은 방향과 상관없이 맨 뒤.
 * @template T
 * @param {T[]|null|undefined} items
 * @param {{ key?: string, dir?: string }|null|undefined} sort
 * @returns {T[]}
 */
export function sortUsageItems(items, sort) {
  const { key, dir } = normalizeUsageSort(sort);
  const accessors = USAGE_SORT_ACCESSORS[key];
  const sign = dir === 'asc' ? 1 : -1;
  const compare = (va, vb) => {
    if (va === undefined || vb === undefined) {
      if (va === vb) return 0;
      return va === undefined ? 1 : -1;
    }
    if (typeof va === 'string' || typeof vb === 'string') return sign * String(va).localeCompare(String(vb), 'ko');
    return sign * (va - vb);
  };
  return arr(items)
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      for (const get of accessors) {
        const c = compare(get(a.item), get(b.item));
        if (c) return c;
      }
      return a.index - b.index;
    })
    .map((x) => x.item);
}

/** 목록 구분 칩의 기본값 — 전체(2026-10-27 사용자 결정: 목업의 '병원' 기본값은 되돌림) */
export const DEFAULT_USAGE_CAT = 'all';

/**
 * @param {unknown} cat
 * @param {'all'|'hospital'|'internal'} [fallback] 없거나 모르는 값일 때 — 목록 화면은 기본값(전체)
 * @returns {'all'|'hospital'|'internal'}
 */
export function normalizeUsageCat(cat, fallback = DEFAULT_USAGE_CAT) {
  const value = Array.isArray(cat) ? cat[0] : cat;
  return USAGE_CATS.includes(value) ? value : fallback;
}

/**
 * 구분 칩과 병원 검색(회사명 부분 일치, 대소문자 무시)으로 거른다 — 화면에서 거른다.
 * @template {{ internal?: boolean, company_name?: string|null }} T
 * @param {T[]|null|undefined} items
 * @param {{ cat?: string, q?: string }} [filter]
 * @returns {T[]}
 */
export function filterUsageItems(items, { cat = 'all', q = '' } = {}) {
  // A bare call (no / unknown chip) filters nothing — the list screen always passes its normalized chip
  const category = normalizeUsageCat(cat, 'all');
  const needle = typeof q === 'string' ? q.trim().toLowerCase() : '';
  return arr(items).filter((it) => {
    if (category === 'hospital' && it.internal) return false;
    if (category === 'internal' && !it.internal) return false;
    return !needle || String(it.company_name ?? '').toLowerCase().includes(needle);
  });
}

/**
 * 병원 먼저, 내부·테스트는 그 아래 따로("내부·테스트" 구분 머리 줄 밑). 각 묶음 안 순서는 유지.
 * @template {{ internal?: boolean }} T
 * @param {T[]|null|undefined} items
 * @returns {{ hospitals: T[], internals: T[] }}
 */
export function groupUsageItems(items) {
  const hospitals = [];
  const internals = [];
  for (const it of arr(items)) (it.internal ? internals : hospitals).push(it);
  return { hospitals, internals };
}

/**
 * 구분 칩의 개수
 * @param {Array<{ internal?: boolean }>|null|undefined} items
 * @returns {{ all: number, hospital: number, internal: number }}
 */
export function usageCounts(items) {
  const list = arr(items);
  const internal = list.filter((it) => it.internal).length;
  return { all: list.length, hospital: list.length - internal, internal };
}

/**
 * 상담사 연결 방식 칸 — vox 직접이 들어간 행만 보조 표기 "vox 직접 n / 요청 m"(`connect_mode.vox_direct` / `handoffs`).
 * @param {object} item 요청 계약 1 `items[]`
 * @returns {{ label: string, sub: string }}
 */
export function connectModeCell(item) {
  const mode = item?.connect_mode?.mode;
  const hasVoxDirect = mode === 'vox_direct' || mode === 'mixed';
  return {
    label: connectModeLabel(mode),
    sub: hasVoxDirect
      ? `vox 직접 ${formatNumber(item.connect_mode?.vox_direct)} / 요청 ${formatNumber(item.handoffs)}`
      : '',
  };
}

/**
 * 통화(웹 제외) 칸의 보조 표기 — 0 이거나 모르는 항목은 뺀다.
 * @param {{ outbound?: unknown, canceled_before_ai?: unknown }|null|undefined} item
 * @returns {string} 예: '발신 8 · AI 전 끊김 19', 없으면 ''
 */
export function callsSubLabel(item) {
  const parts = [];
  if (isNumber(item?.outbound) && item.outbound > 0) parts.push(`발신 ${formatNumber(item.outbound)}`);
  if (isNumber(item?.canceled_before_ai) && item.canceled_before_ai > 0) {
    parts.push(`AI 전 끊김 ${formatNumber(item.canceled_before_ai)}`);
  }
  return parts.join(' · ');
}

/** 사용량 목록 웹 테스트 열·타일 — 마감 전 달의 보조 표기와 툴팁(회신 표 7) */
export const WEB_PENDING_HINT = '마감 뒤 확정';
export const WEB_PENDING_TOOLTIP =
  '웹 테스트 건수는 vox 에서 읽습니다(웹콜은 DB 에 없습니다). 그 달 마감이나 병원 상세 대사 탭의 [vox 건수 확인] 뒤에 채워져, 그 전에는 대개 0 입니다.';

/**
 * 목록의 웹 테스트 건수가 아직 vox 에서 채워지지 않았을 달인가 — 진행 중인 달과, 끝났지만 첫 자동 마감(다음 달
 * 마감일 06:00 KST) 전인 달. 목록 응답은 병원별 마감 상태를 싣지 않아 달로만 정한다.
 * @param {string} month
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export function isWebCountPending(month, nowMs = Date.now()) {
  if (!isValidMonth(month)) return false;
  return nowMs < autoCloseAtMs(month, { closeDay: DEFAULT_CLOSE_DAY, hour: DEFAULT_CLOSE_HOUR });
}

/**
 * 사용량 타일(병원 합계, 내부·테스트 제외 — `hospital_totals` 그대로)
 * @param {object|null|undefined} totals 요청 계약 1 `hospital_totals`
 * @param {{ webPending?: boolean }} [opts] webPending: 웹 테스트 건수가 아직 vox 에서 채워지지 않았을 달(isWebCountPending)
 * @returns {Array<{ key: string, label: string, value: string, sub: string, tip?: string }>}
 */
export function usageTotalsTiles(totals, { webPending = false } = {}) {
  const t = totals ?? {};
  const notFinal = isNumber(t.not_final) && t.not_final > 0 ? `미종료 ${formatNumber(t.not_final)}` : '';
  const pbx = t.connect_mode?.pbx;
  const vox = t.connect_mode?.vox_direct;
  return [
    { key: 'companies', label: '이용 병원 수', value: formatCount(t.companies, '곳'), sub: '' },
    { key: 'calls', label: '통화(웹 제외)', value: formatCount(t.calls, '통'), sub: callsSubLabel(t) },
    webPending
      ? { key: 'web', label: '웹 테스트', value: formatCount(t.web, '건'), sub: WEB_PENDING_HINT, tip: WEB_PENDING_TOOLTIP }
      : { key: 'web', label: '웹 테스트', value: formatCount(t.web, '건'), sub: '' },
    { key: 'ai', label: 'AI 응대 시간', value: formatDuration(t.ai_seconds), sub: notFinal },
    {
      key: 'handoff',
      label: '상담사 연결 응답 / 요청',
      value: `${formatNumber(t.answered)} / ${formatNumber(t.handoffs)}`,
      sub: isNumber(pbx) || isNumber(vox) ? `PBX ${formatNumber(pbx)} · vox 직접 ${formatNumber(vox)}` : '',
    },
    { key: 'talk', label: '상담사 통화 시간', value: formatDuration(t.counselor_talk_seconds), sub: '' },
  ];
}

// ---------------------------------------------------------------------------
// 6. 정산 요약 줄 (화면 명세 2 공통 헤더)
// ---------------------------------------------------------------------------

/**
 * 부가세 = 공급가액의 10%, 원 미만 절사
 * @param {unknown} total
 * @returns {number|null}
 */
export function vatOf(total) {
  return isNumber(total) ? Math.floor(total / 10) : null;
}

/**
 * 공급가액 줄 분해 — 툴팁용(청구 일곱 줄 — 요금표 v70 개정 4 + 상담사 번호 추가, 번호 요청 A5). 응답에 없는 줄은 0 으로 읽는다
 * @param {object|null|undefined} charge 상세 `charge`
 * @returns {string} 예: '월 사용료 90,000 + 120분 초과 통화 15,531 + 발신 28,450 + 웹 텍스트 2,460 + 회선 35,400 +
 *   상담사 번호 추가 0 + 일회성 0'
 */
export function chargeBreakdownText(charge) {
  const lines = charge?.lines;
  if (!lines) return '';
  return [
    ...STATEMENT_LINE_KEYS.map((k) => `${STATEMENT_LINE_LABELS[k]} ${formatNumber(num(lines[k]?.amount) ?? 0)}`),
    `일회성 ${formatNumber(num(lines.one_off?.amount) ?? 0)}`,
  ].join(' + ');
}

/**
 * @typedef {Object} SummaryLine
 * @property {{ amount: number|null, source: string|null, sourceLabel: string|null, partial: boolean, partialLines: string[], breakdown: string, manualOnly: boolean, legacy: boolean, lastConfirmed: { total: number, confirmedAt: number|null, confirmedBy: string|null, confirmedByName: string|null, label: string }|null }} supply
 *   공급가액(일회성 항목 포함). sourceLabel 확정/검토/추정, partial 이면 `일부 제외` 배지 + partialLines 툴팁.
 *   manualOnly: 기본 요율 없이 사람이 넣은 금액(줄 금액·일회성 항목)만 있는 정산 — 계산값이 없다.
 *   legacy: 요금표 v70 이전에 만든 정산서 — 줄이 예전 모양이라 공급가액은 정산서 `total`(그때 금액)이다
 * @property {number|null} vat 부가세(10%, 원 미만 절사)
 * @property {number|null} totalWithVat 공급가액 + 부가세(화면에서 더한다)
 * @property {{ total: number|null, voxCall: number|null, line: number|null, breakdown: string, preStart: number|null, fullTotal: number|null }} cost
 *   vox 원가(통화 + 회선). 시작 월(cost.billable·pre_start 둘 다 있음)이면 청구분(cost.billable) — 마진(청구 − cost.billable)과
 *   맞는다. preStart 는 그때만 시작 전 원가(우리 부담), fullTotal 은 늘 달 전체 cost.total
 * @property {boolean} notBillable 정산 대상 아님(정산 시작일 전 달) — 공급가액·부가세·합계·마진 null, supply.notBillableText '청구 없음'
 * @property {number|null} margin vox 원가 대비, 일회성 항목 제외
 * @property {number|null} marginInclPbxTrunk PBX 트렁크 추정까지 뺀 참고값
 * @property {ReturnType<typeof warningSummary>} warnings
 * @property {boolean} internal
 */

/**
 * 정산 요약 줄 — 공급가액 · 부가세 · 합계 · vox 원가 · 마진 · 경고 n건.
 * - 기본 요율이 없는 달(`rates` null, DEFAULT_PLAN_MISSING): 공급가액·부가세·합계·마진 null('—').
 *   다만 사람이 금액을 넣었으면(줄 금액·일회성 항목) call-api 가 `charge` 를 그 금액으로 싣는다(회신 표 16) —
 *   그때는 공급가액·부가세·합계를 보이고 마진만 '—' 다(판매가가 없어 vox 원가와 견줄 수 없다).
 * - 내부·테스트: 공급가액 0, 마진 null
 * - 재수정 중이면 직전 확정 금액(`statement.last_confirmed`)
 * - 요금표 v70 이전에 만든 정산서(isLegacyStatementDetail — `statement.legacy`): call-api 가 `charge`·`margin` 을 null 로
 *   싣는다(개정 8). 공급가액·부가세는 정산서 `total`·`vat_amount`(그때 저장한 금액)이고 줄 분해·마진은 '—' 다
 * @param {object|null|undefined} detail 상세 응답(요청 계약 2)
 * @returns {SummaryLine}
 */
export function summaryLine(detail) {
  const notBillable = isNotBillableDetail(detail);
  const charge = notBillable ? null : detail?.charge ?? null;
  const legacy = isLegacyStatementDetail(detail);
  // 판매가로 계산한 금액이 있다 — 마진·일부 제외는 이때만 뜻이 있다
  const priced = !!detail?.rates && !!charge && !legacy;
  const manualOnly = !detail?.rates && !!charge;
  const internal = isInternalDetail(detail);
  const warnings = arr(detail?.warnings);
  const statement = detail?.statement ?? null;

  let amount = !charge ? null : internal ? 0 : num(charge.total);
  let vat = amount === null ? null : internal ? 0 : num(charge.vat_amount) ?? vatOf(amount);
  if (legacy && !notBillable) {
    // `charge` 는 null 이지만(개정 8) 정산서에 그때 저장한 금액이 남아 있다
    amount = internal ? 0 : num(statement?.total);
    vat = amount === null ? null : internal ? 0 : num(statement?.vat_amount) ?? vatOf(amount);
  }
  const source = charge ? charge.source ?? null : null;
  // `일부 제외` — 판매가가 없어 뺀 줄이 아직 남았을 때만. call-api 는 사람이 그 줄 금액을 넣어도 마감 시점의
  // `charge.partial` 을 그대로 싣는다(회신 표 13·applyStatementLines) — 사람이 넣은 줄(정산 줄 overridden)의 뺀 항목은
  // 더는 뺀 것이 아니다. 요율 없이 사람이 넣은 금액뿐인 정산의 `partial` 은 "아무것도 계산하지 않았다"는 뜻이라 보이지 않는다.
  // 요금표 v70 에서 판매가 미정으로 빠지는 줄은 회선뿐이다
  const overridden = (key) => statement?.lines?.[key]?.overridden === true;
  const remaining = overridden('line') ? [] : excludedChargeLines(warnings, 'line');
  const partial = priced && !internal && !!charge.partial && (remaining.length > 0 || !hasExcludable(warnings));

  const last = statement?.last_confirmed;
  const lastConfirmed =
    last && !notBillable && statement.status !== 'confirmed' && isNumber(last.total)
      ? {
          total: last.total,
          confirmedAt: num(last.confirmed_at),
          confirmedBy: last.confirmed_by ?? null,
          confirmedByName: last.confirmed_by_name ?? null,
          label: `직전 확정 ${formatWon(last.total)}`,
        }
      : null;

  const cost = detail?.cost ?? null;
  // 시작 월이면 청구분 원가(cost.billable) — 서버 마진(청구 − cost.billable)과 같은 기준. 아니면 지금과 같은 cost
  const bc = billableCostOf(detail);
  const costBasis = bc.split ? cost.billable : cost;
  const margin = !priced || internal || notBillable ? null : num(detail?.margin?.amount);
  return {
    supply: {
      amount,
      source,
      sourceLabel: source ? chargeSourceLabel(source) : null,
      partial,
      partialLines: partial ? remaining : [],
      breakdown: charge && !legacy ? chargeBreakdownText(charge) : '',
      manualOnly,
      legacy,
      lastConfirmed,
      notBillableText: notBillable ? '청구 없음' : '',
    },
    vat,
    totalWithVat: amount === null || vat === null ? null : amount + vat,
    cost: {
      total: bc.total,
      voxCall: bc.voxCall,
      line: bc.line,
      breakdown: costBasis ? `통화 ${formatNumber(costBasis.vox_call)} + 회선 ${formatNumber(costBasis.line)}` : '',
      preStart: bc.preStart,
      fullTotal: bc.fullTotal,
    },
    margin,
    marginInclPbxTrunk: margin === null ? null : num(detail?.margin?.amount_incl_pbx_trunk),
    warnings: warningSummary(warnings),
    internal,
    notBillable,
  };
}

/**
 * 청구 기준 원가 — 시작 월(시작일이 1일이 아님)이면 서버가 `cost.billable`(시작 뒤)·`cost.pre_start`(시작 전)를 함께 싣는다.
 * 둘 다 객체일 때만 나눔으로 보고 billable 을 쓴다. 아니면(칸 없음 — 시작일 미설정·call-api 배포 전 포함) 지금과 같은 cost.
 * 달 전체 cost 는 vox 청구서 대조값이라 뜻을 바꾸지 않는다(fullTotal).
 * @param {object|null|undefined} detail 상세 응답
 * @returns {{ split: boolean, total: number|null, voxCall: number|null, line: number|null, preStart: number|null,
 *   fullTotal: number|null }}
 */
export function billableCostOf(detail) {
  const cost = detail?.cost && typeof detail.cost === 'object' ? detail.cost : null;
  const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
  const split = !!cost && isObj(cost.billable) && isObj(cost.pre_start);
  const basis = split ? cost.billable : cost;
  return {
    split,
    total: num(basis?.total),
    voxCall: num(basis?.vox_call),
    line: num(basis?.line),
    preStart: split ? num(cost.pre_start.total) : null,
    fullTotal: num(cost?.total),
  };
}

/**
 * 웹 사용량 출처(`usage.web_source`·건수 대사 `web_source`)의 대사 탭 표시. 'stored'(2026-11-02)는 vox 요약이 없거나
 * 덜 읽혀 저장한 웹 기록(웹훅 + 이전 vox 조회)으로 센 값이다 — 미러만 본 'mirror' 보다는 차 있지만 vox 로 확인하기 전이다.
 * @param {unknown} source
 * @returns {{ note: string, sub: string, tip: string }} note: DB 타일 보조, sub·tip: 분해 막대 웹 테스트 칸. 모르는 출처는 빈 문구
 */
export function webSourceView(source) {
  const tip = 'studio 에서 시험한 웹 통화';
  switch (source) {
    case 'vox':
      return { note: '웹은 vox 요약', sub: '', tip: `${tip}(vox 웹 요약)` };
    case 'stored':
      return {
        note: '웹 기록(웹훅) · vox 확인 전',
        sub: '웹 요약 미확정',
        tip: `${tip} — vox 웹 요약을 끝까지 읽기 전이라 저장한 웹 기록(웹훅)으로 셌습니다. 마감 때 vox 를 읽어 확정합니다`,
      };
    case 'mirror':
      // vox 웹 요약을 끝까지 읽지 못했고(안 읽었거나 일부만 읽음) 저장한 웹 기록도 없다
      return {
        note: '웹 요약 미확정',
        sub: '웹 요약 미확정',
        tip: `${tip} — vox 웹 요약을 끝까지 읽지 못해 비었거나 덜 찼을 수 있습니다(웹콜은 DB 에 없습니다)`,
      };
    default:
      return { note: '', sub: '', tip };
  }
}

/** 상세 `usage.web_source: 'mirror'` 의 표시(회신 표 5) */
export const WEB_SOURCE_MIRROR_LABEL = '웹 사용량 미확인';
/**
 * 웹콜이 DB 미러에서 빠지기 시작한 달(2026-09-07 부터 — 회신 "근거와 배경"). 그 전 달의 'mirror' 는 미러에 웹콜이
 * 다 있어 미확인이 아니다.
 */
export const WEB_UNMIRRORED_FROM_MONTH = '2026-09';

/**
 * 웹 테스트 사용량이 vox 웹 요약에서 오지 않았을 때의 안내 — 웹콜은 DB 미러에 없어(2026-09-07 부터) vox 웹 요약
 * (`usage.web_source: 'vox'`)이 없으면 웹 테스트가 비었거나 덜 찼을 수 있다. 끝난 달이면 서버가 WEB_USAGE_UNVERIFIED
 * 경고도 따로 낸다.
 * @param {object|null|undefined} detail 상세 응답
 * @returns {{ label: string, tooltip: string }|null} 'vox' 이거나 출처를 모르면 null
 */
export function webUsageNotice(detail) {
  if (detail?.usage?.web_source !== 'mirror') return null;
  const month = detail?.month;
  if (isValidMonth(month) && month < WEB_UNMIRRORED_FROM_MONTH) return null;
  return {
    label: WEB_SOURCE_MIRROR_LABEL,
    tooltip:
      '이 달 vox 웹 요약을 아직 끝까지 읽지 못해(안 읽었거나 일부만 읽음) 웹 테스트 사용량이 비었거나 덜 찼을 수 있습니다(웹콜은 DB 에 없습니다). 대사 탭 [vox 건수 확인]으로 다시 읽거나, 마감 때 vox 를 읽어 채웁니다.',
  };
}

/**
 * AI 응대 시간·10초 단위(`usage.ai`)와 같은 범위의 통화 수 — AI 가 받은 웹 제외 통화, 수신 + 발신. 10초 규칙의
 * 대상(`cost.unit_rule.target`)과 같은 모집단이다. `usage.calls.ai_answered` 는 발신을 뺀 수라(회신 B14) AI 시간·단위
 * 옆에 두면 범위가 어긋난다(recon-diag-06). 대상 수가 없는 응답이면 수신만 센 값을 그렇다고 밝혀 쓴다.
 * @param {object|null|undefined} detail 상세 응답
 * @returns {{ count: number|null, scope: '수신·발신'|'수신만' }}
 */
export function aiTimeCalls(detail) {
  const target = num(detail?.cost?.unit_rule?.target);
  if (target !== null) return { count: target, scope: '수신·발신' };
  return { count: num(detail?.usage?.calls?.ai_answered), scope: '수신만' };
}

/**
 * 10초 단위 수 → 분 표기 — 포함 통화를 분으로 정하므로(120분) 시간으로 올리지 않는다
 * @param {unknown} units
 * @returns {string} 예: 720 → '120분', 1615 → '269분 10초', 3 → '30초'
 */
export function unitsMinutesText(units) {
  if (!isNumber(units)) return EMPTY;
  const sec = Math.max(0, Math.round(units * 10));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (!m) return `${s}초`;
  return s ? `${formatNumber(m)}분 ${s}초` : `${formatNumber(m)}분`;
}

/**
 * 120분 초과 계산식 — 초과 시간 × 1분당 단가 = 금액. 금액은 서버 값(`charge.lines.inbound.overage_amount`, 원 단위
 * 소수 둘째 자리에서 버린 값)을 그대로 쓰고, 정확한 곱이 소수 둘째 자리에서 끝나지 않으면 '…' 를 붙여 '=' 가 틀리지 않게 한다.
 * @param {unknown} overageUnits 초과 10초 단위 수
 * @param {unknown} pricePerMinute 1분당 단가
 * @param {unknown} overageAmount 서버 금액(원 미만 버리기 전)
 * @returns {string} 예: '1,486분 20초 × 1분당 200원 = 297,266.66…원 → 원 미만 버림', 0 → '포함 통화 안 · 초과 없음'
 */
export function overageFormulaText(overageUnits, pricePerMinute, overageAmount) {
  if (!isNumber(overageUnits) || !isNumber(pricePerMinute)) return EMPTY;
  if (overageUnits === 0) return '포함 통화 안 · 초과 없음';
  const formula = `${unitsMinutesText(overageUnits)} × 1분당 ${formatUnitPrice(pricePerMinute)}원`;
  if (!isNumber(overageAmount)) return formula;
  if (Number.isInteger(overageAmount)) return `${formula} = ${formatWon(overageAmount)}`;
  // Exact product in centi-won: units × 10 s ÷ 60 × price × 100
  const centi = (overageUnits * pricePerMinute * 100) / 6;
  const exact = Math.abs(centi - Math.round(centi)) < 1e-6;
  return `${formula} = ${formatNumber(overageAmount)}${exact ? '' : '…'}원 → 원 미만 버림`;
}

/**
 * 발신 통화 수 — 걸기 전 취소·발신 실패(0원)가 있으면 발신 통화 수와 실제로 건 통화 수를 둘 다 보인다.
 * @param {{ calls?: unknown, dialed?: unknown }} counts calls: 발신 통화 수(`usage.outbound.calls`), dialed: 건 통화 수
 * @returns {{ total: string, perCall: string }} total: 발신 항목 사용량 머리, perCall: '발신 건당' 사용량
 *   예: { calls: 79, dialed: 78 } → { total: '발신 79통 · 건 통화 78건', perCall: '건 통화 78건 · 걸기 전 취소·발신 실패 1통(0원)' }
 */
export function outboundCountText({ calls = null, dialed = null } = {}) {
  const c = isNumber(calls) ? calls : null;
  const d = isNumber(dialed) ? dialed : null;
  if (c !== null && d !== null && c > d) {
    return {
      total: `발신 ${formatNumber(c)}통 · 건 통화 ${formatNumber(d)}건`,
      perCall: `건 통화 ${formatNumber(d)}건 · 걸기 전 취소·발신 실패 ${formatNumber(c - d)}통(0원)`,
    };
  }
  return { total: `발신 ${formatNumber(d ?? c)}건`, perCall: `발신 ${formatNumber(d)}건` };
}

/**
 * 회선 사용(`usage.lines[]`)과 청구 회선(`charge.lines.line.items[]`)을 짝짓는다 — 같은 자리·같은 번호를 먼저, 아니면
 * 아무 자리의 같은 번호. 짝이 없는 청구 회선도 뒤에 붙여 어느 회선도 빠지지 않게 한다(정산서·원가 표가 같은 회선을 그린다).
 * @param {unknown} used usage.lines
 * @param {unknown} items charge.lines.line.items
 * @returns {Array<{ u: object|null, item: object|null }>}
 */
export function pairLines(used, items) {
  const us = Array.isArray(used) ? used : [];
  const its = Array.isArray(items) ? items : [];
  const taken = new Set();
  const pairs = us.map((u, idx) => {
    let at = its[idx] && !taken.has(idx) && its[idx].number_last4 === u?.number_last4 ? idx : -1;
    if (at < 0) at = its.findIndex((it, j) => !taken.has(j) && it?.number_last4 === u?.number_last4);
    if (at >= 0) taken.add(at);
    return { u, item: at >= 0 ? its[at] : null };
  });
  its.forEach((item, j) => {
    if (!taken.has(j)) pairs.push({ u: null, item });
  });
  return pairs;
}

/**
 * 120분 사용 게이지(요금표 v70 개정 5) — (전화 단위 `usage.inbound.units_10s` + 웹 음성 단위 `usage.web_voice.units_10s`)
 * ÷ 포함 단위 `charge.lines.inbound.included_units_10s`. 포함 단위는 개통 달이면 일할해 올린 값이다. 기본 요율이 없는 달
 * (charge 없음·요율 없음)은 포함 단위를 몰라 사용량만 보인다(included null). v70 이전 정산서(`statement.legacy`)는 null.
 * @param {object|null|undefined} detail 상세 응답
 * @returns {null|{ phone: number, webVoice: number, used: number, included: number|null, overage: number|null,
 *   percent: number|null, over: boolean, opening: boolean, includedMinutes: number|null, pricePerMinute: number|null,
 *   overageAmount: number|null, amount: number|null, calls: number|null, aiSeconds: number|null, talkSeconds: number|null,
 *   unmatchedLegs: number|null, usedText: string, includedText: string, overageText: string }}
 *   사용량을 하나도 모르면 null
 */
export function inboundPoolView(detail) {
  // v70 이전 정산서는 `usage` 에 inbound·outbound 블록이 없고 청구 줄도 없다(개정 8) — 게이지를 그리지 않는다
  if (isLegacyStatementDetail(detail)) return null;
  const usage = detail?.usage ?? null;
  const line = detail?.rates ? detail?.charge?.lines?.inbound ?? null : null;
  const phone = num(usage?.inbound?.units_10s) ?? num(line?.phone_units_10s);
  const webVoice = num(usage?.web_voice?.units_10s) ?? num(line?.web_voice_units_10s) ?? 0;
  if (phone === null) return null;
  const used = phone + webVoice;
  const included = num(line?.included_units_10s);
  const overage = included === null ? null : num(line?.overage_units_10s) ?? Math.max(0, used - included);
  // Whole percent — the gauge is read at a glance (mockup '165%')
  const percent = included ? Math.round((used * 100) / included) : null;
  return {
    phone,
    webVoice,
    used,
    included,
    overage,
    percent,
    over: included !== null && used > included,
    opening: detail?.charge?.lines?.base?.opening_month === true && included !== null,
    includedMinutes: num(line?.included_minutes),
    pricePerMinute: num(line?.price_per_minute),
    overageAmount: num(line?.overage_amount),
    amount: num(line?.amount),
    calls: num(usage?.inbound?.calls),
    aiSeconds: num(usage?.inbound?.ai_seconds),
    talkSeconds: num(usage?.inbound?.counselor_talk_seconds),
    unmatchedLegs: num(usage?.inbound?.unmatched_legs),
    usedText: unitsMinutesText(used),
    includedText: included === null ? EMPTY : unitsMinutesText(included),
    overageText: overage === null ? EMPTY : unitsMinutesText(overage),
  };
}

// ---------------------------------------------------------------------------
// 7. 마감 폼·이력·일회성 항목 (화면 명세 2-4, 요청 계약 4-2·4-4)
// ---------------------------------------------------------------------------

/**
 * 정산 줄(요금표 v70 개정 4·6 + 상담사 번호 추가, 번호 요청 A5) — 월 사용료·120분 초과 통화·발신·웹 텍스트·회선·상담사 번호
 * 추가. 일회성 항목은 줄 금액이 아니라 따로 목록으로 다룬다(일회성까지 청구 일곱 줄). 예전 줄(AI 응대 `ai`·상담사 연결
 * `counselor`)은 없어졌다 — 저장 본문에 보내면 400 이다. 상담사 번호 줄의 키는 `counselor_number` 다(`counselor` 와 다르다).
 */
export const STATEMENT_LINE_KEYS = deepFreeze(['base', 'inbound', 'outbound', 'web_text', 'line', 'counselor_number']);
export const STATEMENT_LINE_LABELS = deepFreeze({
  base: '월 사용료',
  inbound: '120분 초과 통화',
  outbound: '발신',
  web_text: '웹 텍스트',
  line: '회선',
  counselor_number: '상담사 번호 추가',
  one_off: '일회성 항목',
});

/**
 * 응답에 줄 키가 없을 때의 금액 — 상담사 번호 추가 줄은 call-api 1-2 전 응답에 키가 없어 0원으로 읽는다(call-api 도 빠진 줄을
 * 0 으로 읽는다, 번호 요청 A5). 다른 줄은 늘 있어 모르면 null(빈 칸)이다.
 * @param {string} key
 * @returns {number|null}
 */
export function absentLineAmount(key) {
  return key === 'counselor_number' ? 0 : null;
}

/**
 * 정산 줄의 긴 이름 — 요율표 말 그대로 무엇을 받는 줄인지 붙인다. 월 사용료(VAT 포함 금액)와 포함 분은 그 달 요율에서
 * 읽고, 모르면(요율 없음·v70 이전 요율) 금액을 빼고 120분으로 적는다. 상담사 번호 추가는 그 달 요율 블록에서 포함 수와 1개당
 * 금액을 읽고, 블록이 없으면 짧은 이름만 쓴다.
 * @param {string} key STATEMENT_LINE_KEYS 또는 'one_off'
 * @param {object|null|undefined} rates 상세 `rates`
 * @returns {string} 예: '월 사용료(VAT 포함 99,000원)', '120분 초과 통화', '발신(건당 · 받은 통화)', '회선(추가 070 · 대표번호)',
 *   '상담사 번호 추가(기본 3개 초과 · 1개당 월 49,000원 · VAT 별도)'
 */
export function statementLineLabel(key, rates) {
  switch (key) {
    case 'base': {
      const fee = readCell(rates, 'base.monthly').value;
      return isNumber(fee) ? `월 사용료(VAT 포함 ${formatNumber(fee)}원)` : '월 사용료(VAT 포함)';
    }
    case 'inbound': {
      const minutes = readCell(rates, 'base.minutes').value;
      return `${formatNumber(isNumber(minutes) ? minutes : 120)}분 초과 통화`;
    }
    case 'outbound':
      return '발신(건당 · 받은 통화)';
    case 'line':
      return '회선(추가 070 · 대표번호)';
    case 'counselor_number': {
      const block = counselorNumberRates(rates);
      if (!block || !isNumber(block.included) || !isNumber(block.monthly)) return STATEMENT_LINE_LABELS.counselor_number;
      return `상담사 번호 추가(기본 ${formatNumber(block.included)}개 초과 · 1개당 월 ${formatNumber(block.monthly)}원 · VAT 별도)`;
    }
    default:
      return labelOf(STATEMENT_LINE_LABELS, key);
  }
}

/**
 * 검토 폼 줄 — 정산 `statement.lines` 의 계산값(`estimate`)·금액·수정 여부·메모.
 * `statement.lines` 가 없으면 `charge.lines` 의 금액을 계산값으로 쓴다. 둘 다 그 줄이 없으면 absentLineAmount(상담사 번호
 * 추가 줄은 0).
 * @param {object|null|undefined} detail
 * @returns {Array<{ key: 'base'|'inbound'|'outbound'|'web_text'|'line'|'counselor_number', label: string, estimate: number|null, amount: number|null, overridden: boolean, memo: string }>}
 */
export function statementLineRows(detail) {
  const stLines = detail?.statement?.lines;
  const chLines = detail?.charge?.lines;
  return STATEMENT_LINE_KEYS.map((key) => {
    const s = stLines?.[key];
    const estimate = num(s?.estimate) ?? num(chLines?.[key]?.amount) ?? (s || chLines?.[key] ? null : absentLineAmount(key));
    const amount = num(s?.amount) ?? estimate;
    return {
      key,
      label: statementLineLabel(key, detail?.rates),
      estimate,
      amount,
      overridden: s?.overridden === true,
      memo: typeof s?.memo === 'string' ? s.memo : '',
    };
  });
}

/** v70 이전 정산서 안내(개정 8) — 상세 헤더·정산 탭·요율 탭이 같이 쓴다 */
export const LEGACY_STATEMENT_TEXT =
  '요금표 v70 이전에 만든 정산서입니다. 항목이 예전 모양(AI 응대·상담사 연결)이라 새 항목 금액·마진을 보이지 않고, 금액은 그때 저장한 정산서 금액입니다. 다시 계산하기 전에는 저장·확정·일회성 항목 쓰기를 할 수 없습니다.';

/** 409 `STATEMENT_LEGACY_LINES`(개정 8) — 저장·확정·일회성 항목 쓰기를 v70 이전 정산서에 했을 때 */
export const STATEMENT_LEGACY_LINES_TEXT = 'v70 이전 정산서입니다. 다시 계산한 뒤 저장·확정하세요.';

/**
 * 요금표 v70 이전에 만든 정산서를 그린 상세인가(개정 8) — call-api 가 싣는 `statement.legacy` 하나로만 가른다. 요율·사용량
 * 모양을 보고 짐작하지 않는다(규칙 하나). 그런 정산서는 `charge`·`margin` 이 null 이고, `rates`·`usage` 는 저장된 예전 모양
 * (v70 칸·`usage.inbound`·`usage.outbound` 없음)이며, 공급가액은 정산서 `total`(그때 금액)이다. 그래서 v70 모양을 읽는
 * 곳(청구 표·요율 탭·120분 게이지·발신 사용량)은 모두 이 값으로 가린다. 저장·확정·일회성 항목 쓰기는 409
 * `STATEMENT_LEGACY_LINES` 다. 검토 필요면 [다시 계산], 확정이면 [재수정] → [다시 계산] → [확정]으로 새 줄이 된다.
 * @param {object|null|undefined} detail
 * @returns {boolean}
 */
export function isLegacyStatementDetail(detail) {
  return detail?.statement?.legacy === true;
}

/**
 * v70 이전 정산서에서 할 일 — 검토 필요는 [다시 계산], 확정은 [재수정] → [다시 계산] → [확정]
 * @param {string} status statementStatusOf(detail)
 * @returns {string}
 */
export function legacyStatementActionText(status) {
  return status === 'confirmed'
    ? '[재수정] → [다시 계산] → [확정]하면 새 항목(월 사용료·120분 초과 통화·발신·웹 텍스트·회선·상담사 번호 추가)이 됩니다.'
    : '[다시 계산]하면 새 항목(월 사용료·120분 초과 통화·발신·웹 텍스트·회선·상담사 번호 추가)이 됩니다.';
}

/**
 * 원가·판매가 표(2-3)의 줄 금액이 사람이 넣은 금액인가 — 검토 필요·확정 달의 `charge.lines.<줄>.amount` 는
 * 사람이 넣은 금액일 수 있다(요청 계약 2 `charge.source: review`, 4-2 `overridden`). 그런 줄은 계산 과정
 * ("→ 원 미만 버림")이나 "미정 제외" 배지로 금액을 설명하지 않는다.
 *  - 정산 줄(`statement.lines[key]`)이 `overridden` 을 실어 오면 그 값
 *  - 아니면 금액과 계산값이 둘 다 숫자이고 다를 때
 * @param {'base'|'inbound'|'outbound'|'web_text'|'line'|'counselor_number'} key
 * @param {{ amount?: unknown, computed?: unknown, statementLines?: object|null }} [args]
 *   amount: 표에 보이는 줄 금액(`charge.lines[key].amount`). computed: 표가 계산한 값(판매가가 있는 부분의 합,
 *   원 미만 버림). statementLines: 상세 `statement.lines`(없으면 null).
 * @returns {{ overridden: boolean, estimate: number|null, memo: string }}
 *   estimate: 계산값 — 정산 줄의 `estimate`, 없으면 computed. memo: 정산 줄 메모(앞뒤 공백 없이, 없으면 '')
 */
export function lineOverride(key, { amount = null, computed = null, statementLines = null } = {}) {
  const line = statementLines && typeof statementLines === 'object' ? statementLines[key] : null;
  const fromStatement = line && typeof line === 'object' ? line : null;
  const overridden =
    typeof fromStatement?.overridden === 'boolean'
      ? fromStatement.overridden
      : isNumber(amount) && isNumber(computed) && amount !== computed;
  return {
    overridden,
    estimate: num(fromStatement?.estimate) ?? num(computed),
    memo: typeof fromStatement?.memo === 'string' ? fromStatement.memo.trim() : '',
  };
}

/** 원가표 상담사 번호 추가 줄의 고정 문구(번호 요청 A7) */
export const COUNSELOR_LINE_NO_RATE_TEXT = '이 달 요율에 상담사 번호 요금 없음';
/**
 * 근거 없는 줄(`applies` 키가 없는 빈 줄 — 1-2 전에 만든 정산서 — 과 응답에 줄 키가 없을 때)의 문구. 금액이 0 이든 사람이 넣은
 * 금액이든 같은 문구이고, 금액과 '직접 입력' 배지는 다른 줄처럼 보인다(번호 요청 A7)
 */
export const COUNSELOR_LINE_NO_EVIDENCE_TEXT = '상담사 번호 요금 전에 만든 정산서 · 계산 근거 없음';
export const COUNSELOR_LINE_COST_TEXT = '원가 없음 · 금액 전부 마진';

/** 'YYYY-MM-DD' 의 날(1~31) — 모양이 아니면 null */
function dayOfMonth(day) {
  const m = typeof day === 'string' ? /^\d{4}-\d{2}-(\d{2})$/.exec(day) : null;
  return m ? Number(m[1]) : null;
}

/** 'YYYY-MM-DD' 두 날 → '18일~24일'(같은 날이면 '18일') */
function dayRangeText(from, to) {
  const a = dayOfMonth(from);
  const b = dayOfMonth(to);
  if (a === null || b === null) return '';
  return a === b ? `${a}일` : `${a}일~${b}일`;
}

/**
 * 원가표(2-3)의 상담사 번호 추가 줄 — 줄 자료(`charge.lines.counselor_number`, call-api 1-2)를 근거 문장으로 바꾼다(번호 요청 A7).
 * 금액은 서버 값만 쓰고 화면에서 다시 계산하지 않는다. 구간마다 금액은 두지 않는다(줄은 그 달 합에 한 번만 버린다).
 *  - 'unpriced': `charge` 가 없다(기본 요율 없음)·v70 이전 정산서 — 줄 자료가 없다
 *  - 'absent': `charge` 는 있는데 줄 키가 없다(call-api 1-2 전 응답) — 0원으로 읽고 '상담사 번호 요금 전에 만든 정산서 · 계산
 *    근거 없음'(COUNSELOR_LINE_NO_EVIDENCE_TEXT)
 *  - 'shell': 줄은 있지만 `applies` 키가 없다 — 1-2 전에 만든 검토 필요·확정 정산서(검토 필요는 다시 계산하기 전까지)나 기본
 *    요율 없이 사람이 금액을 넣은 정산서(call-api 계약 C7). 근거(창·구간·빈 날·계산식)를 그리지 않고 줄 `amount`(보통 0, 사람이
 *    넣었으면 그 값)와 같은 문구(COUNSELOR_LINE_NO_EVIDENCE_TEXT)만 보인다 — 금액이 0 이든 사람이 넣은 금액이든 같다
 *  - 'no_rate': 그 달 요율에 블록이 없거나 1개당 금액이 0(예외 면제 포함) — 0원과 '이 달 요율에 상담사 번호 요금 없음'만
 *  - 'no_calls': 웹이 아닌 통화가 0건이라 0원 — 근거(구간·가장 많았던 날)는 그대로 있고 '통화가 없는 달이라 0원(계산했다면 n원)'.
 *    n 은 COUNSELOR_FEE_NO_CALLS details 의 계산했을 금액이고, 그 경고가 없으면(초과가 없던 달) 괄호를 뺀다
 *  - 'applies': 요금을 매겼다 — 구간별 개수·초과, 계산식 한 번, 빈 날(COUNSELOR_COUNT_GAP details), 가장 많았던 날의 번호, 개통 달
 *    청구 창
 * 줄 배지(warning)는 `warnings[]` 에서 고른다 — call-api 줄 `warning` 은 계산할 때 굳어 검토 쓰기가 다시 판정한 중복 의심과
 * 어긋날 수 있다(call-api 열린 항목 — 중복 의심 배지는 `warnings[]` 를 따른다). 순서는 COUNSELOR_LINE_WARNING_CODES.
 * @param {object|null|undefined} detail 상세 응답
 * @returns {{ state: 'unpriced'|'absent'|'shell'|'no_rate'|'no_calls'|'applies', amount: number|null, estimate: number|null,
 *   unitText: string, rateSource: 'default'|'override'|null, segmentsText: string, formulaText: string, windowText: string,
 *   gapText: string, peakText: string, noteText: string, warning: string|null }}
 *   segmentsText 예: '1일~17일 3개(초과 없음) · 18일~24일 4개(초과 1) · 25일~31일 5개(초과 2)'
 *   formulaText 예: '초과 1개 × 7일 + 2개 × 7일 = 21 ÷ 31일 × 49,000원 = 33,193원'
 */
export function counselorNumberLineView(detail) {
  const warnings = arr(detail?.warnings);
  const warningOf = (code) => warnings.find((w) => w?.code === code) ?? null;
  const view = {
    state: 'unpriced',
    amount: null,
    estimate: null,
    unitText: '',
    rateSource: null,
    segmentsText: '',
    formulaText: '',
    windowText: '',
    gapText: '',
    peakText: '',
    noteText: '',
    warning: COUNSELOR_LINE_WARNING_CODES.find((code) => warningOf(code)) ?? null,
  };
  const charge = detail?.charge ?? null;
  if (!charge || isLegacyStatementDetail(detail)) return view;
  const line = charge.lines && typeof charge.lines === 'object' ? charge.lines.counselor_number : null;
  if (!line || typeof line !== 'object') {
    return { ...view, state: 'absent', amount: 0, estimate: 0, noteText: COUNSELOR_LINE_NO_EVIDENCE_TEXT };
  }
  const amount = num(line.amount) ?? 0;
  const estimate = num(line.estimate) ?? amount;
  if (!hasOwn(line, 'applies')) {
    return { ...view, state: 'shell', amount, estimate, noteText: COUNSELOR_LINE_NO_EVIDENCE_TEXT };
  }
  let state = 'no_rate';
  if (line.applies === true) state = 'applies';
  else if (line.reason === 'no_calls') state = 'no_calls';
  if (state === 'no_rate') return { ...view, state, amount, estimate, noteText: COUNSELOR_LINE_NO_RATE_TEXT };

  const included = num(line.included);
  const monthly = num(line.monthly);
  const dim = num(line.days_in_month);
  const segments = arr(line.segments).filter((s) => dayRangeText(s?.from, s?.to) && isNumber(s?.count));
  const segmentsText = segments
    .map((s) => {
      const extra = num(s.extra) ?? 0;
      return `${dayRangeText(s.from, s.to)} ${formatNumber(s.count)}개(${extra > 0 ? `초과 ${formatNumber(extra)}` : '초과 없음'})`;
    })
    .join(' · ');

  const end = num(line.extra_number_days) ?? 0;
  let formulaText = '';
  if (state === 'applies' && end > 0 && isNumber(monthly) && dim) {
    const terms = segments
      .filter((s) => (num(s.extra) ?? 0) > 0 && isNumber(s.days))
      .map((s) => `${formatNumber(s.extra)}개 × ${formatNumber(s.days)}일`);
    const left = terms.length ? `초과 ${terms.join(' + ')} = ` : '';
    formulaText = `${left}${formatNumber(end)} ÷ ${formatNumber(dim)}일 × ${formatNumber(monthly)}원 = ${formatWon(estimate)}`;
  }

  // 개통 달이면 청구 창(포함 070 회선이 켜진 날부터 그 달 끝). 그 밖의 달은 그 달 전체다
  const win = line.window && typeof line.window === 'object' ? line.window : null;
  const windowText =
    win && isNumber(win.days) && dim && win.days < dim && dayRangeText(win.from, win.to)
      ? `${dayRangeText(win.from, win.to)}(${formatNumber(win.days)}일)`
      : '';

  let gapText = '';
  const gap = state === 'applies' ? warningOf('COUNSELOR_COUNT_GAP') : null;
  if (gap) {
    const { days, refs } = counselorGapParts(gap.details);
    if (days.length) gapText = `${days.join(', ')}로 계산${refs.length ? ` · 앞뒤 기록(참고값): ${refs.join(', ')}` : ''}`;
  }

  let peakText = '';
  const peakDay = shortDayLabel(line.peak_day);
  if (peakDay && isNumber(line.peak_count)) {
    const numbers = arr(line.peak_numbers)
      .map((n) => {
        const name = typeof n?.name === 'string' ? n.name.trim() : '';
        const tail = n?.number_tail ? maskNumber(n.number_tail) : '';
        return [name, tail === EMPTY ? '' : tail].filter(Boolean).join(' ');
      })
      .filter(Boolean);
    const parts = [`${peakDay} ${formatNumber(line.peak_count)}개`];
    if (numbers.length) parts.push(numbers.join(', '));
    if (line.peak_numbers_truncated === true && numbers.length) parts.push(`기록에 처음 ${formatNumber(numbers.length)}개만 남음`);
    peakText = parts.join(' · ');
  }

  let noteText = '';
  if (state === 'no_calls') {
    const wouldBe = num(detailsObject(warningOf('COUNSELOR_FEE_NO_CALLS'))?.amount);
    noteText = wouldBe === null ? '통화가 없는 달이라 0원' : `통화가 없는 달이라 0원(계산했다면 ${formatWon(wouldBe)})`;
  }

  return {
    ...view,
    state,
    amount,
    estimate,
    unitText:
      isNumber(monthly) && isNumber(included)
        ? `${formatRateValue('counselor_number.monthly', monthly)} · 기본 ${formatNumber(included)}개`
        : '',
    rateSource: line.rate_source === 'override' || line.rate_source === 'default' ? line.rate_source : null,
    segmentsText,
    formulaText,
    windowText,
    gapText,
    peakText,
    noteText,
  };
}

/**
 * 이 달 일회성 항목 합
 * @param {object|null|undefined} detail
 * @returns {number}
 */
export function oneOffAmountOf(detail) {
  return (
    num(detail?.statement?.lines?.one_off?.amount) ?? num(detail?.charge?.lines?.one_off?.amount) ?? 0
  );
}

/**
 * 검토 폼 합계 — 공급가액(정산 줄 여섯 + 일회성), 부가세(10%, 원 미만 절사), 부가세 포함. 내부·테스트는 0.
 * @param {{ base?: unknown, inbound?: unknown, outbound?: unknown, web_text?: unknown, line?: unknown, counselor_number?: unknown, oneOff?: unknown }} amounts
 * @param {{ internal?: boolean }} [opts]
 * @returns {{ total: number, vat: number, totalWithVat: number }}
 */
export function statementTotals(amounts = {}, { internal = false } = {}) {
  if (internal) return { total: 0, vat: 0, totalWithVat: 0 };
  const parts = [...STATEMENT_LINE_KEYS.map((k) => amounts?.[k]), amounts?.oneOff];
  const total = parts.reduce((sum, v) => sum + (num(v) ?? 0), 0);
  const vat = vatOf(total);
  return { total, vat, totalWithVat: total + vat };
}

/**
 * 마진 = 서티 청구(일회성 항목 제외) − vox 원가. 내부·테스트와 금액이 없으면 null.
 * @param {{ total?: unknown, oneOff?: unknown, costTotal?: unknown, internal?: boolean }} args
 * @returns {number|null}
 */
export function marginOf({ total, oneOff = 0, costTotal, internal = false } = {}) {
  if (internal || !isNumber(total) || !isNumber(costTotal)) return null;
  return total - (num(oneOff) ?? 0) - costTotal;
}

/**
 * 아직 확인하지 않은 차단 경고 코드 — 비어야 `[이 달 확정]` 이 켜진다.
 * @param {Array<object>|null|undefined} warnings
 * @param {string[]|Set<string>|null|undefined} acknowledged 체크한 코드
 * @returns {string[]}
 */
export function unacknowledgedBlocking(warnings, acknowledged) {
  const done = new Set(acknowledged ?? []);
  return splitWarnings(warnings)
    .blocking.map((w) => w.code)
    .filter((code) => !done.has(code));
}

/**
 * 차단 경고 한 건의 내용 표식 — 건수와 분해(details). 같은 코드라도 이 값이 바뀐 경고는 다시 확인해야 한다: 정산 탭
 * (StatementPanel)은 다시 불러와 바뀐 경고의 확인을 지우고, 검토 폼은 저장 응답의 경고와 견준다(changedBlockingWarnings).
 * @param {{ count?: unknown, details?: unknown }|null|undefined} warning
 * @returns {string}
 */
export function blockingWarningSignature(warning) {
  return `${warning?.count ?? ''}|${JSON.stringify(warning?.details ?? null)}`;
}

/**
 * 저장 응답의 차단 경고 가운데 저장 전에 보던 것과 다른 것 — 새로 생겼거나 건수·분해가 바뀐 코드(같은 코드끼리
 * blockingWarningSignature 로 견준다). 저장은 상담사 번호 요금 중복 의심을 다시 판정하고 그 분해에 줄 금액이 들어(번호 요청
 * 요청 계약 5, call-api C9) 확인한 경고가 저장으로 바뀔 수 있다 — 하나라도 있으면 검토 폼은 확정하지 않고 다시 불러와 다시
 * 확인받는다. 사라진 경고는 넣지 않는다(확인할 것이 줄었을 뿐이다). 저장 응답에 경고 칸이 없으면(배열이 아님) 견줄 수 없어
 * 빈 배열이다.
 * @param {Array<object>|null|undefined} before 저장 전에 보던 경고(상세 `warnings`)
 * @param {Array<object>|null|undefined} after 저장 응답의 `warnings`
 * @returns {string[]} 바뀐 차단 경고 코드(after 차례)
 */
export function changedBlockingWarnings(before, after) {
  if (!Array.isArray(after)) return [];
  const seen = new Map(splitWarnings(before).blocking.map((w) => [w.code, blockingWarningSignature(w)]));
  return splitWarnings(after)
    .blocking.filter((w) => seen.get(w.code) !== blockingWarningSignature(w))
    .map((w) => w.code);
}

/**
 * 마감 이력 행(최근 것부터). 동작은 자동 확정 / 자동 검토 필요 / 자동 다시 계산 / 다시 계산 / 저장 /
 * 일회성 항목 / 확정 / 재수정. 이력이 없는 응답이면 빈 배열(화면은 이력을 숨긴다).
 * @param {object|null|undefined} statement
 * @returns {Array<{ key: string, at: string, by: string, action: string, change: string, memo: string }>}
 */
export function revisionRows(statement) {
  // 이력은 일어난 순서로 쌓인다 — 시각이 같으면 뒤에 쌓인 것이 더 최근이다
  return arr(statement?.revisions)
    .map((r, index) => ({ r, index }))
    .sort((a, b) => (num(b.r?.at) ?? 0) - (num(a.r?.at) ?? 0) || b.index - a.index)
    .map(({ r, index }) => ({
      key: `${num(r?.at) ?? 'x'}-${index}`,
      at: formatKstDateTime(r?.at),
      by: actorLabel(r?.by, r?.by_name),
      action: labelOf(REVISION_ACTION_LABELS, r?.action),
      change: `${formatWon(r?.total_before)} → ${formatWon(r?.total_after)}`,
      memo: typeof r?.memo === 'string' ? r.memo : '',
    }));
}

/** 일회성 항목을 추가·수정·삭제할 수 있는 마감 상태 */
export const ONE_OFF_EDITABLE_STATUSES = deepFreeze(['estimating', 'review', 'failed']);
/** 확정·마감 중인 달의 비활성 버튼 툴팁 */
export const ONE_OFF_LOCKED_TOOLTIP = '재수정 뒤 추가';
/** 내부·테스트 회사의 일회성 항목 안내 — 일회성 항목 목록과 추가·수정 모달이 같이 쓴다 */
export const ONE_OFF_INTERNAL_NOTE =
  '내부·테스트 회사는 청구가 0원이라 일회성 항목도 금액에 넣지 않습니다(기록만 남습니다).';

/**
 * @param {string|null|undefined} status statementStatusOf(detail)
 * @returns {boolean}
 */
export function canEditOneOff(status) {
  return ONE_OFF_EDITABLE_STATUSES.includes(status ?? 'estimating');
}

// ---------------------------------------------------------------------------
// 8. 자동 마감 문구와 확인창 문구 (화면 명세 2-4·3, 요청 계약 4-1)
// ---------------------------------------------------------------------------

/** 마감일 기본값(`BILLING_AUTO_CLOSE_DAY`)과 실행 시각(`BILLING_AUTO_CLOSE_HOUR`, KST) — 응답에 값이 없을 때만 쓴다 */
export const DEFAULT_CLOSE_DAY = 3;
export const DEFAULT_CLOSE_HOUR = 6;
/** 자동 마감이 꺼진 환경의 안내(화면 명세 3) */
export const AUTO_CLOSE_DISABLED_TEXT = '자동 마감 꺼짐 — 마감은 [지금 마감 실행]으로만 돈다';
/** 마감 실패에 자동 재시도가 남지 않았을 때(재시도 기간이 끝났다) — 이 병원만 다시 돌리는 버튼으로 보낸다 */
export const NO_AUTO_RETRY_TEXT =
  '자동으로 다시 돌지 않습니다(자동 재시도 기간이 끝났습니다). [지금 다시 실행]으로 다시 실행해 주세요.';
/** 마감 실패인데 자동 마감이 꺼져 있을 때 */
export const NO_AUTO_RETRY_DISABLED_TEXT =
  '자동 마감이 꺼져 있어 자동으로 다시 돌지 않습니다. [지금 다시 실행]으로 다시 실행해 주세요.';
/** 마감 실패인데 자동 마감 대상이 아닌 달(2026-09 이전 — 서버 `scheduled_at: null`)일 때 */
export const NO_AUTO_RETRY_NOT_TARGET_TEXT =
  '자동 마감 대상이 아닌 달이라 자동으로 다시 돌지 않습니다. [지금 다시 실행]으로 다시 실행해 주세요.';
/** 503 BILLING_INDEXES_NOT_READY — [지금 마감 실행]·[지금 다시 실행](회신 표 11). 서버 문구 대신 운영자용 고정 문구 */
export const BILLING_INDEXES_NOT_READY_TEXT =
  '마감 준비(정산 인덱스 확인)가 아직 끝나지 않았습니다. 잠시 뒤 다시 실행해 주세요. 그동안 자동 마감도 건너뜁니다.';

/** `auto_close.close_hour`(KST 시) — 없거나 이상하면 기본 06시 */
function closeHourOf(autoClose) {
  const hour = num(autoClose?.close_hour);
  return hour !== null && Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : DEFAULT_CLOSE_HOUR;
}

/**
 * 마감 현황 `counts` 한 줄(없으면 `last_run` 으로)
 * @param {object|null|undefined} counts 요청 계약 4-1 `counts`
 * @param {object|null|undefined} [lastRun] `auto_close.last_run`
 * @returns {string} 예: '확정 11(자동 11 · 사람 0) · 검토 필요 2 · 실패 0'
 */
export function closeCountsText(counts, lastRun = null) {
  if (counts) {
    const system = num(counts.confirmed_system) ?? 0;
    const human = num(counts.confirmed_human) ?? 0;
    const parts = [
      `확정 ${formatNumber(system + human)}(자동 ${formatNumber(system)} · 사람 ${formatNumber(human)})`,
      `검토 필요 ${formatNumber(num(counts.review) ?? 0)}`,
      `실패 ${formatNumber(num(counts.failed) ?? 0)}`,
    ];
    if (num(counts.closing)) parts.push(`마감 중 ${formatNumber(counts.closing)}`);
    if (num(counts.estimating)) parts.push(`추정 중 ${formatNumber(counts.estimating)}`);
    return parts.join(' · ');
  }
  if (lastRun) {
    return [
      `확정 ${formatNumber(num(lastRun.confirmed) ?? 0)}`,
      `검토 필요 ${formatNumber(num(lastRun.review) ?? 0)}`,
      `실패 ${formatNumber(num(lastRun.failed) ?? 0)}`,
    ].join(' · ');
  }
  return '';
}

/**
 * 그 달을 집는 다음 자동 실행 시각. 자동 마감 잡은 대상 달의 다음 달 마감일 `close_hour`시(KST, 기본 06시)부터
 * 그 다음 달 말일까지 매일 돈다(요청 계약 4-1) — 그 기간에 남은 실행이 없으면 null. 꺼짐 여부는 호출부가 본다.
 * 서버가 `scheduled_at`·`next_retry_at` 을 주면 호출부는 그 값을 먼저 쓴다(회신 표 9) — 이것은 값이 없을 때의 대신이다.
 * @param {string} month 대상 달
 * @param {number} nowMs
 * @param {{ close_day?: number, close_hour?: number }|null} [autoClose]
 * @returns {number|null} unix ms
 */
function nextAutoRunInWindow(month, nowMs, autoClose = null) {
  if (!isValidMonth(month)) return null;
  const closeDay = num(autoClose?.close_day) ?? DEFAULT_CLOSE_DAY;
  const hour = closeHourOf(autoClose);
  const firstRun = autoCloseAtMs(month, { closeDay, hour });
  const windowEnd = monthEndMsKst(addMonths(month, 1));
  const next = Math.max(nextKstHourMs(nowMs, hour), firstRun);
  return next < windowEnd ? next : null;
}

/**
 * 마감 실행 기록(`auto_close.last_run`)이 어떤 실행인가 — 한 줄 안내 앞머리와 툴팁이 같이 쓴다(회신 표 8).
 *  - 자동(trigger 'auto', 없으면 자동으로 본다) / 수동(trigger 'manual' 이거나 이 화면에서 시작한 실행)
 *  - 수동 실행이 회사 하나만 돌았으면(`company_id`) "병원 1곳"
 *  - 실행 전체가 중간에 멈췄으면(`error`) "중단된 실행"
 * @param {object|null|undefined} lastRun
 * @param {{ manualRunId?: string|null, enabled?: boolean }} [opts]
 * @returns {{ manual: boolean, oneCompany: boolean, catchUp: boolean, error: string, label: string }}
 */
export function lastRunKind(lastRun, { manualRunId = null, enabled = true } = {}) {
  const manual =
    lastRun?.trigger === 'manual' ||
    (typeof manualRunId === 'string' && manualRunId !== '' && lastRun?.run_id === manualRunId);
  const oneCompany = typeof lastRun?.company_id === 'string' && lastRun.company_id !== '';
  const error = typeof lastRun?.error === 'string' ? lastRun.error.trim() : '';
  let label;
  if (manual) label = oneCompany ? '마감 실행(수동 · 병원 1곳)' : '마감 실행(수동)';
  else label = enabled ? '자동 마감' : '마감 실행';
  if (error) label = `${label} · 중단된 실행`;
  return { manual, oneCompany, catchUp: lastRun?.catch_up === true, error, label };
}

/**
 * 마감 실행 기록 툴팁 줄 — 실행 ID·종류·시각·결과(건너뜀 포함)·대상 수·중단 사유
 * @param {object|null|undefined} lastRun `auto_close.last_run`
 * @param {{ manualRunId?: string|null, enabled?: boolean }} [opts]
 * @returns {string[]} 없으면 []
 */
export function lastRunTooltipLines(lastRun, opts = {}) {
  if (!lastRun) return [];
  const kind = lastRunKind(lastRun, opts);
  const count = (v) => formatNumber(num(v) ?? 0);
  const head = [kind.manual ? (kind.oneCompany ? '수동 실행 · 병원 1곳만' : '수동 실행') : '자동 실행'];
  if (kind.catchUp) head.push('밀린 달 따라잡기');
  if (lastRun.run_id) head.push(`실행 ID ${lastRun.run_id}`);
  const lines = [head.join(' · ')];
  lines.push(
    [
      `시작 ${formatKstDateTime(lastRun.started_at)}`,
      isNumber(lastRun.finished_at) ? `끝 ${formatKstDateTime(lastRun.finished_at)}` : '아직 실행 중',
    ].join(' · ')
  );
  const result = [
    `확정 ${count(lastRun.confirmed)}`,
    `검토 필요 ${count(lastRun.review)}`,
    `실패 ${count(lastRun.failed)}`,
    `건너뜀 ${count(lastRun.skipped)}`,
  ];
  if (isNumber(lastRun.targets)) result.unshift(`대상 ${formatNumber(lastRun.targets)}곳`);
  lines.push(result.join(' · '));
  if (kind.error) lines.push(`중간에 멈춘 실행입니다: ${kind.error}`);
  return lines;
}

/**
 * 자동 마감 요약 한 줄의 문구와 색 — autoCloseSummaryText·autoCloseSummaryTone 이 같은 판단을 쓴다.
 * @returns {{ text: string, tone: 'ok'|'warn'|'info' }}
 */
function autoCloseSummaryState(closeRuns, month, nowMs, { firstPlanMonth = '', manualRunId = null } = {}) {
  if (!closeRuns) return { text: '', tone: 'info' };
  const ac = closeRuns.auto_close ?? {};
  const enabled = ac.enabled !== false;
  const lastRun = ac.last_run ?? null;
  if (lastRun) {
    // last_run 은 자동이든 수동이든 마지막 실행이다(요청 계약 4-1) — trigger 나 이 화면에서 시작한 실행 id 로 수동을
    // 가린다. 회사 하나만 돈 수동 실행·중간에 멈춘 실행은 앞머리에 적는다(lastRunKind)
    const kind = lastRunKind(lastRun, { manualRunId, enabled });
    const at = formatKstDateTime(num(lastRun.started_at) ?? lastRun.finished_at);
    const text = `${kind.label} ${at} — ${closeCountsText(closeRuns.counts, lastRun)}`;
    if (!enabled) return { text: `${text} · 자동 마감 꺼짐`, tone: 'warn' };
    return { text, tone: kind.error || arr(closeRuns.attention).length ? 'warn' : 'ok' };
  }
  if (!enabled) return { text: AUTO_CLOSE_DISABLED_TEXT, tone: 'warn' };
  // 서버가 예정 시각을 null 로 주면 자동 마감하지 않는 달이다(2026-09 이전 달 — 회신 표 9) — 날짜를 지어내지 않는다.
  // 기본 요율 목록(firstPlanMonth)보다 먼저 본다 — 목록이 늦게 와도 첫 로드부터 같은 문구다
  if (hasOwn(ac, 'scheduled_at') && ac.scheduled_at === null) {
    return { text: '이 달은 자동 마감 대상이 아닙니다 — 마감은 [지금 마감 실행]으로만 돈다', tone: 'info' };
  }
  // 따라잡기는 가장 이른 기본 요율 버전의 달부터다. 그 전 달은 매일 잡이 집는 기간(다음 달 마감일 ~ 말일)까지
  // 지났으면 어떤 자동 실행도 오지 않는다
  if (
    isValidMonth(firstPlanMonth) &&
    isValidMonth(month) &&
    month < firstPlanMonth &&
    nextAutoRunInWindow(month, nowMs, ac) === null
  ) {
    return { text: `기본 요율 시작(${firstPlanMonth}) 전 달이라 자동 마감 대상이 아닙니다`, tone: 'info' };
  }
  const closeDay = num(ac.close_day) ?? DEFAULT_CLOSE_DAY;
  const hour = closeHourOf(ac);
  if (isCurrentMonth(month, nowMs)) {
    return { text: `다음 달 ${closeDay}일 ${pad2(hour)}:00 자동 마감`, tone: 'info' };
  }
  const scheduledAt = num(ac.scheduled_at) ?? autoCloseAtMs(month, { closeDay, hour });
  if (isNumber(scheduledAt) && scheduledAt <= nowMs) {
    return {
      text: `자동 마감 대기 — 예정 시각 ${formatKstDateTime(scheduledAt)}(KST)이 지났고 아직 실행 기록이 없습니다`,
      tone: 'warn',
    };
  }
  return { text: `자동 마감 예정 ${formatKstDateTime(scheduledAt)}(KST)`, tone: 'info' };
}

/**
 * @typedef {Object} AutoCloseSummaryOptions
 * @property {string} [firstPlanMonth] 가장 이른 기본 요율 버전의 적용 시작 월 — 그 전 달은 자동 마감 대상이 아니다
 * @property {string|null} [manualRunId] 이 화면에서 [지금 마감 실행]으로 시작한 실행 id — last_run 이 그 실행이면 수동
 */

/**
 * 기본 요율 화면 위 자동 마감 요약의 한 줄 안내
 * @param {object|null|undefined} closeRuns 요청 계약 4-1 마감 현황 응답
 * @param {string} month 대상 달
 * @param {number} [nowMs]
 * @param {AutoCloseSummaryOptions} [opts]
 * @returns {string} 예: '자동 마감 예정 2026-11-03 06:00(KST)', '다음 달 3일 06:00 자동 마감',
 *   '자동 마감 2026-10-03 06:00 — 확정 11(자동 11 · 사람 0) · 검토 필요 2 · 실패 0',
 *   '마감 실행(수동) 2026-10-08 12:00 — …', '기본 요율 시작(2026-09) 전 달이라 자동 마감 대상이 아닙니다', 꺼짐 안내
 */
export function autoCloseSummaryText(closeRuns, month, nowMs = Date.now(), opts = {}) {
  return autoCloseSummaryState(closeRuns, month, nowMs, opts).text;
}

/**
 * 자동 마감 요약 한 줄의 색 — autoCloseSummaryText 와 같은 판단.
 * @param {object|null|undefined} closeRuns
 * @param {string} month
 * @param {number} [nowMs]
 * @param {AutoCloseSummaryOptions} [opts]
 * @returns {'ok'|'warn'|'info'} ok: 실행 끝·사람이 볼 병원 없음 / warn: 꺼짐·예정 시각 지남·사람이 볼 병원 있음 /
 *   info: 마감 전·자동 마감 대상이 아닌 달
 */
export function autoCloseSummaryTone(closeRuns, month, nowMs = Date.now(), opts = {}) {
  return autoCloseSummaryState(closeRuns, month, nowMs, opts).tone;
}

/**
 * 정산 탭 "월 마감 (자동)" 머리 한 줄 — 상세 `auto_close`(없으면 켜짐·3일·06:00). 마감 시각은 `close_hour` 를 쓰고,
 * 자동 마감 대상이 아닌 달(서버 `scheduled_at: null`)은 그렇다고 말한다.
 * @param {object|null|undefined} autoClose 상세 `auto_close`
 * @returns {string}
 */
export function closeScheduleText(autoClose) {
  const ac = autoClose ?? {};
  if (ac.enabled === false) return AUTO_CLOSE_DISABLED_TEXT;
  if (hasOwn(ac, 'scheduled_at') && ac.scheduled_at === null) {
    return '이 달은 자동 마감 대상이 아닙니다 — 마감은 월 정산 목록의 [지금 마감 실행]으로만 합니다';
  }
  const day = num(ac.close_day) ?? DEFAULT_CLOSE_DAY;
  return `다음 달 ${day}일 ${pad2(closeHourOf(ac))}:00(KST) 서버가 자동 마감 · 차단 경고가 없으면 바로 확정 · 운영자가 매달 입력할 것 없음`;
}

/**
 * 추정 중(마감 전) 안내 — 화면 명세 2-4. `detail.auto_close` 가 없으면 켜짐·3일·06:00 으로 본다.
 * 그 달에 적용할 기본 요율 버전이 없는 달(`rates: null`·DEFAULT_PLAN_MISSING, 시드 이전 달)은 금액이 없어 "추정치"라
 * 하지 않는다. 매일 잡이 집는 기간(다음 달 마감일 ~ 말일)도 지났으면 따라잡기만 남는데, 따라잡기는 가장 이른 기본
 * 요율 버전의 달부터라(요청 계약 4-1) 그 버전이 없는 달에는 자동 실행이 오지 않는다 — 예정 시각을 말하지 않는다.
 * @param {object|null|undefined} detail
 * @param {string} month
 * @param {number} [nowMs]
 * @returns {string} 예: '2026-11-03 06:00 자동 마감 예정. 지금 금액은 추정치입니다.'
 */
export function estimatingNotice(detail, month, nowMs = Date.now()) {
  const ac = detail?.auto_close ?? {};
  const noPlan =
    !!detail && (detail.rates === null || arr(detail.warnings).some((w) => w?.code === 'DEFAULT_PLAN_MISSING'));
  const amountText = noPlan ? '그 달에 적용할 기본 요율 버전이 없어 금액은 계산하지 않았습니다.' : '지금 금액은 추정치입니다.';
  if (ac.enabled === false) return `${AUTO_CLOSE_DISABLED_TEXT}. ${amountText}`;
  // 서버가 예정 시각을 null 로 주면 자동 마감 대상이 아닌 달이다(2026-09 이전 달 — 회신 표 9). 그 달 기본 요율 버전을
  // 만들어도 자동 마감은 오지 않는다 — 날짜를 지어내지 않는다
  if (hasOwn(ac, 'scheduled_at') && ac.scheduled_at === null) {
    return `자동 마감 대상이 아닌 달입니다 — 마감은 월 정산 목록의 [지금 마감 실행]으로만 합니다. ${amountText}`;
  }
  if (noPlan && nextAutoRunInWindow(month, nowMs, ac) === null && isMonthEnded(month, nowMs)) {
    return '자동 마감 대상이 아닌 달입니다. 그 달에 적용할 기본 요율 버전이 생기기 전에는 자동 마감이 이 달을 마감하지 않고, 금액도 계산하지 않습니다.';
  }
  const closeDay = num(ac.close_day) ?? DEFAULT_CLOSE_DAY;
  const at = num(ac.scheduled_at) ?? autoCloseAtMs(month, { closeDay, hour: closeHourOf(ac) });
  if (isNumber(at) && at <= nowMs) {
    return `자동 마감 예정 시각(${formatKstDateTime(at)})이 지났지만 아직 마감되지 않았습니다. ${amountText}`;
  }
  return `${formatKstDateTime(at)} 자동 마감 예정. ${amountText}`;
}

/**
 * 자동 마감이 이 달을 다시 집는 다음 시각. 서버가 `auto_close.next_retry_at` 을 주면 그 값 그대로다(회신 표 9 —
 * 화면이 추정하지 않는다. null 이면 남은 자동 실행이 없다). 값이 없는 응답이면 마감일 `close_hour`시부터 그 다음 달
 * 말일까지 매일이라는 규칙(요청 계약 4-1)으로 계산한다. 꺼져 있으면 null.
 * @param {string} month 대상 달
 * @param {number} [nowMs]
 * @param {{ enabled?: boolean, close_day?: number, close_hour?: number, next_retry_at?: number|null }|null} [autoClose] 상세 `auto_close`
 * @returns {number|null} unix ms
 */
export function autoRetryAt(month, nowMs = Date.now(), autoClose = null) {
  if (autoClose?.enabled === false) return null;
  if (autoClose && hasOwn(autoClose, 'next_retry_at')) return num(autoClose.next_retry_at);
  return nextAutoRunInWindow(month, nowMs, autoClose);
}

/**
 * 마감 실패 화면의 재시도 안내(회신 표 2). vox 가 잠깐 실패한 달은 `failed` 로 남고 다음 자동 실행이 다시 돈다.
 * 남은 자동 실행이 없으면(재시도 기간이 끝났거나 꺼짐) 이 병원만 다시 돌리는 [지금 다시 실행]으로 보낸다.
 * @param {string} month 대상 달
 * @param {number} [nowMs]
 * @param {{ enabled?: boolean, close_day?: number, close_hour?: number, next_retry_at?: number|null }|null} [autoClose] 상세 `auto_close`
 * @returns {string} '다음 자동 실행(내일 06:00)에서 다시 시도합니다.' · NO_AUTO_RETRY_TEXT · NO_AUTO_RETRY_DISABLED_TEXT
 */
export function failedRetryText(month, nowMs = Date.now(), autoClose = null) {
  const at = autoRetryAt(month, nowMs, autoClose);
  if (at !== null) return `다음 자동 실행(${relativeKstDayLabel(at, nowMs)} ${formatKstTime(at)})에서 다시 시도합니다.`;
  if (autoClose?.enabled === false) return NO_AUTO_RETRY_DISABLED_TEXT;
  if (autoClose && hasOwn(autoClose, 'scheduled_at') && autoClose.scheduled_at === null) return NO_AUTO_RETRY_NOT_TARGET_TEXT;
  return NO_AUTO_RETRY_TEXT;
}

/** `NOT_FINAL_CALLS` "통화가 끝나길 기다리기"의 안내 — 자동 마감이 정말 다시 계산하는 경우 */
const NOT_FINAL_DAILY_HINT = '매일 자동으로 다시 계산합니다.';

/**
 * `NOT_FINAL_CALLS` 해결 방법 "통화가 끝나길 기다리기"의 안내. 자동 마감이 이 정산을 정말 다시 계산할 때만
 * "매일 자동으로 다시 계산합니다"라고 한다(요청 계약 4-1).
 *  - 검토 필요인데 사람이 손댔다(저장·재수정·일회성 항목, `human_touched`·`reopened`): 잡이 건너뛴다
 *  - 검토 필요인데 자동 마감이 꺼졌거나 다시 집을 자동 실행이 남지 않았다(autoRetryAt — 서버 `next_retry_at`):
 *    다시 계산하지 않는다
 *  - 그 밖: 매일 다시 계산한다. 검토 필요는 경고가 사라져도 자동 확정하지 않는다(트레이드오프)
 * 마감 실패는 재시도 안내(failedRetryText)와 같은 판단이다 — 재시도가 없으면 [지금 다시 실행]으로 보낸다.
 * @param {object|null|undefined} detail 상세 응답(`statement`, `auto_close`)
 * @param {string} month
 * @param {number} [nowMs]
 * @returns {string}
 */
export function notFinalWaitHint(detail, month, nowMs = Date.now()) {
  const statement = detail?.statement ?? null;
  const status = statement?.status ?? 'estimating';
  const autoClose = detail?.auto_close ?? null;
  if (status === 'review') {
    if (statement.human_touched === true || statement.reopened === true) {
      return '사람이 손댄 정산이라 자동 마감이 다시 계산하지 않습니다 — 통화가 끝난 뒤 [다시 계산]을 누릅니다.';
    }
    if (autoClose?.enabled === false) {
      return '자동 마감이 꺼져 있어 다시 계산하지 않습니다 — 통화가 끝난 뒤 [다시 계산]을 누릅니다.';
    }
    if (autoRetryAt(month, nowMs, autoClose) === null) {
      return '자동 마감이 이 달을 더는 다시 계산하지 않습니다 — 통화가 끝난 뒤 [다시 계산]을 누릅니다.';
    }
    return `${NOT_FINAL_DAILY_HINT} 경고가 사라져도 확정은 사람이 합니다.`;
  }
  if (status === 'failed' && autoRetryAt(month, nowMs, autoClose) === null) {
    return '자동 재시도가 없습니다 — 통화가 끝난 뒤 [지금 다시 실행]을 누릅니다.';
  }
  return NOT_FINAL_DAILY_HINT;
}

/**
 * `[지금 마감 실행]` 을 막는 이유 — 끝난 달만 받는다(아니면 409 MONTH_NOT_ENDED).
 * @param {string} month
 * @param {number} [nowMs]
 * @returns {string|null} 실행할 수 있으면 null
 */
export function closeRunDisabledReason(month, nowMs = Date.now()) {
  return isValidMonth(month) && isMonthEnded(month, nowMs) ? null : '끝난 달에서만 실행할 수 있습니다.';
}

/**
 * `[지금 마감 실행]` 확인창(화면 명세 3). 수납 문장은 갈래 없이 늘 붙인다 — 자동 마감 요약에는 정산 상세가 없다
 * (AC 결제 요청 결론 2·요청 계약 6)
 * @param {string} month
 * @returns {string}
 */
export function closeRunConfirmText(month) {
  return `${monthLabel(month)} 마감을 지금 실행합니다. 이미 확정된 병원은 건드리지 않고, 경고 없는 병원은 바로 확정되어 그 금액이 확정 정산 금액이 됩니다. 수납은 admin-reseller 에서 합니다(카드는 대표 승인 뒤, 계좌이체는 입금 안내 뒤).`;
}

/**
 * 확정 확인창·확정 배너 끝의 수납 문장(AC 결제 요청 결론 2) — 결제가 걸린 달(in·null)은 admin-reseller 안내, 시작 월 이전
 * (before_start)은 '수납 대상 아님', 그 밖(inactive·칸 없는 옛 call-api)은 없다.
 * @param {'in'|'before_start'|'inactive'|null} scope settlementPayment.js paymentScopeOf(detail)
 * @returns {string} 앞에 띄어쓰기를 둔 문장 또는 ''
 */
function collectionSentence(scope) {
  if (isPaymentMonthScope(scope)) return ' 수납은 admin-reseller 에서 합니다.';
  return scope === 'before_start' ? ' 수납 대상 아님(수납 시작 월 이전).' : '';
}

/**
 * `[이 달 확정]` 확인창(화면 명세 2-4). 끝의 수납 문장은 갈래로 고른다(AC 결제 요청 결론 2 표의 문장 그대로)
 * @param {string} month
 * @param {string} companyName
 * @param {number} total 공급가액
 * @param {'in'|'before_start'|'inactive'|null} [scope] paymentScopeOf(detail) — 빼면 inactive(수납 문장 없음)
 * @returns {string}
 */
export function confirmStatementText(month, companyName, total, scope = 'inactive') {
  return `${monthLabel(month)} ${companyName || EMPTY} 정산을 공급가액 ${formatWon(total)}(부가세 별도)으로 확정합니다. 이 금액이 이 달 확정 정산 금액이 됩니다.${collectionSentence(scope)}`;
}

/**
 * 확정 확인창·성공 알림에 덧붙이는 청구 합계 한 문장 — 확정 버튼은 '{청구 합계}원으로 확정'(부가세 포함)인데 확인 문구
 * (결론 2 표 그대로)는 공급가액으로 말하므로, 같은 흐름에서 두 금액을 잇는다. 확인 문구 자체는 바꾸지 않는다.
 * @param {number|null|undefined} totalWithVat 청구 합계(부가세 포함)
 * @returns {string} 숫자가 아니면 ''
 */
export function confirmWithVatText(totalWithVat) {
  return isNumber(totalWithVat) ? `부가세를 더한 청구 합계는 ${formatWon(totalWithVat)}입니다.` : '';
}

/**
 * `[재수정]` 확인창(화면 명세 2-4) — 사유 메모는 필수(입력은 모달 몫). 결제가 걸린 달(in·null)은 받은 돈·멈춤·차액 안내로
 * 바꾸고, 그 밖(before_start·inactive)은 '청구'를 '정산'으로만 바꾼다(AC 결제 요청 결론 2)
 * @param {number} total 지금 확정 금액(공급가액)
 * @param {'in'|'before_start'|'inactive'|null} [scope] paymentScopeOf(detail) — 빼면 inactive
 * @returns {string}
 */
export function reopenConfirmText(total, scope = 'inactive') {
  if (isPaymentMonthScope(scope)) {
    return '검토 필요로 되돌립니다. 받은 돈은 그대로입니다. 재수정 중에는 카드 결제와 입금 기록이 멈춥니다. 다시 확정하면 admin-reseller 에서 차액을 처리합니다(카드면 승인, 계좌이체면 추가 입금 안내 또는 계좌 환불). 자동 마감은 이 정산을 다시 건드리지 않습니다.';
  }
  return `검토 필요로 되돌립니다. 다시 확정할 때까지 지금 확정 금액(${formatWon(total)})이 직전 확정 정산 금액으로 남습니다. 자동 마감은 이 정산을 다시 건드리지 않습니다.`;
}

/**
 * 정산 탭 확정 영역의 배너 문구. 끝의 수납 문장은 확정 확인창과 같은 갈래로 고른다(AC 결제 요청 결론 2 — 표의 문장 그대로라
 * 줄이지 않는다)
 * @param {'in'|'before_start'|'inactive'|null} [scope] paymentScopeOf(detail) — 빼면 inactive
 * @returns {string}
 */
export function confirmedBannerText(scope = 'inactive') {
  return `확정된 정산은 자동 마감이 다시 덮지 않습니다. 이 금액이 이 달 확정 정산 금액입니다.${collectionSentence(scope)}`;
}

/** 확정 카드 본문의 수납 문장(결제가 걸린 달 — in·null) — confirmedReopenHintText 의 가운데 문장과 같다 */
export const REOPEN_PAYMENT_HINT_TEXT = '받은 돈은 그대로입니다. 다시 확정하면 admin-reseller 에서 차액을 처리합니다.';

/**
 * 정산 탭 확정 영역의 재수정 안내. 결제가 걸린 달(in·null)은 받은 돈·차액 문장을 더하고, 그 밖은 '청구'를 '정산'으로만
 * 바꾼다('수납 대상 아님' 문장은 넣지 않는다 — AC 결제 요청 결론 2)
 * @param {number} total 지금 확정 금액(공급가액)
 * @param {'in'|'before_start'|'inactive'|null} [scope] paymentScopeOf(detail) — 빼면 inactive
 * @returns {string}
 */
export function confirmedReopenHintText(total, scope = 'inactive') {
  const payment = isPaymentMonthScope(scope) ? ` ${REOPEN_PAYMENT_HINT_TEXT}` : '';
  return `재수정하면 검토 필요로 돌아가고, 다시 확정할 때까지 지금 확정 금액(${formatWon(total)})이 이 달 확정 정산 금액으로 남습니다.${payment} 재수정한 정산은 자동 마감이 다시 계산하지 않습니다(사유 메모 필수).`;
}

/** 검토 폼 `[저장]` 확인창 — 저장한 정산은 자동 마감이 다시 계산하지 않는다 */
export const SAVE_STATEMENT_CONFIRM_TEXT =
  '검토 필요 상태 그대로 금액·메모만 저장합니다. 저장한 정산은 자동 마감이 다시 계산하지 않습니다.';

/**
 * 409 STATEMENT_CONFLICT — 다른 사람이나 자동 마감이 그 사이 정산을 바꿨다(회신 표 10). 정산 처리(저장·다시 계산·
 * 확정·재수정)와 일회성 항목 쓰기가 받으면 이 안내 뒤 상세를 다시 불러온다. 입력 중인 금액은 남는다.
 */
export const STATEMENT_CONFLICT_TEXT =
  '그 사이 정산이 바뀌었습니다(다른 사람이나 자동 마감). 최신 정산을 다시 불러옵니다 — 확인한 뒤 다시 시도하세요.';

/** 정산 줄 금액 상한 — call-api 는 0 ~ 10,000,000,000 원 정수만 받는다(넘으면 400 VALIDATION_ERROR, 회신 표 13) */
export const STATEMENT_AMOUNT_MAX = 10000000000;

/** 검토 필요 달의 일회성 항목 추가·수정·삭제 확인창(화면 명세 2-4 "확인창에 명시") */
export const ONE_OFF_REVIEW_CONFIRM_TEXT =
  '검토 필요 달에 넣으면 정산에 바로 반영되고, 사람이 손댄 정산이 되어 자동 마감이 다시 계산하지 않습니다.';

/** 회사 예외 저장·전부 해제 확인창의 세 가지(화면 명세 2-1 "저장 확인창에 이 세 가지를 명시") */
export const OVERRIDE_SAVE_EFFECT_LINES = deepFreeze([
  '이 달이 추정 중이면 금액이 바로 바뀝니다.',
  '검토 필요면 정산 탭의 [다시 계산]을 눌러야 정산 스냅샷에 들어갑니다.',
  '확정된 달은 바뀌지 않습니다.',
]);

/**
 * 기본 요율 저장 확인창(화면 명세 3). 확정 정산이 쓴 버전이면 한 줄을 더한다.
 * @param {number|null|undefined} confirmedStatements 이 버전을 쓴 확정 정산 수(새 버전이면 0)
 * @returns {string}
 */
export function defaultPlanSaveConfirmText(confirmedStatements) {
  const n = num(confirmedStatements) ?? 0;
  const text = `이 버전을 쓰는 달 중 확정 전인 달(추정 중)에 바로 반영됩니다. 검토 필요인 병원은 [다시 계산] 또는 다음 자동 실행에서 반영됩니다. 확정된 정산 ${formatNumber(n)}건은 바뀌지 않습니다.`;
  return n > 0
    ? `${text} 이미 쓰인 단가를 바꾸면 같은 달에 병원마다 다른 단가가 적용됩니다. 단가를 바꾸려면 다음 달 버전을 만드세요.`
    : text;
}

/** call-api 가 기본 요율 버전이 하나도 없을 때만 넣는 시드 버전의 적용 시작 월 */
export const DEFAULT_PLAN_SEED_MONTH = '2026-09';

/**
 * 시드 버전 삭제 확인창에 덧붙이는 말 — 시드는 기본 버전이 하나도 없을 때만 넣어서, 다른 버전이 있는 상태에서 지운
 * 2026-09 는 다시 배포해도 되살아나지 않는다(회신 표 14).
 * @param {string} effectiveFrom
 * @returns {string} 시드 달이 아니면 ''
 */
export function defaultPlanDeleteSeedNote(effectiveFrom) {
  return effectiveFrom === DEFAULT_PLAN_SEED_MONTH
    ? `${DEFAULT_PLAN_SEED_MONTH} 는 배포 때 넣은 시드 버전이지만, 지우면 다시 생기지 않습니다(다시 배포해도 되살리지 않습니다).`
    : '';
}

/**
 * 기본 요율 버전 삭제를 막는 이유(버튼 비활성 + 툴팁)
 * @param {{ effective_from: string, confirmed_statements?: number }} plan
 * @param {Array<object>|null|undefined} items 전체 버전
 * @returns {string|null}
 */
export function defaultPlanDeleteBlockReason(plan, items) {
  if ((num(plan?.confirmed_statements) ?? 0) > 0) return '이 버전을 쓴 확정 정산이 있어 삭제할 수 없습니다.';
  if (arr(items).length <= 1) return '마지막 남은 기본 요율 버전은 삭제할 수 없습니다.';
  return null;
}

/**
 * 회사 예외 버전 삭제를 막는 이유
 * @param {{ confirmed_statements?: number }|null|undefined} override
 * @returns {string|null}
 */
export function overrideDeleteBlockReason(override) {
  return (num(override?.confirmed_statements) ?? 0) > 0
    ? '이 예외 버전을 쓴 확정 정산이 있어 삭제할 수 없습니다.'
    : null;
}

/** 삭제된 회사 표시(목록 행·사람이 볼 병원 칩) — 회신 표 6 */
export const DELETED_COMPANY_LABEL = '삭제된 회사';

/** lease 가 만료돼 멈춘 마감(`attention[].status: 'closing'`, error_code CLOSE_INTERRUPTED) — 회신 표 8 */
export const CLOSE_INTERRUPTED_LABEL = '마감 중단';

/**
 * "사람이 볼 병원" 칩 — 검토 필요(재수정 포함)·마감 실패·마감 중단. 칩 색은 상태, 툴팁에 남은 차단 경고 코드나
 * 실패·중단 사유(`error_message`, 없으면 `error_code`). 경고는 헤더 경고 툴팁(warningSummary)과 같은 '이름(코드)' 모양이다.
 * 마감 중단은 마감하던 인스턴스가 멈춰 lease 가 만료된 `closing` 이다 — 상세에는 lease 가 없어 "마감 중"으로만 보인다.
 * @param {object} item 요청 계약 4-1 `attention[]`
 * @returns {{ companyId: string|null, label: string, status: 'review'|'failed'|'closing'|string, statusLabel: string, color: string, tooltip: string, reason: string, deleted: boolean }}
 *   tooltip 은 삭제된 회사면 '삭제된 회사 · ' 를 앞에 붙인 문장, reason 은 붙이기 전 문장이다
 */
export function attentionChip(item) {
  const status = item?.status ?? 'review';
  const failed = status === 'failed';
  const interrupted = status === 'closing';
  const codes = arr(item?.blocking_codes);
  const reason = (typeof item?.error_message === 'string' && item.error_message.trim()) || item?.error_code || '';
  let tooltip;
  if (failed) tooltip = reason ? `마감 실패 · ${reason}` : '마감 실패';
  else if (interrupted) tooltip = reason ? `${CLOSE_INTERRUPTED_LABEL} · ${reason}` : CLOSE_INTERRUPTED_LABEL;
  else if (codes.length) tooltip = `남은 차단 경고: ${codes.map((c) => `${warningMeta(c).label}(${c})`).join(', ')}`;
  else tooltip = '검토 필요';
  const deleted = item?.company_deleted === true;
  let statusLabel = item?.reopened ? '검토 필요(재수정)' : '검토 필요';
  if (failed) statusLabel = '마감 실패';
  else if (interrupted) statusLabel = CLOSE_INTERRUPTED_LABEL;
  let color = STATEMENT_STATUS_META.review.color;
  if (failed) color = STATEMENT_STATUS_META.failed.color;
  else if (interrupted) color = 'volcano';
  return {
    companyId: item?.company_id ?? null,
    label: item?.company_name || item?.company_id || EMPTY,
    status,
    statusLabel,
    color,
    tooltip: deleted ? `${DELETED_COMPANY_LABEL} · ${tooltip}` : tooltip,
    reason: tooltip,
    deleted,
  };
}

// ---------------------------------------------------------------------------
// 9. 대사·연결 진단 (화면 명세 2-2·2-6)
// ---------------------------------------------------------------------------

/** vox 한도(429) 안내 — 화면 명세 2-2 */
export const RECON_RATE_LIMITED_TEXT = 'vox 호출 한도에 걸렸습니다. 잠시 후 다시 시도하세요.';

/**
 * 건수 대사 판정 — 웹 테스트를 뺀 건수끼리 비교한다(DB `db_non_web_total` 대 vox `vox_live_non_web_total`, 허용 오차 0 —
 * 회신 표 3). 웹콜은 DB 미러에 없어 전체 건수로는 늘 어긋난다. 청구서 수량은 판정에 쓰지 않는다.
 * call-api 는 월말 뒤에 읽은 vox 값으로만 COUNT_MISMATCH 를 낸다 — 그 전에 읽은 값이면 `final: false` 이고 이름표에
 * "월말 전 값"을 단다(차이가 있어도 경고가 되지 않는다).
 * @param {unknown} dbNonWebTotal
 * @param {unknown} voxNonWebTotal
 * @param {{ fetchedAt?: unknown, month?: string }} [opts] vox 를 읽은 시각(`vox_live_fetched_at`)과 대상 달 — 둘 다 있어야 판정한다
 * @returns {{ state: 'match'|'mismatch'|'unknown', diff: number|null, label: string, final: boolean }}
 */
export function reconStatus(dbNonWebTotal, voxNonWebTotal, { fetchedAt = null, month = '' } = {}) {
  if (!isNumber(dbNonWebTotal) || !isNumber(voxNonWebTotal)) {
    return { state: 'unknown', diff: null, label: 'vox 건수 확인 전', final: false };
  }
  // 읽은 시각·달을 모르면(예전 모양) 판정한 값으로 본다
  const final = !isNumber(fetchedAt) || !isValidMonth(month) || fetchedAt >= monthEndMsKst(month);
  const tail = final ? '' : ' · 월말 전 값';
  const diff = voxNonWebTotal - dbNonWebTotal;
  if (diff === 0) return { state: 'match', diff: 0, label: `일치${tail}`, final };
  return { state: 'mismatch', diff, label: `차이 ${formatNumber(Math.abs(diff))}건${tail}`, final };
}

/**
 * 누락 통화 ID 를 받지 못한 이유(`missing: null` 일 때의 `missing_unavailable_reason` — 회신 B17)
 * @param {unknown} reason 'not_requested' | 'too_many_calls' | null
 * @returns {string}
 */
export function reconMissingUnavailableText(reason) {
  if (reason === 'too_many_calls') {
    return '통화가 많아 vox 통화 ID 를 끝까지 읽지 못했습니다(100페이지 또는 25초 초과). 누락 목록 없이 건수로만 봅니다.';
  }
  if (reason === 'not_requested') return '누락 통화 ID 를 요청하지 않은 응답입니다. [누락 통화 ID 확인]으로 다시 읽습니다.';
  return 'vox 통화 ID 목록을 받지 못했습니다. 잠시 후 다시 시도하세요.';
}

/**
 * 건수 대사 호출 실패의 안내 — 429 는 고정 문구, 409(하위 조직 없음·키 미발급)는 그 사유.
 * 그 밖은 null(호출부가 parseCallApiError 로 처리).
 * @param {number|undefined} status
 * @param {string|undefined} code
 * @param {string|undefined} [message] 서버 봉투 message(한국어일 때만 쓴다)
 * @returns {string|null}
 */
export function reconErrorText(status, code, message) {
  if (status === 429) return RECON_RATE_LIMITED_TEXT;
  const korean = typeof message === 'string' && /[가-힣]/.test(message) ? message : null;
  if (code === 'ORGANIZATION_NOT_PROVISIONED') return korean ?? '이 병원에는 vox 하위 조직이 없습니다.';
  if (code === 'ORGANIZATION_KEY_NOT_READY') return korean ?? '이 병원의 vox 하위 조직 키가 아직 발급되지 않았습니다.';
  return null;
}

// ---- 상담사 연결 시도(attempts) — 리포트 번호별 시도·수집률, 통화 단건 시도 순서(attempts 회신) ----

/**
 * 번호별 시도 표의 열 — 리포트 `per_number[].attempts` 의 키(화면 명세 2-6 순서). `other`(failed·계약 밖 결과)는
 * 열 없이 `total` 에만 든다(attempts 회신 "공통").
 */
export const ATTEMPT_COLUMN_KEYS = deepFreeze([
  'total',
  'answered',
  'no_answer_30s',
  'unavailable',
  'caller_hangup_ringing',
  'no_ring',
  'superseded',
]);
/** 합계에 더하는 키 — 열 일곱 + other */
const ATTEMPT_COUNT_KEYS = [...ATTEMPT_COLUMN_KEYS, 'other'];
const ATTEMPT_DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** 시도 묶음인가 — 객체면 센다. null(모름 — 수집 전)·배열·원시값은 묶음이 아니다 */
const isAttemptCounts = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
/** 시도 묶음 → 키마다 숫자(빠진 칸은 null — 표에서 '—') */
const attemptCounts = (v) => Object.fromEntries(ATTEMPT_COUNT_KEYS.map((k) => [k, num(v[k])]));

/**
 * 시도 수집 시작일 — 번호 행의 `attempts_collected_from`('YYYY-MM-DD', KST). 환경 전체 값이라 모든 행이 같고,
 * 수집 시작 전 달에도 실린다. 시도가 아직 한 건도 없는 환경이거나 형식이 틀리면 null.
 * @param {Array<object>|null|undefined} perNumber 리포트 `per_number`
 * @returns {string|null}
 */
export function attemptsCollectedFrom(perNumber) {
  for (const row of arr(perNumber)) {
    const value = row?.attempts_collected_from;
    if (typeof value === 'string' && ATTEMPT_DATE_RE.test(value)) return value;
  }
  return null;
}

/**
 * 시도 기록 수집률(연결 진단 칩) = `flow.handoffs_with_attempts` ÷ `flow.handoffs_attempts_expected`(attempts 회신 admin 2).
 * 분모는 vox 직접 전환이 아니고 라우팅이 상담사 번호를 고른, 수집 시작 뒤의 직원 연결 요청이다. `flow.handoffs` 로
 * 나누지 않는다 — 대상 번호가 없던 요청과 vox 직접 전환에는 원래 시도가 없어, 빠진 기록이 없어도 100% 가 되지 않는다.
 * 100% 는 다 모였을 때만 보인다(덜 모였으면 반올림해도 99.9% 에서 멈춘다).
 * @param {object|null|undefined} flow 리포트 `flow`
 * @returns {{ state: 'pending'|'none'|'partial'|'rate', value: string, expected: number|null, withAttempts: number|null, complete: boolean }}
 *   pending = 둘 다 null(수집 전) · none = 분모 0('—') · partial = 한쪽만 온 응답('—') · rate = 'n.n%'
 */
export function attemptsCollectionRate(flow) {
  const expected = num(flow?.handoffs_attempts_expected);
  const withAttempts = num(flow?.handoffs_with_attempts);
  const base = { expected, withAttempts, complete: false };
  if (expected === null && withAttempts === null) return { ...base, state: 'pending', value: '수집 전' };
  if (expected === 0) return { ...base, state: 'none', value: EMPTY };
  if (expected === null || withAttempts === null) return { ...base, state: 'partial', value: EMPTY };
  const complete = withAttempts >= expected;
  const pct = Math.round((withAttempts * 1000) / expected) / 10;
  return { ...base, state: 'rate', value: `${(complete ? pct : Math.min(pct, 99.9)).toFixed(1)}%`, complete };
}

/**
 * 연결 진단 2-6 — 번호별 시도 표와 칩의 값(attempts 회신 admin 1~3·5).
 *  - rows: 번호 행(per_number 순서). attempts 가 null 이면 그 행은 수집 전
 *  - unattributed: 어느 상담사 번호와도 맞지 않는 시도(지워진 번호 등, `per_number_unattributed.attempts`) — 시도가 있을 때만
 *  - totals: 시도 묶음이 있는 번호 행 + 번호 밖 시도의 합 = 그 달 회사 시도 전체. 묶음이 하나도 없으면 null
 *  - noRing: 벨 없이 끝난 시도 = Σ 번호 행 no_ring + 번호 밖 no_ring(admin 3)
 *  - collected: 이 달 시도를 모으고 있다 — 시도 묶음이나 수집률 값이 하나라도 있다(등록 번호가 없어도 안다)
 * @param {object|null|undefined} stats 리포트 응답(GET .../callbot/stats/monthly)
 * @returns {{ collected: boolean, collectedFrom: string|null, pendingLabel: string, rows: Array<{ key: string, number: string, name: string, attempts: Record<string, number|null>|null }>, unattributed: Record<string, number|null>|null, totals: Record<string, number>|null, noRing: number|null, rate: ReturnType<typeof attemptsCollectionRate> }}
 */
export function diagAttemptsView(stats) {
  const perNumber = arr(stats?.per_number);
  const collectedFrom = attemptsCollectedFrom(perNumber);
  const rows = perNumber.map((row, i) => ({
    key: row?.counselor_number_id ?? `n${i}`,
    number: maskNumber(row?.number),
    name: typeof row?.name === 'string' && row.name.trim() ? row.name : '',
    attempts: isAttemptCounts(row?.attempts) ? attemptCounts(row.attempts) : null,
  }));
  const rawUnattributed = stats?.per_number_unattributed?.attempts;
  const unattributed = isAttemptCounts(rawUnattributed) ? attemptCounts(rawUnattributed) : null;
  const sources = [...rows.map((r) => r.attempts), unattributed].filter(Boolean);
  const totals = sources.length
    ? Object.fromEntries(ATTEMPT_COUNT_KEYS.map((k) => [k, sources.reduce((sum, c) => sum + (c[k] ?? 0), 0)]))
    : null;
  const rate = attemptsCollectionRate(stats?.flow);
  return {
    collected: sources.length > 0 || rate.state !== 'pending',
    collectedFrom,
    pendingLabel: collectedFrom ? `수집 전(수집 시작 ${collectedFrom})` : '수집 전',
    rows,
    unattributed: unattributed && ATTEMPT_COUNT_KEYS.some((k) => (unattributed[k] ?? 0) > 0) ? unattributed : null,
    totals,
    noRing: totals ? totals.no_ring : null,
    rate,
  };
}

/** busy·unavailable·congestion·rejected 를 묶는 이름 — 원인을 단정하지 않는다 */
const ATTEMPT_UNAVAILABLE_LABEL = '받을 수 없는 상태';
const ATTEMPT_OTHER_LABEL = '기타';

/**
 * 시도 결과 원값(pbx-ami `outcome`) → 화면 이름. 번호별 표의 열과 같은 말을 쓰되, no_answer 는 30초 타임아웃 말고도
 * 상대 전화기의 즉시 거절(480·483)을 함께 담아 줄마다 벨 시간을 붙이는 통화 1건 확인에서는 '응답 없음'이라 쓴다.
 * failed 와 계약 밖 값은 '기타'.
 */
export const ATTEMPT_OUTCOME_LABELS = deepFreeze({
  answered: '받음',
  no_answer: '응답 없음',
  busy: ATTEMPT_UNAVAILABLE_LABEL,
  unavailable: ATTEMPT_UNAVAILABLE_LABEL,
  congestion: ATTEMPT_UNAVAILABLE_LABEL,
  rejected: ATTEMPT_UNAVAILABLE_LABEL,
  caller_hung_up: '벨 중 고객 종료',
  not_dialed: '벨 없이 끝남',
  superseded: '다른 연결로 대체',
});

/** 시도를 만든 지시(`trigger`) — 모르는 값은 원값 그대로 보인다 */
export const ATTEMPT_TRIGGER_LABELS = deepFreeze({ route: '처음 연결', retry: '다음 번호로', pickup: '당겨받기' });

/**
 * 통화 단건의 시도 한 줄. 시작은 직원 연결 요청(수신) 뒤 몇 초인지, 벨은 벨 시간(받았으면 받기까지)이다.
 * admin 은 진단 원값(DialStatus · Q.850 cause · SIP)을 보여도 된다(attempts 회신 admin 4). 결과는 outcome 으로만 판정한다 —
 * 원값이 결과와 어긋나 보여도 정상일 수 있다(pbx-ami 회신 결론 3).
 */
function attemptRow(attempt, i, receivedAt) {
  const outcome = typeof attempt?.outcome === 'string' && attempt.outcome ? attempt.outcome : null;
  const outcomeLabel = outcome && hasOwn(ATTEMPT_OUTCOME_LABELS, outcome) ? ATTEMPT_OUTCOME_LABELS[outcome] : ATTEMPT_OTHER_LABEL;
  const startedAt = num(attempt?.started_at);
  const offsetSec =
    startedAt !== null && receivedAt !== null ? Math.max(0, Math.round((startedAt - receivedAt) / 1000)) : null;
  const index = num(attempt?.candidate_index);
  const count = num(attempt?.candidate_count);
  const ringSec = num(attempt?.ring_seconds);
  let ring = '안 울림';
  if (num(attempt?.ring_started_at) !== null) ring = ringSec === null ? EMPTY : `${formatNumber(Math.round(ringSec))}초`;
  const cause = num(attempt?.cause);
  const causeText = typeof attempt?.cause_text === 'string' ? attempt.cause_text.trim() : '';
  const sipCode = num(attempt?.sip_code);
  const diagnostics = [
    typeof attempt?.dial_status === 'string' ? attempt.dial_status.trim() : '',
    cause === null ? '' : `cause ${cause}${causeText ? `(${causeText})` : ''}`,
    sipCode === null ? '' : `SIP ${sipCode}`,
  ].filter(Boolean);
  return {
    key: `${attempt?.attempt_no ?? 'a'}:${i}`,
    order: i + 1,
    startedAt: offsetSec === null ? formatKstTime(startedAt) : `+${formatDuration(offsetSec)}`,
    number: maskNumber(attempt?.target_number),
    candidate: index === null || count === null ? EMPTY : `${index + 1}/${count}`,
    trigger: labelOf(ATTEMPT_TRIGGER_LABELS, attempt?.trigger),
    outcomeLabel,
    // 이름 하나에 원값 여럿이 묶이거나 모르는 값('기타')이면 원값을 함께 보인다
    outcomeCode: outcome && [ATTEMPT_UNAVAILABLE_LABEL, ATTEMPT_OTHER_LABEL].includes(outcomeLabel) ? outcome : '',
    answered: outcome === 'answered',
    ring,
    diagnostics: diagnostics.length ? diagnostics.join(' · ') : EMPTY,
  };
}

/** 통화 단건 404 — 상담사 연결 leg 가 없는 통화 */
export const PBX_CALL_NOT_FOUND_TEXT = '상담사 연결 기록 없음(콜봇 단독 통화)';

/**
 * 통화 1건 연결 확인(화면 명세 2-6). 응답 = 연결 시각이 있거나 통화 시간이 0보다 큼(리포트와 같은 판정).
 * 벨·대기 = 연결 시각 − 수신 시각(연결이 없으면 종료 시각 − 수신 시각). 고객 번호(`caller_number`)는
 * 결과에 싣지 않는다.
 * 시도(`attempts`, attempts 회신 "pbx-calls"): null(또는 키가 없는 옛 응답)은 모름 — 이 환경에 시도 기록이 아직 없거나
 * 수집 시작 전에 들어온 통화다. 빈 배열은 기록 없음. 배열은 받은 순서 그대로 줄을 만든다(서버가 시각순으로
 * 정렬한다 — attempt_no 로 다시 정렬하지 않는다).
 * @param {object|null|undefined} call `GET .../pbx-calls/:voxCallId` 응답
 * @returns {null | { voxCallId: string|null, answered: boolean, resultLabel: string, routeLabel: string, voxDirect: boolean, target: string, receivedAt: string, waitSec: number|null, wait: string, talkSec: number|null, talk: string, attempts: Array<ReturnType<typeof attemptRow>>|null }}
 */
export function pbxCallSummary(call) {
  if (!call) return null;
  const connectedAt = num(call.connected_at);
  const receivedAt = num(call.received_at);
  const talkSec = num(call.talk_sec);
  const answered = connectedAt !== null || (talkSec ?? 0) > 0;
  const until = connectedAt ?? num(call.ended_at);
  const waitSec =
    receivedAt !== null && until !== null ? Math.max(0, Math.round((until - receivedAt) / 1000)) : null;
  return {
    voxCallId: call.vox_call_id ?? null,
    answered,
    resultLabel: answered ? '응답' : '미응답',
    routeLabel: labelOf(PBX_CONNECT_VIA_LABELS, call.connect_via),
    voxDirect: call.connect_via === 'vox-direct',
    target: maskNumber(call.target_counselor_number),
    receivedAt: formatKstDateTime(receivedAt),
    waitSec,
    wait: formatDuration(waitSec),
    talkSec,
    talk: formatDuration(talkSec),
    attempts: Array.isArray(call.attempts) ? call.attempts.map((a, i) => attemptRow(a, i, receivedAt)) : null,
  };
}

// ---------------------------------------------------------------------------
// 10. 상세 탭과 목록 쿼리 (화면 명세 2 탭, 화면 명세 1 헤더)
// ---------------------------------------------------------------------------

/** 라우트 이름(src/router/index.js) */
export const SETTLEMENT_ROUTE_NAMES = deepFreeze({
  list: 'callbotSettlementList',
  rates: 'callbotSettlementRates',
  detail: 'callbotSettlementDetail',
});

/** 상세 탭 — 쿼리 `tab` */
export const DETAIL_TABS = deepFreeze([
  { key: 'settle', label: '정산' },
  { key: 'rates', label: '요율' },
  { key: 'recon', label: '대사' },
  { key: 'diag', label: '연결 진단' },
]);
export const DEFAULT_DETAIL_TAB = 'settle';

/**
 * 쿼리 `tab` → 탭 key. 없거나 모르는 값이면 정산 탭.
 * @param {unknown} raw
 * @returns {'settle'|'rates'|'recon'|'diag'}
 */
export function resolveTabParam(raw) {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return DETAIL_TABS.some((t) => t.key === value) ? value : DEFAULT_DETAIL_TAB;
}

/**
 * 목록이 쓰고 상세의 `← 병원 목록`·기본 요율 화면의 돌아가기 링크가 읽는 sessionStorage 키.
 * 읽고 쓰기는 settlementListQuery.js(브라우저 전용)가 한다 — 이 모듈은 저장소를 만지지 않는다.
 */
export const LIST_QUERY_STORAGE_KEY = 'admin.settlementListQuery';

/**
 * 목록 필터 → 저장·쿼리용 값. 기본값(전체·빈 검색·통화 내림차순)은 뺀다.
 * @param {{ cat?: unknown, q?: unknown, sort?: unknown }|null|undefined} filter sort 는 문자열 또는 { key, dir }
 * @returns {{ cat?: string, q?: string, sort?: string }}
 */
export function compactListQuery(filter) {
  const out = {};
  const cat = normalizeUsageCat(filter?.cat);
  if (cat !== DEFAULT_USAGE_CAT) out.cat = cat;
  const rawQ = Array.isArray(filter?.q) ? filter.q[0] : filter?.q;
  const q = typeof rawQ === 'string' ? rawQ.trim() : '';
  if (q) out.q = q;
  const rawSort = filter?.sort;
  const sort = formatUsageSort(
    typeof rawSort === 'string' || Array.isArray(rawSort) ? parseUsageSort(rawSort) : rawSort
  );
  if (sort) out.sort = sort;
  return out;
}

/**
 * sessionStorage 에 둘 JSON
 * @param {{ cat?: unknown, q?: unknown, sort?: unknown }} filter
 * @returns {string}
 */
export function serializeListQuery(filter) {
  return JSON.stringify(compactListQuery(filter));
}

/**
 * sessionStorage 값(JSON 문자열 또는 객체) → 목록 필터. 깨졌거나 없으면 {}.
 * @param {unknown} raw
 * @returns {{ cat?: string, q?: string, sort?: string }}
 */
export function parseListQuery(raw) {
  let obj = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      obj = null;
    }
  }
  return obj && typeof obj === 'object' && !Array.isArray(obj) ? compactListQuery(obj) : {};
}

/**
 * 목록으로 돌아가는 위치 — 같은 달 + 저장해 둔 구분·검색·정렬
 * @param {string} month
 * @param {unknown} [saved] parseListQuery 가 읽는 값(sessionStorage 원문 가능)
 * @returns {{ name: string, query: Record<string, string> }}
 */
export function settlementListLocation(month, saved) {
  const query = isValidMonth(month) ? { month } : {};
  return { name: SETTLEMENT_ROUTE_NAMES.list, query: { ...query, ...parseListQuery(saved) } };
}

/**
 * 병원 상세로 가는 위치 — 목록 행·자동 마감 요약의 병원 칩은 `tab` 없이(정산 탭). 삭제된 회사도 같은 위치다
 * (회신 "2026-10-19 개정" — 상세가 삭제된 회사도 받아 따로 가를 표시가 없다).
 * 병원 이름을 알면 히스토리 state 에 실어 보낸다 — 상세가 응답 전(첫 로드)에도 제목에 이름을 쓴다.
 * 새 탭으로 열면 state 가 없어 응답의 이름을 기다린다.
 * @param {string} companyId
 * @param {string} month
 * @param {string} [tab]
 * @param {{ companyName?: string|null }} [opts]
 * @returns {{ name: string, params: { companyId: string }, query: Record<string, string>, state?: { settlementCompany: { id: string, name: string } } }}
 */
export function settlementDetailLocation(companyId, month, tab, { companyName = null } = {}) {
  const query = isValidMonth(month) ? { month } : {};
  if (tab && tab !== DEFAULT_DETAIL_TAB) query.tab = resolveTabParam(tab);
  const location = { name: SETTLEMENT_ROUTE_NAMES.detail, params: { companyId: String(companyId) }, query };
  const name = typeof companyName === 'string' ? companyName.trim() : '';
  if (name) location.state = { settlementCompany: { id: String(companyId), name } };
  return location;
}

/**
 * 히스토리 state 에 실려 온 병원 이름(settlementDetailLocation) — 그 병원 것일 때만
 * @param {unknown} state window.history.state
 * @param {string} companyId
 * @returns {string|null}
 */
export function companyNameFromState(state, companyId) {
  const c = state && typeof state === 'object' ? state.settlementCompany : null;
  return c && c.id === String(companyId) && typeof c.name === 'string' && c.name ? c.name : null;
}
